<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { agentName as agentNameStore, currentView, fundPins, fundLps, fundSyncFilterSummary, pollVersion, pollingActive, userEmail as userEmailStore, FUND_STAGES, fundStageColor, fundStageLabel, avatarColor, initialsFor } from '../lib/stores';
  import TodayHero from '../lib/TodayHero.svelte';
  import TodayNav from '../lib/TodayNav.svelte';
  import SyncButton from '../lib/SyncButton.svelte';
  import { createSectionSpy, jumpToSection } from '../lib/sectionSpy.svelte';
  import { renderSafeMarkdown, unwrapHardBreaks, repairLostApostrophes, stripCids } from '../lib/markdown';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';
  import FundDraftCard, { type CardDraft, type CardSend } from '../lib/FundDraftCard.svelte';
  import { htmlToPlain, isHtml } from '../lib/email_markdown';
  import BriefingView from '../lib/briefing/BriefingView.svelte';
  import PortfolioBriefing from '../lib/briefing/PortfolioBriefing.svelte';
  import { createBriefingStore, type BriefingResearch } from '../lib/briefing/store.svelte';
  import type { Briefing, Source as BriefingSource } from '../lib/briefing/types';
  import { fileIconPath, formatFileSize, isPdf, localDay, parseDay, senderName } from '../lib/format';
  import { followupStatus } from '../lib/followup';
  import { downloadSupportFile, framedUrl, isSupportView, canUseAs } from '../lib/supportView';
  import {
    addFundNote, editFundNote, assignFundInstitution, confirmFundRole, createFundInstitution, getFundInstitutionActivity, fundChat, fundDocumentUrl, generateFundOutput, getFundContext,
    getFundMessage, getFundSent, getFundScheduled, getAllFundScheduled, getFundScheduledDetail,
    refreshFundThreads,
    editFundScheduled, getFundWorkspace, getFundPins, pinFundContact, unpinFundContact, pinFundOrganization, unpinFundOrganization,
    cancelFundScheduled, submitFundEmail, setFundStage, setFundFollowUp, resumeFundAutoStage, renameFundContact,
    setFundInstitutionStage, setFundInstitutionFollowUp, resumeFundInstitutionAutoStage,
    getFundDashboardViews, getFundDashboardMailActivity, saveFundDashboardView, deleteFundDashboardView,
    getFundBriefing, generateFundBriefing, putFundBriefingOverride, deleteFundBriefingOverride,
    lockFundBriefing, heartbeatFundBriefingLock, releaseFundBriefingLock, refreshFundResearch,
    getFundTodayDecisions, getFundTodayCalls, getFundTodayBriefs, downloadFundCallBriefPdf,
    actOnFundTodayDecision, actOnFundTodayBrief,
    type FundTodayDecision, type FundTodayCall, type FundTodayBrief,
    polishFundEmail, saveFundProviderDraft, saveFundReviewDraft, getFundEmailTracking,
    type FundContact, type FundRole, type FundRoleChoice, type FundWorkspace, type FundMailActivity, type FundLatestSubjects, type FundSendReceipt, type FundInstitutionMessage,
    type FundScheduledSend, type FundScheduledDetail, type FundMailAddress,
    saveFundChatMemory, getFundContactFiles, fundContactFileUrl, fundContactFilePreviewUrl, fundDocumentPreviewUrl,
    type FundContactFile,
  } from '../lib/api/fund-ir';

  let { view = 'today', contactId = '', userName = '' }: {
    view?: 'today' | 'dashboard' | 'contacts' | 'scheduled' | 'detail';
    contactId?: string;
    userName?: string;
  } = $props();

  let workspace = $state<FundWorkspace | null>(null);
  let loading = $state(true);
  let error = $state('');
  // Dashboard search is part of a saved view; Contacts search is a local directory
  // affordance. Sharing one value made a query silently follow the user between pages.
  let dashboardSearch = $state('');
  let contactsSearch = $state('');
  let detailOrigin = $state<'today' | 'dashboard' | 'contacts'>('dashboard');
  // Also catches navigation initiated by the app shell (for example a pinned LP),
  // not only links rendered inside this component.
  $effect(() => {
    if (view === 'today' || view === 'dashboard' || view === 'contacts') detailOrigin = view;
  });
  let detailTab = $state<'briefing' | 'assistant' | 'emails' | 'contacts' | 'memory' | 'files'>('briefing');
  let briefingView = $state<'web' | 'doc'>('web');
  let savingRole = $state('');
  let savingStage = $state(false);
  let savingFollowUp = $state(false);
  let savingPin = $state(false);
  let pinError = $state('');
  let moreOpen = $state(false);
  let followUpEditing = $state(false);
  let followUpEditorRequested = $state('');
  let nextStepDraft = $state('');
  let followUpDateDraft = $state('');
  let contactContext = $state<Awaited<ReturnType<typeof getFundContext>> | null>(null);
  let contextLoading = $state(false);
  let contextError = $state('');
  let draftInstruction = $state('');
  let groundingDocumentIds = $state<string[]>([]);
  let generating = $state<'brief' | 'draft' | ''>('');
  let sentReceipts = $state<FundSendReceipt[]>([]);
  let emailTracking = $state<Awaited<ReturnType<typeof getFundEmailTracking>>['messages']>([]);
  let openedMessages = $state<Set<string>>(new Set());
  let emailQuery = $state('');
  let expandedEmailThreads = $state<Set<string>>(new Set());
  let activeEmailThreadKey = $state('');
  let expandedPerson = $state('');
  let refreshingThreads = $state(false);
  let messageBodies = $state<Record<string, string>>({});
  let messageErrors = $state<Record<string, string>>({});
  let messageRecipients = $state<Record<string, { label: string; people: FundMailAddress[] }[]>>({});
  let expandedRecipients = $state(new Set<string>());
  let composeOpen = $state(false);
  let composeSubject = $state('');
  let composeBody = $state('');
  let composeCc = $state('');
  let composeReplyMessageId = $state('');
  let composeFiles = $state<File[]>([]);
  let composeDocumentIds = $state<string[]>([]);
  let composeTracking = $state(false);
  let composeMeeting = $state(false);
  let meetingSummary = $state('');
  let meetingLocation = $state('');
  let meetingStart = $state('');
  let meetingEnd = $state('');
  let scheduleOpen = $state(false);
  let scheduleDate = $state('');
  let scheduleTime = $state('');
  let scheduledSends = $state<FundScheduledSend[]>([]);
  let allScheduled = $state<FundScheduledSend[]>([]);
  let scheduledLoading = $state(false);
  let scheduledError = $state('');
  let scheduledFilter = $state<'all' | 'pending' | 'sent' | 'attention'>('all');
  let selectedSchedule = $state<FundScheduledDetail | null>(null);
  let scheduleSubject = $state('');
  let scheduleBody = $state('');
  let scheduleCc = $state('');
  let scheduleEditDate = $state('');
  let scheduleEditTime = $state('');
  let scheduleSaving = $state(false);
  let savingInstitution = $state(false);
  let organizationEditing = $state(false);
  let newInstitutionName = $state('');
  let institutionActivity = $state<FundInstitutionMessage[]>([]);
  let institutionActivityError = $state('');
  let institutionActivityLoading = $state(false);
  let todayDecisions = $state<FundTodayDecision[]>([]);
  let todayDecisionsError = $state('');
  let todayDecisionsLoaded = $state(false);
  let todayDecisionsSequence = 0;
  let todayCalls = $state<FundTodayCall[]>([]);
  let todayCallsError = $state('');
  let todayCallsLoading = $state(false);
  let todayCallsLoaded = $state(false);
  let todayBriefs = $state<FundTodayBrief[]>([]);
  let todayBriefsLoaded = $state(false);
  let todayBriefsError = $state('');
  let todayBriefsSequence = 0;
  let todayBriefsExpanded = $state(false);
  let todayBriefError = $state('');
  let todayBriefContactId = $state('');
  let todayCallBriefDownloadKey = $state('');
  let todayCallBriefError = $state('');
  let todayDraftError = $state('');
  // Respond/Chase cards keyed by todayKey(): synced message text, an on-demand draft, in-flight work, undoable dismissals.
  let todayInbound = $state<Record<string, { text: string; loading: boolean; error: string }>>({});
  let todayCardDrafts = $state<Record<string, CardDraft>>({});
  let todayBusy = $state<Record<string, '' | 'generate' | 'polish' | 'send'>>({});
  const todayRequestIds: Record<string, string> = {};
  let todayDismissed = $state<Set<string>>(new Set());
  let todaySendNotice = $state('');
  let todayChatSeed = $state<{ counterparty_id: string; text: string } | null>(null);
  // The contact-switch effect resets the tab, so a Today hand-off names the tab to land on.
  let todayTabRequest = $state<{ counterparty_id: string; tab: 'memory' } | null>(null);
  let todayComposeRequest = $state<{ contactId: string; subject: string; body: string } | null>(null);
  let todayExpanded = $state<Record<string, boolean>>({});
  let todayCallsSequence = 0;
  let todayReplyRequest = $state<{ counterparty_id: string; message_id: string;
    subject: string; draftBody?: string; draftSubject?: string } | null>(null);
  let noteEditorOpen = $state(false);
  let noteEditId = $state('');
  let noteTitle = $state('');
  let noteBody = $state('');
  let noteSaving = $state(false);
  let composeRequestId = $state('');
  let sending = $state(false);
  let savingReviewDraft = $state(false);
  let polishing = $state(false);
  let polishChanges = $state('');
  let sendNotice = $state('');
  let actionError = $state('');
  let addOpen = $state(false);
  let newEmail = $state('');
  let newName = $state('');
  let newRole = $state<FundRole>('investor');
  /* The dashboard shows model-filed and manually filed relationships. */
  let dashTab = $state<'ir' | 'portfolio'>('ir');
  type IrStatus = 'overdue' | 'soon' | 'later' | 'undated' | 'none';
  /* Multi-select, like the Sales dashboard chips: an empty array means "All".
     Views saved before this change stored one string ('all' for no filter), so
     applySavedView() widens them on read rather than discarding the view. */
  let irStatusFilter = $state<IrStatus[]>([]);
  let irStageFilter = $state<string[]>([]);
  /* Sorting is driven by the column headers, the way the Sales dashboard does it. The
     union keeps `stage` so a view saved while it was a select option still applies;
     nothing sets it any more. */
  let irSort = $state<'name' | 'stage' | 'last_email' | 'due' | 'status'
    | 'inbound' | 'outbound'>('due');
  let irSortDir = $state<'asc' | 'desc'>('asc');
  let irInstitutionFilter = $state('');
  let irPinnedOnly = $state(false);
  let openFilterDD = $state<'stage' | 'status' | 'institution' | null>(null);
  const IR_STATUS_OPTIONS: { key: IrStatus; label: string }[] = [
    { key: 'overdue', label: 'Overdue' }, { key: 'soon', label: 'Soon' },
    { key: 'later', label: 'On track' }, { key: 'undated', label: 'No due date' },
    { key: 'none', label: 'No next step' },
  ];
  /* `bookmarked` promotes the VIEW into the dropdown's shortcut section — it is not the
     row ★, which pins a relationship to the sidebar. The backend stores each view as a
     free-form JSON blob, so the field costs no migration; a view saved before it existed
     reads back as undefined and lands in "All views". */
  type FundViewPreset = { name: string; tab: 'ir' | 'portfolio' | 'sorting';
    status: IrStatus[] | string; sort: typeof irSort; institution: string;
    stage: string[] | string; pinned: boolean; search: string; bookmarked?: boolean;
    sortDir?: 'asc' | 'desc';
    /* Written by views saved while the Unchecked chip existed. Nothing reads it; it stays
       in the type so an old view still parses instead of being dropped on load. */
    checked?: '' | 'unchecked' | 'checked' };
  let savedViews = $state<Record<string, FundViewPreset>>({});
  let activeViewKey = $state('');
  let newViewName = $state('');
  let viewNaming = $state(false);
  let viewError = $state('');
  let viewDropdownOpen = $state(false);
  let editingViewKey = $state<string | null>(null);
  /* The menu renders outside the scrolling table so its actions stay visible. */
  let openStageDD = $state<string | null>(null);
  let stageMenuPosition = $state({ top: 0, left: 0, anchorTop: 0 });
  let dashboardMailActivity = $state<FundMailActivity>({});
  let dashboardLatestSubjects = $state<FundLatestSubjects>({});
  let dashboardMailError = $state('');
  type FundTrackingMessage = Awaited<ReturnType<typeof getFundEmailTracking>>['messages'][number];
  let dashboardTracking = $state<Record<string, FundTrackingMessage[]>>({});
  let dashboardTrackingLoading = $state<Set<string>>(new Set());
  let dashboardTrackingErrors = $state<Record<string, string>>({});
  let viewSaving = $state(false);
  let expandedFundOrganizations = $state<Set<string>>(new Set());
  let expandedContactOrganizations = $state<Set<string>>(new Set());
  let collapsedContactSearchGroups = $state<Set<string>>(new Set());
  // Contacts mirrors the Dashboard's two tabs; sections inside start open, like Sales.
  let contactsTab = $state<'lp' | 'portfolio'>('lp');
  let collapsedContactSections = $state<Set<string>>(new Set());
  let editingContactId = $state('');
  let editName = $state('');
  let editRole = $state<FundRoleChoice | 'unknown' | ''>('');
  let savingContactEdit = $state(false);
  // Unclassified starts closed, like Sales' Non-Clients; a search opens every group.
  let expandedIrStages = $state<Set<string>>(
    new Set([...FUND_STAGES.map(s => s.key), '', '__mixed__']));
  let collapsedPortfolioStages = $state<Set<string>>(new Set());
  let portfolioSort = $state<'name' | 'last_email' | 'inbound' | 'outbound'>('last_email');
  let portfolioSortDir = $state<'asc' | 'desc'>('asc');

  // Same pill-nav behavior as the Sales Today: the pill for the section in view
  // is the active one, and a click scrolls there rather than jumping.
  const spy = createSectionSpy();
  const spyOn = spy.spyOn;

  // The LP briefing owns the centre of this page. Mike has one conversation and
  // three display modes; moving its snippet never resets messages or the draft.
  const agent = $derived($agentNameStore || 'Dealton');
  let shellEl = $state<HTMLElement>();
  type MikeMode = 'rail' | 'bubble' | 'tab';
  const savedMikeMode = typeof localStorage === 'undefined' ? null : localStorage.getItem('fundMikeMode');
  let mikeMode = $state<MikeMode>(savedMikeMode === 'bubble' || savedMikeMode === 'tab' ? savedMikeMode : 'rail');
  let mikeNarrow = $state(typeof matchMedia !== 'undefined' && matchMedia('(max-width: 900px)').matches);
  let mikeOverlayOpen = $state(false);
  let mikeW = $state(Math.max(240, Math.min(520, Number(
    typeof localStorage === 'undefined' ? 340 : localStorage.getItem('fundRailW')) || 340)));
  let mikeDragging = $state(false);
  let readingLabel = $state('Summary');
  const showMikeRail = $derived(mikeMode === 'rail' && !mikeNarrow);
  const showMikeBubble = $derived(mikeMode === 'bubble' || (mikeMode === 'rail' && mikeNarrow));
  function setMikeMode(mode: MikeMode) {
    mikeMode = mode;
    mikeOverlayOpen = false;
    localStorage.setItem('fundMikeMode', mode);
    if (mode === 'tab') detailTab = 'assistant';
    else if (detailTab === 'assistant') detailTab = 'briefing';
  }
  function openMikeBubble() {
    if (mikeNarrow) mikeOverlayOpen = true;
    else setMikeMode('rail');
  }
  function resizeMike(event: PointerEvent) {
    if (event.button !== 0 || !shellEl) return;
    event.preventDefault();
    mikeDragging = true;
    const move = (e: PointerEvent) => {
      if (!shellEl) return;
      const box = shellEl.getBoundingClientRect();
      mikeW = Math.max(240, Math.min(520, box.width - 429, box.right - e.clientX));
    };
    const stop = () => {
      mikeDragging = false;
      localStorage.setItem('fundRailW', String(Math.round(mikeW)));
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
  }
  function resizeMikeKey(event: KeyboardEvent) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    mikeW = Math.max(240, Math.min(520, mikeW + (event.key === 'ArrowLeft' ? 20 : -20)));
    localStorage.setItem('fundRailW', String(mikeW));
  }
  function updateMikeNarrow() {
    mikeNarrow = window.matchMedia('(max-width: 900px)').matches
      || (!!shellEl && shellEl.clientWidth < 687);
    if (!mikeNarrow) mikeOverlayOpen = false;
  }
  onMount(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    mq.addEventListener('change', updateMikeNarrow);
    updateMikeNarrow();
    return () => mq.removeEventListener('change', updateMikeNarrow);
  });
  $effect(() => {
    const node = shellEl;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateMikeNarrow);
    observer.observe(node);
    updateMikeNarrow();
    return () => observer.disconnect();
  });
  $effect(() => {
    if (mikeMode !== 'tab' && detailTab === 'assistant') detailTab = 'briefing';
  });



  const allContacts = $derived(workspace?.counterparties ?? []);
  // Sales groups contacts by external company. Fund uses the same visual clue.
  // A shared email domain can suggest a label without setting an LP role.
  function nameMatchesEmailDomain(name: string, domain: string): boolean {
    const root = domain.split('.')[0]?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
    const label = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return root.length >= 4 && label.length >= 4
      && (label.includes(root) || root.includes(label));
  }
  function isBareDomainName(name: string | null | undefined): boolean {
    return /^[^\s@]+\.[a-z]{2,}$/i.test((name || '').trim());
  }
  const organizationNamesByDomain = $derived.by(() => {
    const names = new Map<string, Map<string, number>>();
    for (const contact of allContacts.filter(isFundDirectoryContact)) {
      const domain = contact.suggested_organization_domain;
      const name = contact.suggested_organization_name;
      if (!domain || !name || isBareDomainName(name) || (contact.suggested_source === 'model_candidate'
          && !nameMatchesEmailDomain(name, domain))) continue;
      const counts = names.get(domain) || new Map<string, number>();
      counts.set(name, (counts.get(name) || 0) + 1);
      names.set(domain, counts);
    }
    const best = new Map<string, string>();
    for (const [domain, counts] of names) {
      best.set(domain, [...counts].sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)[0][0]);
    }
    return best;
  });
  function personLabel(contact: FundContact): string {
    const raw = (contact.display_name || '').trim();
    if (contact.kind === 'organization') return raw || 'Unknown organization';
    if (raw && !raw.includes('@') && !/^[^\s@]+\.[a-z]{2,}$/i.test(raw)) {
      if (raw.split(',').length === 2) {
        const [family, given] = raw.split(',').map(part => part.trim());
        if (family && given) return `${given} ${family}`;
      }
      return raw;
    }
    const local = (contact.email || raw).split('@')[0];
    return local.split(/[._+-]+/).filter(Boolean).map(part =>
      part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ') || 'Unknown contact';
  }
  function organizationHint(contact: FundContact): { name: string; source: string } | null {
    const confirmed = (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
    if (confirmed) return { name: isBareDomainName(confirmed.name) ? personLabel(contact) : confirmed.name,
      source: contact.confirmed_by
      ? 'Team-linked LP institution' : 'LP institution from synced mail' };
    const domain = contact.suggested_organization_domain;
    if (contact.suggested_source === 'model_candidate') {
      if (contact.role === 'investor' || contact.suggested_role === 'investor') {
        const supportedName = domain ? organizationNamesByDomain.get(domain) : null;
        if (supportedName) return { name: supportedName, source: 'Grouped by email domain' };
        return { name: personLabel(contact), source: 'Potential LP from synced mail' };
      }
      const supportedName = domain ? organizationNamesByDomain.get(domain) : null;
      if (supportedName) return { name: supportedName, source: 'Grouped by email domain' };
      return { name: personLabel(contact),
        source: 'Contact name shown' };
    }
    if (contact.suggested_organization_name && !isBareDomainName(contact.suggested_organization_name))
      return { name: contact.suggested_organization_name, source: 'Suggested from mail' };
    const sharedName = domain ? organizationNamesByDomain.get(domain) : null;
    if (sharedName) return { name: sharedName, source: 'Grouped by email domain' };
    return domain ? { name: personLabel(contact), source: 'Contact name shown' } : null;
  }
  function relationshipInstitution(contact: FundContact) {
    return (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
  }
  /** The stored stage, null until set or inferred: for provenance, not display. */
  function storedStage(contact: FundContact): string | null {
    // Portfolio has no stage (Existing vs Potential is its type); hide any inferred one.
    if (isPortfolioContact(contact)) return null;
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.stage : contact.stage;
  }
  /** An LP with no stored stage is a Prospect, the stage prompt's floor for an identified possible LP. */
  function relationshipStage(contact: FundContact): string | null {
    return storedStage(contact) || (contact.role === 'investor' ? 'Prospect' : null);
  }
  function relationshipStageSource(contact: FundContact) {
    const institution = relationshipInstitution(contact);
    return institution ? institution.stage_set_by || null : contact.stage_set_by || null;
  }
  function relationshipStageReason(contact: FundContact) {
    const institution = relationshipInstitution(contact);
    return institution ? institution.stage_reason || null : contact.stage_reason || null;
  }
  function relationshipNextStep(contact: FundContact): string | null {
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.next_step : contact.next_step || null;
  }
  function nextStepAuthor(record: { next_step_set_by?: string | null; next_step_set_at?: string | null }): string {
    if (record.next_step_set_by === 'auto:mail_activity')
      return `Set automatically from synced mail${record.next_step_set_at ? ` · ${mailDate(record.next_step_set_at)}` : ''}`;
    if (!record.next_step_set_by) return 'Set by your team';
    return `Set by ${record.next_step_set_by}${record.next_step_set_at ? ` · ${mailDate(record.next_step_set_at)}` : ''}`;
  }
  function relationshipDue(contact: FundContact): string | null {
    return relationshipInstitution(contact) ? relationshipInstitution(contact)!.follow_up_on : contact.follow_up_on || null;
  }
  // A group is a display aid and never changes anyone's role. A verified
  // organization or a shared business domain groups people; an email provider
  // does not determine whether the investor acts personally or for a firm.
  const GROUP_SOURCE_LABEL: Record<string, string> = {
    'Suggested from mail': 'Unconfirmed · name from mail',
    'Grouped by email domain': 'Unconfirmed · same email domain',
    'Contact name shown': 'Unconfirmed · contact name',
    'Potential LP from synced mail': 'Unconfirmed · possible LP from mail',
  };
  function groupFundContacts(contacts: FundContact[]) {
    type Group = { key: string; name: string; source: string; contacts: FundContact[]; organization: boolean };
    const groups = new Map<string, Group>();
    for (const contact of contacts) {
      const institution = (workspace?.institutions || []).find(i => i.institution_id === contact.institution_id);
      const domain = contact.suggested_organization_domain;
      const hint = organizationHint(contact);
      // The displayed name, so a same-domain colleague without a name of their own joins the row.
      const portfolioCandidate = displayRole(contact) === 'portfolio'
        && contact.suggested_source === 'model_candidate';
      const portfolioName = displayRole(contact) === 'portfolio' && hint && !portfolioCandidate
        ? hint.name.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
      const key = institution ? `institution:${institution.institution_id}`
        : portfolioName ? `portfolio:${portfolioName}`
        : domain && (!portfolioCandidate || organizationNamesByDomain.has(domain)) ? `domain:${domain}`
        : contact.kind === 'organization' ? `entity:${contact.counterparty_id}`
        : `person:${contact.counterparty_id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.contacts.push(contact);
        // Prefer an evidenced organization name over a raw domain fallback.
        if (hint && ((existing.source === 'Contact name shown' && hint.source !== 'Contact name shown') ||
            (existing.source === 'Grouped by email domain' && hint.source === 'Suggested from mail'))) {
          existing.name = hint.name;
          existing.source = hint.source;
        }
      } else {
        groups.set(key, { key, name: hint?.name || personLabel(contact),
          source: hint?.source || '', contacts: [contact],
          organization: key.startsWith('institution:') || key.startsWith('domain:')
            || key.startsWith('portfolio:') || key.startsWith('entity:') });
      }
    }
    return [...groups.values()].map(group => ({
      ...group,
      source: GROUP_SOURCE_LABEL[group.source] ?? group.source,
    }));
  }
  function groupNextAction(contacts: FundContact[]): FundContact | null {
    return [...contacts].filter(c => !!relationshipNextStep(c)).sort((a, b) =>
      (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name))[0] || null;
  }
  function groupLatestMail(contacts: FundContact[]): FundContact | null {
    return [...contacts].filter(c => !!c.last_seen).sort(mostRecentFirst)[0] || null;
  }
  // From synced mail, not `sample_subjects`: those are whichever sync page wrote last, not the newest.
  function groupLatestSubject(contacts: FundContact[]): FundLatestSubjects[string] | null {
    return contacts.map(c => dashboardLatestSubjects[c.counterparty_id])
      .filter((entry): entry is FundLatestSubjects[string] => !!entry)
      .sort((a, b) => mailTime(b.at) - mailTime(a.at))[0] || null;
  }
  function groupMailActivity(contacts: FundContact[], direction: 'inbound' | 'outbound'): string | null {
    const institutionIds = [...new Set(contacts.map(c => c.institution_id)
      .filter((id): id is string => !!id))];
    const entityStamp = institutionIds.length === 1
      ? dashboardMailActivity[`institution:${institutionIds[0]}`]?.[direction] || null
      : null;
    return [entityStamp, ...contacts.map(c => dashboardMailActivity[c.counterparty_id]?.[direction] || null)]
      .filter((stamp): stamp is string => !!stamp)
      .sort((a, b) => mailTime(b) - mailTime(a))[0] || null;
  }
  function activityAgo(stamp: string | null): string {
    if (!stamp || !mailTime(stamp)) return '—';
    const days = Math.max(0, Math.floor((Date.now() - mailTime(stamp)) / 86400000));
    return `${days}d ago`;
  }
  function toggleFundOrganization(key: string) {
    const next = new Set(expandedFundOrganizations);
    if (next.has(key)) next.delete(key);
    else {
      next.add(key);
      if (view === 'dashboard' && dashTab === 'ir') {
        const entity = irSortedEntities.find(item => item.key === key);
        if (entity) void loadEntityTracking(entity);
      }
    }
    expandedFundOrganizations = next;
  }
  async function loadEntityTracking(entity: IrEntity) {
    if (dashboardTracking[entity.key] || dashboardTrackingLoading.has(entity.key)) return;
    dashboardTrackingLoading = new Set([...dashboardTrackingLoading, entity.key]);
    const requests = await Promise.allSettled(entity.contacts.filter(c => c.role === 'investor')
      .map(c => getFundEmailTracking(c.counterparty_id)));
    const messages = requests.flatMap(result => result.status === 'fulfilled' ? result.value.messages : []);
    dashboardTracking = { ...dashboardTracking, [entity.key]: messages.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) };
    if (requests.some(result => result.status === 'rejected')) {
      dashboardTrackingErrors = { ...dashboardTrackingErrors,
        [entity.key]: 'Some tracked updates could not be loaded.' };
    } else {
      const { [entity.key]: _old, ...otherErrors } = dashboardTrackingErrors;
      dashboardTrackingErrors = otherErrors;
    }
    const next = new Set(dashboardTrackingLoading);
    next.delete(entity.key);
    dashboardTrackingLoading = next;
  }
  function toggleContactOrganization(key: string) {
    if (contactsSearch.trim()) {
      const next = new Set(collapsedContactSearchGroups);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      collapsedContactSearchGroups = next;
      return;
    }
    const next = new Set(expandedContactOrganizations);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    expandedContactOrganizations = next;
  }
  type DisplayRelationship = FundRole | 'unknown';
  function displayRole(contact: FundContact): DisplayRelationship | null {
    if (contact.role) return contact.role;
    if (contact.suggested_source === 'model_neither') return 'neither';
    if (contact.suggested_source === 'model_unknown') return 'unknown';
    return (contact.suggested_source === 'model_review'
      || contact.suggested_source === 'model_auto' || contact.suggested_source === 'model_auto_v16'
      || contact.suggested_source === 'model_candidate'
      ? contact.suggested_role || null : null);
  }
  function isFundDirectoryContact(contact: FundContact): boolean {
    if (!contact.email || contact.automated_address) return false;
    const role = displayRole(contact);
    return role === 'investor' || role === 'portfolio';
  }
  // Match the backend sidebar and pin eligibility. Legacy model suggestions
  // without a filed role remain in Contacts but are not actionable LP rows.
  const dashboardLps = $derived(allContacts.filter(c => c.role === 'investor').sort(mostRecentFirst));
  const todayLpGroups = $derived(groupFundContacts(dashboardLps).sort((a, b) =>
    mailTime(groupLatestMail(b.contacts)?.last_seen) - mailTime(groupLatestMail(a.contacts)?.last_seen)
    || a.name.localeCompare(b.name)));
  const respondDecisions = $derived(todayDecisions.filter(d => d.kind === 'respond'));
  const chaseDecisions = $derived(todayDecisions.filter(d => d.kind === 'chase'));
  const respondOpen = $derived(respondDecisions.filter(d => !todayDismissed.has(todayKey(d))));
  const nextActions = $derived.by(() => {
    const seenInstitutions = new Set<string>();
    return dashboardLps.filter(c => {
      if (!relationshipNextStep(c)) return false;
      if (!c.institution_id) return true;
      if (seenInstitutions.has(c.institution_id)) return false;
      seenInstitutions.add(c.institution_id);
      return true;
    }).sort((a, b) => (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name));
  });
  const dueActions = $derived(nextActions.filter(nextStepIsDue));
  type ChaseItem = { key: string; contact: FundContact | undefined;
    decision: FundTodayDecision | null };
  // A shared outbound message can have several recipients. Chase is a work list,
  // so each recipient gets their own row and their own snooze/dismiss action.
  const chaseItems = $derived<ChaseItem[]>([
    ...dueActions.map(contact => ({ key: `due:${contact.counterparty_id}`, contact, decision: null })),
    ...[...chaseDecisions].sort((a, b) => b.age_days - a.age_days
      || a.display_name.localeCompare(b.display_name)).map(decision => ({
        key: `mail:${decision.counterparty_id}:${decision.message_id}`,
        contact: allContacts.find(c => c.counterparty_id === decision.counterparty_id), decision,
      })),
  ]);
  const newLpSuggestions = $derived(dashboardLps.filter(c =>
    c.suggested_source === 'model_candidate' && !c.automated_address));
  const newLpGroups = $derived(groupFundContacts(newLpSuggestions));
  // A relationship is quiet only when the latest synced email across all its
  // known people is old. One quiet person must not label an active LP firm cold.
  const quietGroups = $derived(groupFundContacts(dashboardLps)
    .filter(group => {
      const latest = mailTime(groupLatestMail(group.contacts)?.last_seen);
      return latest > 0 && Date.now() - latest >= 30 * 86400000;
    }).sort((a, b) => mailTime(groupLatestMail(a.contacts)?.last_seen)
      - mailTime(groupLatestMail(b.contacts)?.last_seen)));
  /* Fund Today is the sales Today, section for section (designs/today/today-ir). Two
     consequences live here:

     1. EVERY section keeps its header in its own place, empty or not. The page used to
        hide an empty section and collect the names into one "All clear" block, which
        moved a heading the reader had learnt the position of, and made "nothing in
        Respond" and "Respond is still loading" look like the same page. An empty section
        now shows one line under its own header. That is why every `hideEmpty*` flag and
        `clearTodayLabels` is gone rather than merely unused.
     2. A recorded follow-up that is DUE is a Chase row, so there is no separate "Next
        steps" section — `chaseItems` includes it. Follow-ups that are not due yet
        are not Today's business; they live on the dashboard. */
  const chaseOpen = $derived(chaseDecisions.filter(d => !todayDismissed.has(todayKey(d))));
  const chaseTotal = $derived(dueActions.length + chaseOpen.length);
  function countLabel(parts: [number, string, string][]): string {
    return parts.filter(([count]) => count > 0)
      .map(([count, singular, plural]) => `${count} ${count === 1 ? singular : plural}`)
      .join(' · ');
  }
  const respondCountLabel = $derived(countLabel([
    [respondOpen.length, 'conversation', 'conversations'],
    [respondOpen.filter(decision => !!todayCardDraft(decision)).length, 'draft ready', 'drafts ready'],
  ]));
  const chaseCountLabel = $derived(countLabel([
    [dueActions.length, 'next step due', 'next steps due'],
    [chaseOpen.length, 'awaiting reply', 'awaiting reply'],
  ]));
  /* The stage pill on a Chase recipient. */
  function chaseStage(contact: FundContact | undefined) {
    const stage = contact && relationshipStage(contact);
    return stage
      ? { label: fundStageLabel(stage), color: fundStageColor(stage) }
      : { label: 'No stage', color: 'var(--text-dim)' };
  }
  const pendingSends = $derived(allScheduled.filter(send => send.status === 'pending')
    .sort((a, b) => a.send_at.localeCompare(b.send_at)));
  const todayNavItems = $derived([
    { id: 'fund-briefing', label: 'Today briefing', count: todayBriefs.length },
    ...(pendingSends.length
      ? [{ id: 'fund-sends', label: 'Going out soon', count: pendingSends.length }]
      : []),
    { id: 'fund-calls', label: 'Calls', count: todayCalls.length },
    { id: 'fund-respond', label: 'Respond', count: respondOpen.length },
    { id: 'fund-chase', label: 'Chase', count: chaseTotal },
    { id: 'fund-new-lps', label: 'Possible LPs', count: newLpGroups.length },
    { id: 'fund-quiet', label: 'Quiet', count: quietGroups.length },
  ]);
  const todayStatus = $derived.by(() => {
    if (!workspace?.inbox_connected)
      return "Connect your inbox and I'll start keeping track of your LP relationships.";
    if (!allContacts.length)
      return "Your inbox is connected. Run a sync and I'll find your LP relationships.";
    if (!todayDecisionsLoaded || !todayBriefsLoaded) return 'Pulling together your day…';
    if (todayDecisionsError)
      return "I couldn't finish going through your mail. Retry below to get the full picture.";
    const respond = respondOpen.length;
    const due = dueActions.length;
    const chase = chaseOpen.length;
    const calls = todayCallsLoaded && !todayCallsError ? todayCalls.length : 0;
    const briefs = todayBriefs.length;
    const possible = newLpGroups.length;
    const quiet = quietGroups.length;
    const parts: string[] = [];
    if (briefs) parts.push(`${briefs} AI event briefing${briefs === 1 ? '' : 's'} to read`);
    if (respond) parts.push(`${respond} message${respond === 1 ? '' : 's'} to look at`);
    if (due) parts.push(`${due} of your next steps ${due === 1 ? 'is' : 'are'} due`);
    if (chase) parts.push(`${chase} email${chase === 1 ? '' : 's'} you sent ${chase === 1 ? "hasn't" : "haven't"} had a reply yet`);
    if (calls) parts.push(`${calls} LP call${calls === 1 ? '' : 's'} in the next 3 days`);
    if (possible) parts.push(`${possible} possible LP relationship${possible === 1 ? '' : 's'} to look at`);
    if (quiet) parts.push(`${quiet} LP${quiet === 1 ? ' has' : 's have'} gone quiet`);
    if (!parts.length)
      return "I've been through your mail — nothing needs you today. Good day to reach out to someone new.";
    const summary = parts.length === 1 ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    const timing = chase || quiet
      ? " Waiting on a reply isn't a deadline — whether to nudge is your call." : '';
    return `I've been through your mail — ${summary}.${timing}`;
  });
  // Potential is a Portfolio type, whether inferred from mail or chosen by the team.
  const potentialPortfolio = (c: FundContact) =>
    (!c.role && c.suggested_source === 'model_candidate' && c.suggested_role === 'portfolio')
    || (c.role === 'portfolio' && c.suggested_source === 'user_candidate');
  const portfolio = $derived(allContacts.filter(c => c.role === 'portfolio' || potentialPortfolio(c))
    .sort(mostRecentFirst));
  const isPortfolioContact = (c: FundContact) => c.role === 'portfolio' || potentialPortfolio(c);
  /* The contact page's actions — next step, Briefing, Mike, drafts and reviewed send — for an
     LP, or a Portfolio contact with a direct address. Stage, pins and Organization stay LP-only. */
  const relationshipActions = (c: FundContact) => !c.automated_address
    && (c.role === 'investor' || (isPortfolioContact(c) && !!c.email));
  // The label where a role select has no value: there is no "suggested" tier.
  const unfiledLabel = (c: FundContact) => displayRole(c) === 'unknown'
    ? 'Unclear from mail' : displayRole(c) === 'investor' ? 'Potential LP'
      : displayRole(c) === 'portfolio' ? 'Potential Portfolio' : 'Unclassified';
  function typeLabel(c: FundContact): string {
    if (potentialPortfolio(c)) return 'Potential Portfolio';
    if (c.role === 'investor' && c.suggested_source === 'model_candidate' && !c.confirmed_by) return 'Potential LP';
    return c.role ? roleLabel(c.role) : unfiledLabel(c);
  }
  const roleChoice = (c: FundContact): FundRoleChoice | 'unknown' | '' =>
    potentialPortfolio(c) ? 'potential_portfolio' : c.role
      || (c.suggested_source === 'model_neither' ? 'neither'
        : c.suggested_source === 'model_unknown' ? 'unknown' : '');
  const selected = $derived(allContacts.find(c => c.counterparty_id === contactId) ?? null);
  const selectedOrganization = $derived(contactId.startsWith('org:')
    ? groupFundContacts(allContacts).find(group => group.key === contactId.slice(4)) || null : null);
  const selectedOrganizationRecord = $derived(selectedOrganization?.key.startsWith('institution:')
    ? (workspace?.institutions || []).find(row => row.institution_id === selectedOrganization.key.slice('institution:'.length)) || null : null);
  const groundableDocs = $derived((workspace?.documents || []).filter(doc =>
    doc.size_bytes <= 5 * 1024 * 1024 && /\.(pdf|docx|txt|md|csv)$/i.test(doc.filename)));
  let followUpContactId = '';
  $effect(() => {
    if (!selected || selected.counterparty_id === followUpContactId) return;
    followUpContactId = selected.counterparty_id;
    noteEditorOpen = false;
    noteEditId = '';
    nextStepDraft = relationshipNextStep(selected) || '';
    followUpDateDraft = relationshipDue(selected) || '';
    followUpEditing = followUpEditorRequested === selected.counterparty_id;
    followUpEditorRequested = '';
    organizationEditing = false;
  });
  const selectedInstitution = $derived((workspace?.institutions || []).find(i => i.institution_id === selected?.institution_id) || null);
  const institutionMembers = $derived(allContacts.filter(c => c.institution_id
    && c.institution_id === selected?.institution_id
    && c.counterparty_id !== selected?.counterparty_id
    && c.role === 'investor' && !c.automated_address));
  const institutionBriefMembers = $derived(allContacts.filter(c => c.role === 'investor'
    && c.institution_id && c.institution_id === selected?.institution_id && !c.automated_address));

  // ── the structured seven-section briefing (lib/briefing/) ──────────────────
  // The API is injected rather than imported by the store, so `BriefingView`
  // stays page-agnostic: the Sales client page can mount the same component
  // against its own endpoints.
  const briefing = createBriefingStore({
    get: getFundBriefing,
    generate: generateFundBriefing,
    putOverride: putFundBriefingOverride,
    deleteOverride: deleteFundBriefingOverride,
    lock: lockFundBriefing,
    heartbeat: heartbeatFundBriefingLock,
    release: releaseFundBriefingLock,
    refreshResearch: refreshFundResearch,
  });
  let briefingNotice = $state('');
  // One line above the briefing: what public research it rests on, or why there is none.
  function researchLine(r: BriefingResearch): string {
    const day = (iso: string | null) => iso
      ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
    const minutes = (iso: string | null) => iso ? Math.max(1, Math.round((Date.now() - Date.parse(iso)) / 60_000)) : 0;
    const sources = `${r.public_sources} public source${r.public_sources === 1 ? '' : 's'}`;
    switch (r.status) {
      case 'queued': case 'running':
        return `Researching public sources${r.started_at ? ` · started ${minutes(r.started_at)} min ago` : ''}. `
          + 'A new version of this briefing follows when it finishes.';
      case 'none': return 'No public research for this LP yet.';
      case 'needs_identity': case 'failed': case 'capped': return r.error || 'Public research did not finish.';
      default:
        return `Public research: ${sources}${r.updated_at ? ` · updated ${day(r.updated_at)}` : ''}`
          + (r.status === 'partial' ? ' · incomplete' : '');
    }
  }
  // Which questions public research left without the facts they need.
  function coverageLine(r: BriefingResearch): string {
    if (r.status !== 'ready' && r.status !== 'partial') return '';
    const coverage = r.coverage ?? [];
    if (!coverage.length) return '';
    const gaps = coverage
      .map(c => ({ q: c.question.toUpperCase(), needs: [...c.not_found, ...c.missing] }))
      .filter(c => c.needs.length);
    if (!gaps.length) return 'Public facts cover all seven questions.';
    return 'Not found in public sources: '
      + gaps.map(g => `${g.q} ${g.needs.map(n => n.replace(/_/g, ' ')).join(', ')}`).join(' · ');
  }
  // Briefing is a relationship record, not an email action. The Portfolio endpoint
  // accepts a company-only record and can ground it in team notes, so a missing
  // person email must not hide the company's briefing. Compose, reply and chat keep
  // using `relationshipActions` / `canAskAssistant` below.
  const briefingAllowed = $derived(!!selected && !selected.automated_address
    && (selected.role === 'investor' || isPortfolioContact(selected)));
  const portfolioPage = $derived(!!selected && isPortfolioContact(selected));
  const lpBriefingAllowed = $derived(briefingAllowed && !portfolioPage);
  // Potential Portfolio uses the same record; Neither can read earlier team notes only.
  const recordAllowed = $derived(!!selected && (selected.role === 'neither'
    || (!selected.automated_address && (selected.role === 'investor'
        || selected.role === 'portfolio' || potentialPortfolio(selected)))));
  // Only fall back after a person is selected. This component also stays mounted
  // on Today, where `selected` is null; changing tabs there would make a later
  // sidebar click open an LP on Emails instead of the default Briefing.
  $effect(() => {
    if (view !== 'detail' || loading || !selected) return;
    if (detailTab === 'briefing' && !briefingAllowed) detailTab = 'emails';
  });
  // Every workspace reload replaces `selected` with an equal copy, and loading
  // the briefing again on that blanks it mid-read or mid-edit, so only a change
  // of LP (or leaving and returning) loads it. Not reactive on purpose.
  let briefingLoadedFor = '';
  $effect(() => {
    const target = view === 'detail' && lpBriefingAllowed ? (selected?.counterparty_id ?? '') : '';
    untrack(() => {
      if (target === briefingLoadedFor) return;
      briefingLoadedFor = target;
      if (!target) return;
      briefingNotice = '';
      briefing.load(target);
    });
  });
  // Poll and heartbeat timers outlive the component otherwise.
  $effect(() => () => briefing.destroy());
  // A closed tab cannot run the view's teardown; release the edit lock on the way out.
  $effect(() => {
    const release = () => briefing.releaseOnUnload();
    window.addEventListener('pagehide', release);
    return () => window.removeEventListener('pagehide', release);
  });

  // Dev only: the three primary contacts from seed-fund-ui-demo.sh each have a
  // distinct synthetic briefing. The production bundle excludes the loader.
  let briefingFixture = $state<Briefing | null>(null);
  let briefingFixtureNotice = $state('');
  const sandboxDemoIds = new Set(['ui-demo-elliot', 'ui-demo-david', 'ui-demo-lena']);
  const sandboxDemoAvailable = $derived(import.meta.env.DEV
    && $userEmailStore === 'ui@jojo-ui.test'
    && !!selected && sandboxDemoIds.has(selected.counterparty_id));
  const visibleBriefingFixture = $derived(briefingFixture?.counterparty_id === selected?.counterparty_id
    ? briefingFixture : null);
  async function loadSandboxDemo(id: string) {
    // Keep the import inside this direct compile-time guard: Vite must omit
    // both the fixture and its text from dist-stage and live builds.
    if (import.meta.env.DEV) {
      const { sandboxDemoBriefing, sandboxDemoNotice } = await import('../lib/briefing/sandbox-demo');
      if (selected?.counterparty_id === id) {
        briefingFixture = sandboxDemoBriefing(id);
        briefingFixtureNotice = sandboxDemoNotice;
      }
    }
  }
  $effect(() => {
    const id = selected?.counterparty_id;
    if (!sandboxDemoAvailable || !id) return;
    void loadSandboxDemo(id);
  });

  /** A footnote on an email source opens that message in the Emails tab. Only
   *  email sources reach here — this page has nowhere to send a meeting. */
  function openBriefingSource(source: Pick<BriefingSource, 'message_id' | 'owner_email'>) {
    if (!selected || !source.message_id) return;
    const owner = source.owner_email || $userEmailStore;
    const key = `${selected.counterparty_id}:${owner}:${source.message_id}`;
    detailTab = 'emails';
    // A message renders only inside its expanded thread, and a search can hide that thread.
    emailQuery = '';
    const thread = emailThreads.find(item => item.messages.some(message =>
      message.message_id === source.message_id && message.owner_email === owner));
    if (thread) {
      expandedEmailThreads = new Set(expandedEmailThreads).add(thread.key);
      activeEmailThreadKey = thread.key;
    }
    // toggleMessage closes an open message, so only call it to open one.
    if (!openedMessages.has(key)) void toggleMessage(selected.counterparty_id, owner, source.message_id);
    requestAnimationFrame(() => document.getElementById(`fund-msg-${key}`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }));
  }
  // A confirmed institution owns the relationship view; each email still belongs
  // to its actual contact and synced mailbox. Unconfirmed domain hints never widen it.
  const relationshipEmails = $derived(selectedInstitution
    ? institutionActivity
    : (contactContext?.messages || []).map(message => ({ ...message,
        counterparty_id: selected?.counterparty_id || '',
        display_name: selected?.display_name || '',
        email: selected?.email || '' })));
  const visibleRelationshipEmails = $derived(relationshipEmails.filter(message =>
    !emailQuery.trim() || `${message.subject} ${message.sender} ${message.display_name}`
      .toLowerCase().includes(emailQuery.trim().toLowerCase())));
  const emailThreads = $derived.by(() => {
    type Message = (typeof relationshipEmails)[number];
    const groups = new Map<string, { key: string; subject: string; sent_at: string; messages: Message[] }>();
    const seen = new Set<string>();
    for (const message of relationshipEmails) {
      // A provider thread ID is scoped to the connected mailbox. Old evidence
      // without one stays as a single message; subject equality is not proof.
      const key = message.thread_id
        ? `${message.owner_email}:thread:${message.thread_id}`
        : `${message.owner_email}:message:${message.message_id}`;
      const evidenceKey = `${message.owner_email}:${message.message_id}`;
      if (seen.has(evidenceKey)) continue;
      seen.add(evidenceKey);
      const group = groups.get(key);
      if (group) group.messages.push(message);
      else groups.set(key, { key, subject: message.subject || '(No subject)',
        sent_at: message.sent_at, messages: [message] });
    }
    return [...groups.values()];
  });
  function spanSince(stamp: string | null | undefined): string {
    const time = mailTime(stamp);
    if (!time) return '';
    const days = Math.floor((Date.now() - time) / 86400000);
    if (days < 1) return 'today';
    if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
    if (days < 60) return `${Math.floor(days / 7)} weeks`;
    if (days < 730) return `${Math.floor(days / 30.44)} months`;
    return `${Math.floor(days / 365)} years`;
  }
  // Header status line (designs/client/header.html). Waiting/owed reuse the Today Chase/Respond signals.
  const headerStatus = $derived.by(() => {
    if (!selected) return null;
    const ids = new Set(selectedInstitution ? institutionMembers.map(c => c.counterparty_id) : [selected.counterparty_id]);
    const latest = relationshipEmails.reduce<(typeof relationshipEmails)[number] | null>((best, message) =>
      !best || mailTime(message.sent_at) > mailTime(best.sent_at) ? message : best, null);
    let last = '';
    if (latest) {
      const direction = messageDirection(latest);
      const subject = latest.subject
        ? ` “${latest.subject.length > 60 ? `${latest.subject.slice(0, 57)}…` : latest.subject}”` : '';
      const when = emailListDate(latest.sent_at);
      const mine = latest.owner_email.toLowerCase() === $userEmailStore.toLowerCase();
      last = direction === 'outbound' ? `Last: ${mine ? 'you' : 'your team'} emailed${subject}, ${when}`
        : direction === 'inbound' ? `Last: ${selectedInstitution ? latest.display_name || 'they' : 'they'} wrote${subject}, ${when}`
        : `Last email${subject}, ${when}`;
    }
    const due = relationshipDue(selected);
    const respond = respondDecisions.find(decision => ids.has(decision.counterparty_id));
    const chase = chaseDecisions.find(decision => ids.has(decision.counterparty_id));
    const recent = (direction: string) => relationshipEmails.some(message =>
      messageDirection(message) === direction && Date.now() - mailTime(message.sent_at) <= 30 * 86400000);
    const state = followUpStatus(selected) === 'overdue' && due
      ? { label: 'Overdue', icon: '●', color: 'var(--red-ink)', age: spanSince(due) }  // -ink: text-contrast step of the status hue
      : respond ? { label: 'You owe a reply', icon: '⚠', color: 'var(--orange-ink)', age: spanSince(respond.sent_at) }
      : chase ? { label: 'Waiting on them', icon: '⚠', color: 'var(--orange-ink)', age: spanSince(chase.sent_at) }
      : recent('inbound') && recent('outbound') ? { label: 'Active', icon: '✓', color: 'var(--green-ink)', age: '' }
      : null;
    return state || last ? { state, last } : null;
  });
  function emailListDate(value: string | null | undefined): string {
    if (!value) return '—';
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
      ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    });
  }
  /* Direction comes off the headers the way `state_db.py` derives mail activity:
     exactly one From address, matched against the synced mailbox and the LP's own
     address. An ambiguous sender gets no label rather than a guess. */
  function senderAddress(raw: string): string {
    const found = (raw || '').match(/[^\s<>,;"]+@[^\s<>,;"]+/g) || [];
    return found.length === 1 ? found[0].toLowerCase() : '';
  }
  function messageDirection(message: { sender: string; owner_email: string; email?: string }): string {
    const address = senderAddress(message.sender);
    if (!address) return '';
    if (address === (message.owner_email || '').toLowerCase()) return 'outbound';
    if (address === (message.email || '').toLowerCase()) return 'inbound';
    return '';
  }
  /** One line of the already-synced excerpt — a collapsed row costs no request. */
  function messageSnippet(excerpt: string | undefined): string {
    return repairLostApostrophes(stripCids(excerpt || ''))
      .replace(/[#*>`_~-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 140);
  }
  /* What a thread-level Respond answers: the newest message that is both this LP's
     and in the viewer's own mailbox, because that is what the provider threads on.
     `relationshipEmails` is newest-first, so the first match is the newest. */
  function threadReplyTarget(thread: { messages: (typeof relationshipEmails)[number][] }) {
    return thread.messages.find(message => message.counterparty_id === selected?.counterparty_id
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase());
  }
  const replyThreadAvailable = $derived(!!composeReplyMessageId && emailThreads.some(thread =>
    thread.messages.some(message => message.message_id === composeReplyMessageId
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase())));
  const visibleEmailThreads = $derived(emailThreads.filter(thread => !emailQuery.trim()
    || thread.messages.some(message => visibleRelationshipEmails.includes(message))));
  function replyDecisionForThread(thread: (typeof emailThreads)[number]) {
    return respondDecisions.find(decision => thread.messages.some(message =>
      message.counterparty_id === decision.counterparty_id
      && message.message_id === decision.message_id
      && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase()));
  }
  const orderedEmailThreads = $derived([...visibleEmailThreads].sort((a, b) =>
    Number(!!replyDecisionForThread(b)) - Number(!!replyDecisionForThread(a))));
  const activeEmailThread = $derived(visibleEmailThreads.find(thread => thread.key === activeEmailThreadKey)
    || (visibleEmailThreads.length === 1 ? visibleEmailThreads[0] : null));
  const mikeContextLabel = $derived(detailTab === 'briefing'
    ? `Reading: ${portfolioPage ? 'Portfolio briefing' : readingLabel}`
    : detailTab === 'emails' && activeEmailThread
      && activeEmailThread.messages.some(message => message.counterparty_id === selected?.counterparty_id)
      ? `Looking at: ${activeEmailThread.subject.replace(/^\[Synthetic sandbox\]\s*/i, '')} (thread)`
      : `Looking at: ${detailTab === 'assistant' ? 'this relationship' : detailTab[0].toUpperCase() + detailTab.slice(1)}`);
  function toggleEmailThread(thread: (typeof emailThreads)[number]) {
    const next = new Set(expandedEmailThreads);
    if (next.has(thread.key)) { next.delete(thread.key); activeEmailThreadKey = ''; }
    else {
      next.add(thread.key);
      activeEmailThreadKey = thread.key;
      const message = thread.messages[0];
      if (message && !openedMessages.has(`${message.counterparty_id}:${message.owner_email}:${message.message_id}`))
        toggleMessage(message.counterparty_id, message.owner_email, message.message_id);
    }
    expandedEmailThreads = next;
  }
  function openThreadInEmails(thread: (typeof emailThreads)[number]) {
    detailTab = 'emails';
    emailQuery = '';  // a search would hide the thread
    if (expandedEmailThreads.has(thread.key)) activeEmailThreadKey = thread.key;
    else toggleEmailThread(thread);
    requestAnimationFrame(() => document.getElementById(`fund-thread-${thread.key}`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
  }
  // Same visibility as the Emails tab: Neither and unfiled contacts show no mail.
  const peopleMailShown = $derived(recordAllowed && selected?.role !== 'neither');
  // Only mail this page already loaded; a shared domain never widens it (see `relationshipEmails`).
  function personMail(person: FundContact): { state: 'loading' | 'error' | 'elsewhere' | 'ready';
      error?: string; threads: (typeof emailThreads)[number][] } {
    if (selectedInstitution) {
      if (institutionActivityLoading) return { state: 'loading', threads: [] };
      if (institutionActivityError) return { state: 'error', error: institutionActivityError, threads: [] };
    } else if (person.counterparty_id !== selected?.counterparty_id) return { state: 'elsewhere', threads: [] };
    else if (!contactContext && contextError) return { state: 'error', error: contextError, threads: [] };
    else if (!contactContext) return { state: 'loading', threads: [] };
    return { state: 'ready', threads: emailThreads.filter(thread =>
      thread.messages.some(message => message.counterparty_id === person.counterparty_id)) };
  }
  /* Who on our side has actually touched this LP. No ownership is stored, so this is
     read back off the records — the mailboxes the synced messages came from, plus
     whoever confirmed the relationship — rather than claimed as an account owner. */
  /* The people on this LP entity. An LP is an organization holding 1..n people; a
     confirmed `fund_institutions` grouping wins, and failing that colleagues are read
     off the shared corporate domain and must say so — a domain is a display hint, never
     a confirmed institution (Sigma Lithium alone spans three registrable domains).
     A personal-email contact has no `suggested_organization_domain` at all — the backend
     leaves it null on consumer domains — so this is empty and they stand alone. */
  const lpColleagues = $derived.by(() => {
    if (!selected) return [] as { contact: FundContact; source: string }[];
    if (selected.institution_id)
      return institutionMembers.map(contact => ({ contact, source: 'LP institution' }));
    const domain = selected.suggested_organization_domain;
    if (!domain) return [];
    return allContacts
      .filter(c => c.counterparty_id !== selected.counterparty_id
        && c.suggested_organization_domain === domain
        // A Potential Portfolio from mail has no role; unfiled colleagues must not match it.
        && (isPortfolioContact(selected) ? isPortfolioContact(c) : c.role === selected.role)
        && !c.automated_address)
      .map(contact => ({ contact, source: 'Shared email domain' }));
  });
  /* "no reply yet" from the synced mail itself — a message whose sender carries their
     address. Only the open contact's evidence is loaded, so colleagues get no such claim
     rather than a guessed one. `null` means we cannot say. */
  const selectedHasReplied = $derived.by(() => {
    const messages = contactContext?.messages || [];
    const address = (selected?.email || '').toLowerCase();
    if (!address || !messages.length) return null;
    return messages.some(message => (message.sender || '').toLowerCase().includes(address));
  });
  /* Memory's record block, read off workspace rows and synced mail only (no model call, so it is
     right before any brief exists). Role and stage appear only when mail set them; the header
     already shows and edits the team's own choices. */
  const lpRecord = $derived.by(() => {
    const rows: { label: string; value: string; detail?: string; lines?: string[] }[] = [];
    if (!selected) return rows;
    if (selected.email) rows.push({ label: 'Contact', value: `${selected.display_name} · ${selected.email}` });
    const messages = contactContext?.messages;
    if (messages) {
      const dates = messages.map(m => m.sent_at).filter(Boolean).sort();
      const address = (selected.email || '').toLowerCase();
      const replied = !!address && messages.some(m => (m.sender || '').toLowerCase().includes(address));
      rows.push({ label: 'Mail', value: messages.length
        ? `${messages.length} message${messages.length === 1 ? '' : 's'}`
          + (dates.length ? `, ${mailDate(dates[0])} – ${mailDate(dates[dates.length - 1])}` : '')
          + (replied ? ' · they have replied' : ' · no reply from them yet')
        : 'No synced email yet' });
    }
    if (!selected.role && !isPortfolioContact(selected) && selected.suggested_source !== 'model_candidate')
      rows.push({ label: 'Filed', value: 'Not filed yet · choose a role under ⋯' });
    else if (selected.suggested_source === 'model_candidate' || (selected.role && selected.set_by === 'model'))
      rows.push({ label: 'Filed', value: `${typeLabel(selected)} · from synced mail`,
        detail: selected.suggested_reason || '' });
    const stageSource = relationshipStageSource(selected);
    if (relationshipStage(selected) && (stageSource === 'ai' || stageSource === 'candidate' || stageSource === 'admin_proof'))
      rows.push({ label: 'Stage', value: `${fundStageLabel(relationshipStage(selected))} · from `
        + (stageSource === 'admin_proof' ? 'fund administration mail' : 'synced mail') });
    if (!selectedInstitution && selected.suggested_organization_name
        && organizationHint(selected)?.name !== selected.suggested_organization_name)
      rows.push({ label: 'Organization', value: `${selected.suggested_organization_name} · named in their mail, not grouped` });
    if (teamOnLp.length > 1) rows.push({ label: 'Team', value: '',
      lines: teamOnLp.map(person => `${person.email} · ${person.notes.toLowerCase()}`) });
    return rows;
  });
  // Mike's "Save to memory" ends its note with this line (fund_ir.save_chat_to_memory); it only tags the row.
  const CHAT_NOTE_MARK = '_Saved from the assistant chat._';
  const memoryNotes = $derived((contactContext?.notes || []).map(note => {
    const body = note.body.trimEnd();
    const fromChat = body.endsWith(CHAT_NOTE_MARK);
    return { ...note, fromChat, text: fromChat ? body.slice(0, -CHAT_NOTE_MARK.length).trimEnd() : note.body };
  }));
  let openNoteId = $state('');
  // Generated brief/draft fold away, except when a flow lands here to show one.
  let generatedOpen = $state(false);
  const teamOnLp = $derived.by(() => {
    const roles = new Map<string, Set<string>>();
    const note = (email: string, text: string) => {
      const key = email.trim().toLowerCase();
      if (!key) return;
      if (!roles.has(key)) roles.set(key, new Set());
      roles.get(key)!.add(text);
    };
    for (const message of contactContext?.messages || []) note(message.owner_email, 'Synced this thread');
    note(selected?.confirmed_by || '', 'Set the relationship');
    if (selected && relationshipNextStep(selected)) {
      const setter = (relationshipInstitution(selected) || selected).next_step_set_by || '';
      if (setter.includes('@')) note(setter, 'Set the next step');
    }
    return [...roles.entries()].map(([email, texts]) => ({ email, notes: [...texts].join(' · ') }));
  });
  /* CC candidates: the other people on this LP entity, plus the teammates who already
     have mail with it. The recipient stays locked to this LP — only CC is picked
     here, it only ever appends to what you typed, and the server re-validates every
     address before the send ledger sees it. */
  const unique = (addresses: string[], exclude: string[] = []) => addresses.filter(
    (address, index, all) => !!address && address !== selected?.email
      && !exclude.includes(address) && all.indexOf(address) === index);
  const ccLpContacts = $derived(unique(lpColleagues.map(entry => entry.contact.email || '')));
  const ccTeam = $derived(unique(teamOnLp.map(person => person.email), ccLpContacts));
  const ccSuggestions = $derived([...ccLpContacts, ...ccTeam]);
  function toggleComposeDocument(documentId: string) {
    composeDocumentIds = composeDocumentIds.includes(documentId)
      ? composeDocumentIds.filter(id => id !== documentId)
      : [...composeDocumentIds, documentId];
  }
  /* The picker appends: choosing a second time in a `<input type="file">` replaces its
     own FileList, which silently dropped everything attached before it. */
  function addComposeFiles(event: Event & { currentTarget: HTMLInputElement }) {
    const picked = Array.from(event.currentTarget.files || []);
    const known = new Set(composeFiles.map(file => `${file.name}:${file.size}`));
    composeFiles = [...composeFiles,
      ...picked.filter(file => !known.has(`${file.name}:${file.size}`))];
    event.currentTarget.value = '';
  }
  function removeComposeFile(index: number) {
    composeFiles = composeFiles.filter((_, position) => position !== index);
  }
  const ccAlreadyIncluded = (address: string) =>
    composeCc.toLowerCase().split(/[,;]/).map(part => part.trim()).includes(address.toLowerCase());
  function addCc(address: string) {
    if (ccAlreadyIncluded(address)) return;
    const current = composeCc.trim().replace(/,$/, '');
    composeCc = current ? `${current}, ${address}` : address;
  }
  $effect(() => {
    const id = selectedInstitution?.institution_id || selectedOrganizationRecord?.institution_id;
    if (view !== 'detail' || (!selectedOrganizationRecord && detailTab !== 'contacts' && detailTab !== 'emails') || !id) {
      institutionActivity = [];
      institutionActivityError = '';
      return;
    }
    institutionActivity = [];
    institutionActivityError = '';
    institutionActivityLoading = true;
    getFundInstitutionActivity(id).then(result => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivity = result.messages;
    }).catch((err: any) => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivityError = err?.message || 'Could not load institution activity.';
    }).finally(() => {
      if (selected?.institution_id === id || selectedOrganizationRecord?.institution_id === id) institutionActivityLoading = false;
    });
  });
  const shown = $derived(allContacts.filter(c => {
    const term = contactsSearch.trim().toLowerCase();
    return !term || `${c.display_name} ${c.email ?? ''} ${organizationHint(c)?.name ?? ''} ${c.suggested_reason ?? ''}`.toLowerCase().includes(term);
  }).sort(mostRecentFirst));
  // Contacts shows people with an LP or Portfolio relationship, including
  // early model suggestions. Raw mailbox participants remain available to
  // classification but do not become directory cards on discovery alone.
  const directoryContacts = $derived(shown.filter(isFundDirectoryContact));
  const lpDirectoryContacts = $derived(directoryContacts.filter(c => displayRole(c) === 'investor'));
  const portfolioDirectoryContacts = $derived(directoryContacts.filter(c => displayRole(c) === 'portfolio'));
  const PORTFOLIO_TYPES = [{ invested: true, label: 'Existing' }, { invested: false, label: 'Potential' }] as const;
  // An existing holding has a filed, non-Potential Portfolio member; anything else is Potential.
  const companyInvested = (group: IrEntity) =>
    group.contacts.some(c => c.role === 'portfolio' && !potentialPortfolio(c));
  /* Sales Contacts' shape: section → organization row with its pill → people. LP sections are
     stages; Portfolio has no stage, so its two sections are Existing and Potential. */
  const contactSections = $derived.by(() => {
    const groups = groupFundContacts(contactsTab === 'lp' ? lpDirectoryContacts : portfolioDirectoryContacts)
      .sort((a, b) => a.name.localeCompare(b.name));
    const sections = contactsTab === 'portfolio'
      ? [{ key: 'existing', label: 'Existing', color: fundStageColor('Client'), groups: groups.filter(companyInvested) },
         { key: 'potential', label: 'Potential', color: 'var(--text-dim)', groups: groups.filter(g => !companyInvested(g)) }]
      : [...FUND_STAGES.map(s => s.key), ''].map(key => ({
          key: key || 'none', label: key ? fundStageLabel(key) : 'No stage',
          color: key ? fundStageColor(key) : 'var(--text-dim)',
          groups: groups.filter(g => contactGroupStage(g) === key) }));
    return sections.filter(section => section.groups.length);
  });
  // The pill's target: the LP whose (institution's) stage the row shows, as on the Dashboard.
  function contactGroupStage(group: IrEntity): string {
    const target = entityStageContact(group);
    return (target && relationshipStage(target)) || '';
  }
  const stageEditable = (group: IrEntity) => actionableLp(entityStageContact(group)) && !savingStage;
  const typeEditable = (group: IrEntity) => !savingRole && group.contacts.some(c => !!c.email && !c.automated_address);
  function toggleContactSection(key: string) {
    const next = new Set(collapsedContactSections);
    if (next.has(key)) next.delete(key); else next.add(key);
    collapsedContactSections = next;
  }
  // Existing ↔ Potential for every member with an address; next step and briefing stay.
  async function setCompanyInvested(group: IrEntity, invested: boolean) {
    openStageDD = null;
    const role: FundRoleChoice = invested ? 'portfolio' : 'potential_portfolio';
    const targets = group.contacts.filter(c => c.email && !c.automated_address && roleChoice(c) !== role);
    if (!targets.length || savingRole) return;
    savingRole = group.key; actionError = '';
    try {
      for (const contact of targets) await confirmFundRole({ email: contact.email!, name: contact.display_name }, role);
    } catch (err: any) {
      actionError = err?.message || 'Could not save this company.';
    } finally {
      savingRole = '';
      await load();
    }
  }
  function startContactEdit(contact: FundContact) {
    editingContactId = contact.counterparty_id;
    editName = contact.display_name;
    editRole = roleChoice(contact);
    actionError = '';
  }
  async function saveContactEdit(contact: FundContact) {
    const name = editName.trim();
    if (!name || savingContactEdit) return;
    const role = editRole;
    const roleChanged = !!role && role !== 'unknown' && role !== roleChoice(contact);
    if (roleChanged && role === 'neither' && !confirmNeither(contact)) return;
    savingContactEdit = true; actionError = '';
    try {
      // Filing saves the name with the role; a name-only edit has its own route.
      if (roleChanged && contact.email) await confirmFundRole({ email: contact.email, name }, role as FundRoleChoice);
      else if (name !== contact.display_name) await renameFundContact(contact.counterparty_id, name);
      editingContactId = '';
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this contact.';
    } finally { savingContactEdit = false; }
  }
  const confirmNeither = (contact: FundContact) => confirm(`Remove ${contact.display_name} from LP and Portfolio contacts?\n\n`
    + 'They are marked Neither. Synced mail and team notes stay; their briefing, stage, next step and pin are cleared.');
  async function removeFromContacts(contact: FundContact) {
    if (!contact.email || savingRole || !confirmNeither(contact)) return;
    await setRole(contact, 'neither');
  }
  const shownScheduled = $derived(allScheduled.filter(item => scheduledFilter === 'all'
    || item.status === scheduledFilter
    || (scheduledFilter === 'attention' && (item.status === 'uncertain' || item.status === 'claimed'))));

  /* Relationships filters on search alone — the view tabs do the rest. `shown` stays the
     Contacts page's list. */
  const searchTerm = $derived(dashboardSearch.trim().toLowerCase());
  /* Takes the term as an argument so the saved-view dropdown can count what each view
     would show — it needs to match against a stored term, not the one in the box. */
  const matchesSearch = (c: FundContact, term: string) => !term
    || `${c.display_name} ${c.email ?? ''} ${organizationHint(c)?.name ?? ''} ${c.suggested_reason ?? ''}`.toLowerCase().includes(term);
  const bySearch = (c: FundContact) => matchesSearch(c, searchTerm);
  const FUND_SOON_DAYS = 7; // LP cadence is slower than Sales' 3-day Soon
  const SHARED_STATUS: Record<string, IrStatus> = {
    Overdue: 'overdue', Soon: 'soon', 'On track': 'later', Parked: 'undated' };
  function followUpStatus(contact: FundContact): IrStatus {
    if (!relationshipNextStep(contact)) return 'none';
    const due = relationshipDue(contact);
    return due ? SHARED_STATUS[followupStatus(due, '', new Date(), FUND_SOON_DAYS).label] : 'undated';
  }
  /** Chase work, as in Sales: due today or overdue. Soon stays a Dashboard status. */
  function nextStepIsDue(contact: FundContact): boolean {
    const due = relationshipDue(contact);
    return !!relationshipNextStep(contact) && !!due && due <= localDay();
  }
  const isPinned = (id: string) => $fundPins.some(pin => pin.counterparty_id === id);
  const isOrganizationPinned = (key: string) => $fundPins.some(pin => pin.entity_key === key);
  function entityKeyForContact(contact: FundContact): string | null {
    return contact.institution_id ? `institution:${contact.institution_id}`
      : contact.suggested_organization_domain ? `domain:${contact.suggested_organization_domain}` : null;
  }
  /** The route assigns a business-domain LP to its organization before writing. */
  function canAddNextStep(contact: FundContact): boolean {
    return relationshipActions(contact);
  }
  /* DESIGN-SYSTEM §4/§6: a read-only outline pill, and only when the contact has
     both a next step and a due date. Anything else renders an em dash — the page
     never guesses a status from mail activity alone. */
  function statusPill(contact: FundContact): { label: string; cls: string } | null {
    const status = followUpStatus(contact);
    if (status === 'overdue') return { label: 'Overdue', cls: 'status-pill-red' };
    if (status === 'soon') return { label: 'Soon', cls: 'status-pill-amber' };
    if (status === 'later') return { label: 'On track', cls: 'status-pill-green' };
    return null;
  }
  function toggleIrFilter<T extends string>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter(entry => entry !== value) : [...list, value];
  }
  function clearIrFilters() {
    irStatusFilter = []; irStageFilter = []; irInstitutionFilter = '';
    irPinnedOnly = false; dashboardSearch = ''; openFilterDD = null;
  }
  function toggleIrStageGroup(key: string) {
    const next = new Set(expandedIrStages);
    if (next.has(key)) next.delete(key); else next.add(key);
    expandedIrStages = next;
  }
  const irFiltersSet = $derived(irStatusFilter.length > 0 || irStageFilter.length > 0
    || !!irInstitutionFilter || irPinnedOnly || !!searchTerm);
  const IR_STAGE_OPTIONS = FUND_STAGES.map(s => ({ key: s.key, label: s.label }));  // an LP with no stage counts as Prospect
  const irStageLbl = $derived(!irStageFilter.length ? 'All'
    : irStageFilter.length === 1
      ? IR_STAGE_OPTIONS.find(option => option.key === irStageFilter[0])?.label || 'All'
      : `${irStageFilter.length} selected`);
  const irStatusLbl = $derived(!irStatusFilter.length ? 'All'
    : irStatusFilter.length === 1
      ? (IR_STATUS_OPTIONS.find(o => o.key === irStatusFilter[0])?.label ?? 'All')
      : `${irStatusFilter.length} selected`);
  const irInstitutionLbl = $derived(!irInstitutionFilter ? 'All'
    : (workspace?.institutions || []).find(i => i.institution_id === irInstitutionFilter)?.name || 'All');
  // Clicking anywhere outside an open chip or the ⋯ menu closes it, same as the Sales dashboard.
  function onWindowClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (openFilterDD && !target?.closest?.('.fchip-wrap')) openFilterDD = null;
    if (openStageDD && !target?.closest?.('.fund-ir-stage-dd')) openStageDD = null;
    if (moreOpen && !target?.closest?.('.lp-more')) moreOpen = false;
    if (viewDropdownOpen && !target?.closest?.('.dash-view-bar')) {
      // Drop the in-place rename with the menu, or reopening shows a stray edit box.
      viewDropdownOpen = false; editingViewKey = null;
    }
  }
  function toggleStageMenu(key: string, button: HTMLButtonElement) {
    if (openStageDD === key) { openStageDD = null; return; }
    const rect = button.getBoundingClientRect();
    stageMenuPosition = {
      top: Math.max(8, rect.bottom + 6),
      left: Math.max(8, Math.min(rect.right - 240, window.innerWidth - 248)),
      anchorTop: rect.top,
    };
    openStageDD = key;
  }
  /** Flip above the trigger when the menu's real height doesn't fit below (MultiSelect's `anchor` rule). */
  function fitStageMenu(node: HTMLElement) {
    const { top, anchorTop } = stageMenuPosition;
    const height = node.offsetHeight, below = window.innerHeight - top - 8, above = anchorTop - 14;
    if (height <= below) return;
    if (above > below) node.style.top = `${anchorTop - 6 - Math.min(height, above)}px`;
    node.style.maxHeight = `${Math.max(below, above)}px`;
  }
  /** The inline rename starts with the old name selected, so typing replaces it. */
  function focusSelect(node: HTMLInputElement) { node.focus(); node.select(); }
  /** The next-step line and the ⋯ entry open the same editor, seeded from the record. */
  function openFollowUpEditor() {
    nextStepDraft = selected ? relationshipNextStep(selected) || '' : '';
    followUpDateDraft = selected ? relationshipDue(selected) || '' : '';
    followUpEditing = true;
  }
  /* Chip counts are taken over the search result rather than the fully filtered
     rows: a count that shrank as you ticked a sibling chip reads as rows vanishing. */
  const irSearched = $derived(dashboardLps.filter(bySearch));
  const entityCount = (contacts: FundContact[]) => groupFundContacts(contacts).length;
  const stageRank = (stage: string | null) => {
    const index = FUND_STAGES.findIndex(option => option.key === stage);
    return index < 0 ? FUND_STAGES.length : index;
  };
  /* ONE predicate for "does this contact pass the IR filters". The table reads it with
     the live chips; the saved-view dropdown reads it with each view's stored chips to
     count what that view would show. A second copy of this is exactly how a count goes
     stale the next time someone adds a chip. */
  type IrFilters = { term: string; status: IrStatus[]; institution: string;
    stage: string[]; pinned: boolean };
  function irMatches(c: FundContact, f: IrFilters): boolean {
    return matchesSearch(c, f.term)
      && (!f.status.length || f.status.includes(followUpStatus(c)))
      && (!f.institution || c.institution_id === f.institution)
      && (!f.stage.length || f.stage.includes(relationshipStage(c) || ''))
      && (!f.pinned || isPinned(c.counterparty_id)
        || (!!entityKeyForContact(c) && isOrganizationPinned(entityKeyForContact(c)!)));
  }
  const irCurrentFilters = $derived<IrFilters>({ term: searchTerm, status: irStatusFilter,
    institution: irInstitutionFilter, stage: irStageFilter, pinned: irPinnedOnly });
  /* Contact order inside an entity, not row order: the rows are entities and they are
     sorted by `compareIrEntities` after grouping. */
  const irRows = $derived(dashboardLps.filter(c => irMatches(c, irCurrentFilters))
    .sort((a, b) => (relationshipDue(a) || '9999-12-31').localeCompare(relationshipDue(b) || '9999-12-31')
      || a.display_name.localeCompare(b.display_name)));
  // The Dashboard counts and renders relationship entities. Stage remains a
  // contact-level filter until there is a persisted institution-level stage;
  // grouping an institution under one person's stage would misstate its state.
  const irEntities = $derived(groupFundContacts(irRows));
  const allIrEntities = $derived(groupFundContacts(dashboardLps));
  function entityContactStage(contacts: FundContact[]): string {
    const stages = new Set(contacts.map(c => relationshipStage(c) || ''));
    return stages.size === 1 ? [...stages][0] : '__mixed__';
  }
  /* Sorting runs over ENTITIES, not the contacts underneath them. Every sortable cell
     shows an entity-level value — the soonest recorded action across its contacts, the
     most recent synced mail in either direction — so sorting the contacts and letting
     grouping order fall out of Map insertion would order rows by numbers the row does
     not display. `asc` always means "the order the column reads in": soonest date, most
     urgent status, most recent mail. Rows with nothing recorded sort last in `asc`,
     never first, because an empty cell is not the top of any ranking. */
  type IrEntity = ReturnType<typeof groupFundContacts>[number];
  const IR_STATUS_RANK: Record<IrStatus, number> = {
    overdue: 0, soon: 1, later: 2, undated: 3, none: 4,
  };
  const entityAction = (entity: IrEntity) => groupNextAction(entity.contacts);
  function entityStatusRank(entity: IrEntity): number {
    const action = entityAction(entity);
    return action ? IR_STATUS_RANK[followUpStatus(action)] : IR_STATUS_RANK.none;
  }
  function entityDue(entity: IrEntity): string {
    const action = entityAction(entity);
    return (action && relationshipDue(action)) || '9999-12-31';
  }
  function entityMail(entity: IrEntity, key: typeof irSort): number {
    if (key !== 'last_email') return mailTime(groupMailActivity(entity.contacts, key === 'outbound' ? 'outbound' : 'inbound'));
    // Without a subject (redacted View as, institution-only mail) the newest direction still sorts.
    const subject = groupLatestSubject(entity.contacts);
    return subject ? mailTime(subject.at) : Math.max(
      mailTime(groupMailActivity(entity.contacts, 'inbound')), mailTime(groupMailActivity(entity.contacts, 'outbound')));
  }
  function compareIrEntities(a: IrEntity, b: IrEntity): number {
    const byName = a.name.localeCompare(b.name);
    // A bigger timestamp is a SMALLER "Nd ago", and a missing one is 0, so
    // descending time is ascending age and puts never-synced rows last.
    const ranked = irSort === 'name' ? byName
      : irSort === 'status' ? entityStatusRank(a) - entityStatusRank(b) || byName
      : irSort === 'stage' ? stageRank(entityContactStage(a.contacts) || null)
          - stageRank(entityContactStage(b.contacts) || null) || byName
      : irSort === 'due' ? entityDue(a).localeCompare(entityDue(b)) || byName
      : entityMail(b, irSort) - entityMail(a, irSort) || byName;
    return irSortDir === 'desc' ? -ranked : ranked;
  }
  const irSortedEntities = $derived([...irEntities].sort(compareIrEntities));
  /* The row ★ pins the ENTITY: an organization goes to `fund_org_pins`, a standalone
     person to `fund_pins`. Only human or current-policy model LPs are actionable. */
  // The workspace API masks any model role the backend has not made actionable.
  const actionableLp = (contact: FundContact | undefined | null): contact is FundContact => !!contact
    && contact.role === 'investor';
  // The org pin route takes institution:/domain: keys only; any other group pins its person.
  const orgPinKey = (entity: IrEntity) => entity.organization && /^(?:institution|domain):/.test(entity.key);
  const entityPinContact = (entity: IrEntity) =>
    (!orgPinKey(entity) && actionableLp(entity.contacts[0])
      && !entity.contacts[0]?.automated_address)
      ? entity.contacts[0] : null;
  const entityPinnable = (entity: IrEntity) => orgPinKey(entity)
    ? entity.contacts.some(c => actionableLp(c))
    : !!entityPinContact(entity);
  const entityPinned = (entity: IrEntity) => {
    if (orgPinKey(entity)) return isOrganizationPinned(entity.key);
    const contact = entityPinContact(entity);
    return !!contact && isPinned(contact.counterparty_id);
  };
  function toggleEntityPin(entity: IrEntity) {
    if (orgPinKey(entity)) { toggleOrganizationPin(entity.key); return; }
    const contact = entityPinContact(entity);
    if (contact) togglePin(contact.counterparty_id);
  }
  /* Which record a stage write lands on. An institution owns its members' stage; a
     standalone person owns their own. A domain group writes through a confirmed member,
     and the backend saves the group as one institution on that first write. */
  const entityStageContact = (entity: IrEntity) =>
    entity.contacts.find(c => !!c.institution_id && c.role === 'investor')
    || entity.contacts.find(c => !!c.institution_id)
    || (entity.organization ? entity.contacts.find(c => actionableLp(c)) || null
      : entity.contacts[0] || null);
  // That first write turns a `domain:` row into an `institution:` row; keep it open.
  function keepEntityOpen(entity: IrEntity, contactId: string) {
    const contact = allContacts.find(c => c.counterparty_id === contactId);
    const key = contact && entityKeyForContact(contact);
    if (key && key !== entity.key && expandedFundOrganizations.has(entity.key))
      expandedFundOrganizations = new Set([...expandedFundOrganizations, key]);
    if (key && key !== entity.key && expandedContactOrganizations.has(entity.key))
      expandedContactOrganizations = new Set([...expandedContactOrganizations, key]);
  }
  /* The backend rechecks the actionable LP role before brief, draft, or send. */
  async function setEntityStage(entity: IrEntity, stage: string) {
    const target = entityStageContact(entity);
    if (!target || savingStage) return;
    openStageDD = null;
    savingStage = true; actionError = '';
    try {
      if (target.institution_id) await setFundInstitutionStage(target.institution_id, stage || null);
      else await setFundStage(target.counterparty_id, stage || null);
      await load();
      keepEntityOpen(entity, target.counterparty_id);
    } catch (err: any) {
      actionError = err?.message || 'Could not save this relationship stage.';
      await load();
    } finally { savingStage = false; }
  }
  async function resumeEntityAutoStage(entity: IrEntity) {
    const target = entityStageContact(entity);
    if (!target || savingStage) return;
    savingStage = true; actionError = '';
    try {
      if (target.institution_id) await resumeFundInstitutionAutoStage(target.institution_id);
      else await resumeFundAutoStage(target.counterparty_id);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not resume automatic stage updates.';
    } finally { savingStage = false; }
  }
  /* The rail's date field and the LP page's editor are two entry points on ONE write
     path (spec v3). `next_step` is passed through untouched — a date-only save must not
     blank the text someone wrote. */
  async function setEntityFollowUpDate(entity: IrEntity, date: string) {
    const target = entityStageContact(entity);
    if (!target || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      await writeNextStep(target, relationshipNextStep(target) || '', date || null);
      await load();
      keepEntityOpen(entity, target.counterparty_id);
    } catch (err: any) {
      actionError = err?.message || 'Could not save this follow-up date.';
      await load();
    } finally { savingFollowUp = false; }
  }
  const irGroups = $derived([...FUND_STAGES.map(s => s.key), '', '__mixed__']
    .map(key => ({
      key,
      label: key === '__mixed__' ? 'Mixed contact stages'
        : key ? fundStageLabel(key) : 'No stage',
      color: key === '__mixed__' || !key ? 'var(--text-dim)' : fundStageColor(key),
      rows: irSortedEntities.filter(entity => entityContactStage(entity.contacts) === key),
    })).filter(group => group.rows.length));
  /* One click sorts by a column; clicking the active one flips it. The design marks the
     sorted header with a filled arrow — `.th-sort` / `.sort-ind` are the shared Sales
     affordance in app.css, not a second vocabulary for this page. */
  function sortIr(key: typeof irSort) {
    if (irSort === key) irSortDir = irSortDir === 'asc' ? 'desc' : 'asc';
    else { irSort = key; irSortDir = 'asc'; }
  }
  const irAriaSort = (key: typeof irSort): 'none' | 'ascending' | 'descending' =>
    irSort !== key ? 'none' : irSortDir === 'asc' ? 'ascending' : 'descending';
  const irSortIndicator = (key: typeof irSort) =>
    irSort !== key ? '▼' : irSortDir === 'asc' ? '▲' : '▼';
  /* The spec's note under the table. It states what the scan is actually doing, because
     with stage groups the first row is not the top of the sort — it is the top of its
     group, and that has read as a broken sort before. Same glyph as the live header, so
     the two cannot disagree. */
  const IR_SORT_LABELS: Record<typeof irSort, string> = { name: 'LP', status: 'Status',
    due: 'Follow-up', inbound: 'Inbound', outbound: 'Outbound',
    last_email: 'Latest email', stage: 'Stage' };
  const irScanNote = $derived(`Showing ${irEntities.length} of ${allIrEntities.length}`
    + ` · grouped by stage, sorted within each group by ${IR_SORT_LABELS[irSort]}`
    + ` ${irSortIndicator(irSort)}`);
  const portfolioRows = $derived(portfolio.filter(bySearch));
  // The LP table's sort: same direction rule, so "▲" on a mail column is most recent first.
  function comparePortfolioCompanies(a: IrEntity, b: IrEntity): number {
    const byName = a.name.localeCompare(b.name);
    const ranked = portfolioSort === 'name' ? byName
      : entityMail(b, portfolioSort) - entityMail(a, portfolioSort) || byName;
    return portfolioSortDir === 'desc' ? -ranked : ranked;
  }
  const portfolioCompanies = $derived(groupFundContacts(portfolioRows).sort(comparePortfolioCompanies));
  const allPortfolioCompanies = $derived(groupFundContacts(portfolio));
  function sortPortfolio(key: typeof portfolioSort) {
    if (portfolioSort === key) portfolioSortDir = portfolioSortDir === 'asc' ? 'desc' : 'asc';
    else { portfolioSort = key; portfolioSortDir = 'asc'; }
  }
  const portfolioAriaSort = (key: typeof portfolioSort): 'none' | 'ascending' | 'descending' =>
    portfolioSort !== key ? 'none' : portfolioSortDir === 'asc' ? 'ascending' : 'descending';
  const portfolioSortIndicator = (key: typeof portfolioSort) =>
    portfolioSort !== key ? '▼' : portfolioSortDir === 'asc' ? '▲' : '▼';
  const PORTFOLIO_SORT_LABELS: Record<typeof portfolioSort, string> = { name: 'Company',
    last_email: 'Latest email', inbound: 'Inbound', outbound: 'Outbound' };
  const portfolioScanNote = $derived(`Showing ${portfolioCompanies.length} of ${allPortfolioCompanies.length}`
    + ` · grouped into Existing and Potential, sorted within each group by ${PORTFOLIO_SORT_LABELS[portfolioSort]}`
    + ` ${portfolioSortIndicator(portfolioSort)}`);
  function activateRowOnKey(event: KeyboardEvent, action: () => void) {
    // Rows can contain their own buttons (Pin on Dashboard, Stage in Contacts).
    // Their keyboard events bubble; only the row itself owns disclosure.
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    action();
  }
  // A company page is a member's page; prefer a filed Portfolio member, then the latest mail.
  const portfolioPageContact = (company: IrEntity) => company.contacts.find(c => c.role === 'portfolio' && !potentialPortfolio(c))
    || company.contacts.find(c => potentialPortfolio(c))
    || groupLatestMail(company.contacts) || company.contacts[0];
  const briefingPortfolioCompanies = $derived(allPortfolioCompanies.map(company => ({
    name: company.name,
    counterparty_id: portfolioPageContact(company).counterparty_id,
  })));
  // Portfolio has no stage: existing holdings, then Potential (Contacts uses the same split).
  const portfolioGroups = $derived([
    { key: '__invested__', label: 'Existing', color: fundStageColor('Client'), rows: portfolioCompanies.filter(companyInvested) },
    { key: '__potential__', label: 'Potential', color: 'var(--text-dim)', rows: portfolioCompanies.filter(c => !companyInvested(c)) },
  ].filter(group => group.rows.length));
  function togglePortfolioStage(key: string) {
    const next = new Set(collapsedPortfolioStages);
    if (next.has(key)) next.delete(key); else next.add(key);
    collapsedPortfolioStages = next;
  }
  /* The assistant reads this counterparty's synced mail. The server requires
     an actionable LP or a Portfolio contact for chat, brief and draft requests. */
  const canAskAssistant = $derived(!!selected?.email && relationshipActions(selected));
  const canGenerateDraft = $derived(!!contactContext?.messages.some(m => m.evidence_number)
    || !!contactContext?.notes?.length);
  const selectedName = $derived(selected?.display_name || 'this contact');
  let messages = $state<{ role: string; content: string }[]>([]);
  let chatInput = $state('');
  let chatLoading = $state(false);
  let chatError = $state('');
  let chatGeneration = 0;
  let memorySaving = $state(false);
  let memoryNotice = $state('');
  // Saving the same conversation again rewrites this note, as Sales' "Save to memory" does.
  let memoryNoteId = '';
  // A conversation belongs to one LP contact. A deep link changes `contactId`
  // without going through openContact(), so the reset lives here, not in the click.
  let chatContactId = $state('');
  // Runs after the click, so a click that wants another tab leaves it here (openContactAt).
  let landing: { contactId: string; tab: typeof detailTab; person: string } | null = null;
  $effect(() => {
    if (contactId === chatContactId) return;
    chatGeneration += 1;
    chatContactId = contactId;
    const land = landing?.contactId === contactId ? landing : null;
    landing = null;
    detailTab = land?.tab ?? 'briefing';
    expandedPerson = land?.person ?? '';
    if (land?.person) setTimeout(() => document.getElementById(`fund-person-${land.person}`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 60);
    messages = []; chatInput = ''; chatLoading = false; chatError = '';
    memorySaving = false; memoryNotice = ''; memoryNoteId = ''; closeFilePreview();
    openNoteId = ''; generatedOpen = false;
    sentReceipts = []; emailTracking = []; openedMessages = new Set(); emailQuery = ''; expandedEmailThreads = new Set(); activeEmailThreadKey = ''; messageBodies = {}; messageErrors = {}; messageRecipients = {}; expandedRecipients = new Set();
    refreshingThreads = false;
    scheduledSends = []; composeFiles = []; composeDocumentIds = [];
    groundingDocumentIds = [];
    noteEditorOpen = false; noteEditId = ''; noteTitle = ''; noteBody = '';
    composeOpen = false; composeSubject = ''; composeBody = ''; composeRequestId = '';
    sendNotice = '';
    if (todayReplyRequest?.counterparty_id === contactId && selected?.role === 'investor') {
      startReply({ message_id: todayReplyRequest.message_id,
        subject: todayReplyRequest.subject, owner_email: $userEmailStore });
      if (todayReplyRequest.draftBody) composeBody = todayReplyRequest.draftBody;
      if (todayReplyRequest.draftSubject) composeSubject = todayReplyRequest.draftSubject;
      todayReplyRequest = null;
    }
    if (todayComposeRequest?.contactId === contactId && selected?.role === 'investor') {
      startFundEmail();
      composeSubject = todayComposeRequest.subject;
      composeBody = todayComposeRequest.body;
      todayComposeRequest = null;
    }
    if (todayTabRequest?.counterparty_id === contactId) {
      detailTab = todayTabRequest.tab;
      generatedOpen = true;  // Today's "Prepare brief" lands here to show the brief.
      todayTabRequest = null;
    }
    if (todayChatSeed?.counterparty_id === contactId) {
      chatInput = todayChatSeed.text;
      if (mikeMode === 'tab') detailTab = 'assistant';
      // Same as clicking the bubble: overlay when narrow, the rail when wide.
      else if (mikeMode === 'bubble' || mikeNarrow) openMikeBubble();
      todayChatSeed = null;
    }
  });

  async function handleChat() {
    const asked = chatInput.trim();
    if (!asked || chatLoading || !selected) return;
    const askedContactId = selected.counterparty_id;
    const generation = chatGeneration;
    chatInput = '';
    chatError = '';
    const turns = [...messages, { role: 'user', content: asked }];
    messages = turns;
    chatLoading = true;
    try {
      const answer = await fundChat(askedContactId, turns);
      if (generation !== chatGeneration || contactId !== askedContactId) return;
      messages = [...messages, { role: 'assistant', content: answer.reply }];
    } catch (err: any) {
      if (generation !== chatGeneration || contactId !== askedContactId) return;
      // Restore the unsent turn to the composer so Retry sends it exactly once.
      messages = turns.slice(0, -1);
      chatInput = asked;
      chatError = err?.message || 'Could not answer from the synced context.';
    } finally {
      if (generation === chatGeneration && contactId === askedContactId) chatLoading = false;
    }
  }
  function quickAsk(question: string) { chatInput = question; handleChat(); }
  function restartConversation() {
    if (messages.length && !confirm('Restart the conversation? This clears the current chat.')) return;
    messages = []; chatError = ''; memoryNotice = ''; memoryNoteId = '';
  }
  async function saveChatToMemory() {
    if (memorySaving || !selected || !messages.some(m => m.role === 'assistant')) return;
    const id = selected.counterparty_id;
    const generation = chatGeneration;
    memorySaving = true; memoryNotice = ''; chatError = '';
    try {
      const saved = await saveFundChatMemory(id, messages, memoryNoteId);
      if (generation !== chatGeneration || contactId !== id) return;
      memoryNoteId = saved.note_id;
      memoryNotice = `${saved.updated ? 'Updated' : 'Saved'} “${saved.title}” in Memory.`;
      await reloadContext(id);
    } catch (err: any) {
      if (generation === chatGeneration && contactId === id) chatError = err?.message || 'Could not save to memory.';
    } finally {
      if (generation === chatGeneration && contactId === id) memorySaving = false;
    }
  }

  let contactFiles = $state<FundContactFile[]>([]);
  let contactFilesLoading = $state(false);
  let contactFilesError = $state('');
  let openFileKey = $state('');
  let filePreviewUrl = $state('');
  let filePreviewError = $state(false);
  function closeFilePreview() {
    filePreviewUrl = ''; filePreviewError = false; openFileKey = '';
  }
  // Frame the /api/ URL itself: the page CSP blocks blob: frames in prod. Use as adds a one-path ticket.
  async function toggleFilePreview(key: string, url: string, name: string) {
    const opening = openFileKey !== key;
    closeFilePreview();
    if (!opening) return;
    openFileKey = key;
    if (!isPdf(name)) return;
    try {
      const framed = await framedUrl(url);
      if (openFileKey === key) filePreviewUrl = framed;
    } catch {
      if (openFileKey === key) filePreviewError = true;
    }
  }
  const filesContactId = $derived(view === 'detail' && detailTab === 'files' ? selected?.counterparty_id || '' : '');
  $effect(() => {
    const id = filesContactId;
    contactFiles = []; contactFilesError = ''; contactFilesLoading = !!id;
    if (!id) return;
    getFundContactFiles(id).then(result => {
      if (filesContactId !== id) return;
      contactFiles = result.files;
    }).catch((err: any) => {
      if (filesContactId === id) contactFilesError = err?.message || 'Could not load files.';
    }).finally(() => {
      if (filesContactId === id) contactFilesLoading = false;
    });
  });

  /* One request feeds both sidebar stores: `fundPins` is the star state this page
     reads, `fundLps` the full list the shell renders. Called from every reload, not
     just the pin buttons — confirming a role adds a row, and a stage edit repaints
     its dot, so a list refreshed only on pin/unpin goes stale while you work. */
  async function refreshSidebarLps() {
    const data = await getFundPins();
    $fundPins = data.pinned || [];
    $fundLps = data.lps || [];
  }

  /* Only on a RELOAD. The shell's boot already fetched the list, so refreshing it on
     mount is one redundant request per page visit; what goes stale is the roster and
     the stage dots AFTER a write, and every one of those paths calls load() again.
     The stores repaint after each write's load(); a roster refresh failure must
     not blank the workspace. */
  let sidebarListLoaded = false;

  async function load() {
    if (sidebarListLoaded) void refreshSidebarLps().catch(() => {});
    sidebarListLoaded = true;
    try {
      const data = await getFundWorkspace();
      workspace = data;
      groundingDocumentIds = groundingDocumentIds.filter(id =>
        data.documents.some(doc => doc.document_id === id));
      error = '';
    } catch (err: any) {
      error = err?.message || 'Could not load your Investor Relations workspace.';
    } finally {
      loading = false;
    }
  }

  async function loadViews() {
    try {
      const data = await getFundDashboardViews();
      savedViews = Object.fromEntries(Object.entries(data.view_states)
        .filter(([, value]) => typeof value.name === 'string' && typeof value.tab === 'string')) as Record<string, FundViewPreset>;
      viewError = '';
    } catch (err: any) { viewError = err?.message || 'Could not load saved views.'; }
  }
  async function loadDashboardMailActivity() {
    try {
      const data = await getFundDashboardMailActivity();
      dashboardMailActivity = data.activity;
      dashboardLatestSubjects = data.latest_subjects || {};
      dashboardMailError = '';
    } catch (err: any) {
      dashboardMailActivity = {};
      dashboardLatestSubjects = {};
      dashboardMailError = err?.message || 'Mail activity is unavailable.';
    }
  }
  /* A view saved before the chips became multi-select holds a single string, and
     the status one used 'all' to mean no filter. Widen it rather than drop the view. */
  const widenStatus = (value: IrStatus[] | string): IrStatus[] =>
    Array.isArray(value) ? value : (!value || value === 'all' ? [] : [value as IrStatus]);
  const legacyFundStages: Record<string, string> = {
    prospect: 'Prospect', engaged: 'Engaged', due_diligence: 'Active Deal',
    commitment_in_progress: 'Near Close', active_lp: 'Client',
    on_hold: 'On Hold', former_lp: 'On Hold',
  };
  const widenStage = (value: string[] | string): string[] =>
    [...new Set((Array.isArray(value) ? value : (value ? [value] : []))
      .map(stage => legacyFundStages[stage] || stage || 'Prospect'))];  // a saved "Not set" ('') is now Prospect
  /* A view is a PRESET — the filters and sort you come back to — and the live chips are
     remembered tweaks on top of it. That is the model the Sales dashboard settled on
     (2026-07-17); this page reads the same control, so it must not invent a third one.
     `· modified` means "the chips no longer match the preset", and the two buttons are
     the only ways to resolve it: overwrite the preset, or keep it and save a new one. */
  const ALL_INVESTORS_KEY = '';
  const PORTFOLIO_VIEW_KEY = '__portfolio__';
  const PINNED_VIEW_KEY = '__builtin_pinned__';
  const UP_NEXT_VIEW_KEY = '__builtin_up_next__';
  const PIPELINE_VIEW_KEY = '__builtin_pipeline__';
  type FundSavedView = { key: string; name: string; bookmarked: boolean;
    builtin: boolean; preset: FundViewPreset };
  const emptyFundPreset = (): FundViewPreset => ({ name: 'All LPs', tab: 'ir',
    status: [], sort: 'due', sortDir: 'asc', institution: '', stage: [], pinned: false,
    search: '' });
  const currentFundPreset = (name: string, bookmarked = false): FundViewPreset =>
    ({ name, bookmarked, tab: dashTab, status: irStatusFilter, sort: irSort,
      sortDir: irSortDir, institution: irInstitutionFilter, stage: irStageFilter,
      pinned: irPinnedOnly, search: dashboardSearch });
  /* `name` and `bookmarked` are presentation, not filters. The builtin Portfolio
     view is a separate destination, so selecting it must not mark the active filter
     preset dirty. Older saved views can still restore their original destination. */
  const sameFundPreset = (a: FundViewPreset, b: FundViewPreset) =>
    JSON.stringify([[...widenStatus(a.status)].sort(), a.sort, a.sortDir || 'asc',
      a.institution, [...widenStage(a.stage)].sort(), !!a.pinned,
      (a.search || '').trim(), a.checked || ''])
    === JSON.stringify([[...widenStatus(b.status)].sort(), b.sort, b.sortDir || 'asc',
      b.institution, [...widenStage(b.stage)].sort(), !!b.pinned,
      (b.search || '').trim(), b.checked || '']);
  const allFundViews = $derived<FundSavedView[]>([
    { key: ALL_INVESTORS_KEY, name: 'All LPs', bookmarked: true, builtin: true,
      preset: emptyFundPreset() },
    { key: PINNED_VIEW_KEY, name: 'Pinned', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Pinned', pinned: true } },
    { key: UP_NEXT_VIEW_KEY, name: 'Up next', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Up next', status: ['overdue', 'soon'] } },
    { key: PIPELINE_VIEW_KEY, name: 'Pipeline', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Pipeline', stage: [
        'Near Close', 'Active Deal', 'Engaged', 'Top of Funnel', 'Prospect'] } },
    { key: PORTFOLIO_VIEW_KEY, name: 'Portfolio context', bookmarked: false, builtin: true,
      preset: { ...emptyFundPreset(), name: 'Portfolio context', tab: 'portfolio' } },
    ...Object.entries(savedViews).map(([key, preset]) => ({ key, name: preset.name,
      bookmarked: !!preset.bookmarked, builtin: false, preset })),
  ]);
  const activeFundView = $derived(allFundViews.find(v => v.key === activeViewKey)
    || allFundViews[0]);
  const fundViewDirty = $derived(!sameFundPreset(activeFundView.preset,
    currentFundPreset(activeFundView.name)));
  /* The count each view would show, computed through the one `irMatches` predicate. The
     chips only apply on the IR tab, exactly as the markup does it. Legacy
     sorting views now open the LP dashboard. */
  function fundViewCount(preset: FundViewPreset): number {
    const term = (preset.search || '').trim().toLowerCase();
    const source = preset.tab === 'portfolio' ? portfolio : dashboardLps;
    const rows = preset.tab !== 'portfolio'
      ? source.filter(c => irMatches(c, { term, status: widenStatus(preset.status),
          institution: preset.institution, stage: widenStage(preset.stage),
          pinned: !!preset.pinned }))
      : source.filter(c => matchesSearch(c, term));
    return groupFundContacts(rows).length;
  }
  function applyFundView(viewToApply: FundSavedView) {
    const preset = viewToApply.preset;
    activeViewKey = viewToApply.key;
    viewDropdownOpen = false;
    dashTab = preset.tab === 'portfolio' ? 'portfolio' : 'ir';
    irStatusFilter = widenStatus(preset.status);
    irSort = preset.sort;
    irSortDir = preset.sortDir || 'asc';
    irInstitutionFilter = preset.institution;
    irStageFilter = widenStage(preset.stage);
    irPinnedOnly = preset.pinned;
    dashboardSearch = preset.search;
  }
  // Kept for the deep-link/back paths that address a view by key.
  function applySavedView(key: string) {
    const match = allFundViews.find(v => v.key === key);
    if (match) applyFundView(match);
  }
  async function writeFundView(key: string, preset: FundViewPreset) {
    viewSaving = true; viewError = '';
    try {
      await saveFundDashboardView(key, preset);
      savedViews = { ...savedViews, [key]: preset };
      return true;
    } catch (err: any) {
      viewError = err?.message || 'Could not save view.';
      return false;
    } finally { viewSaving = false; }
  }
  async function saveCurrentView() {
    const name = newViewName.trim();
    if (!name || name.length > 60 || viewSaving) return;
    const key = crypto.randomUUID().replaceAll('-', '');
    if (await writeFundView(key, currentFundPreset(name))) {
      activeViewKey = key; newViewName = ''; viewNaming = false;
    }
  }
  async function updateActiveFundView() {
    if (activeFundView.builtin || viewSaving) return;
    await writeFundView(activeFundView.key,
      currentFundPreset(activeFundView.name, activeFundView.bookmarked));
  }
  function openSaveAsNew() {
    newViewName = ''; viewNaming = true; viewDropdownOpen = false;
  }
  async function toggleFundViewBookmark(viewToToggle: FundSavedView, event: MouseEvent) {
    event.stopPropagation();
    if (viewToToggle.builtin || viewSaving) return;
    await writeFundView(viewToToggle.key,
      { ...viewToToggle.preset, bookmarked: !viewToToggle.bookmarked });
  }
  async function renameFundView(viewToRename: FundSavedView, name: string) {
    const trimmed = name.trim();
    if (viewToRename.builtin || !trimmed || trimmed.length > 60
        || trimmed === viewToRename.name) return;
    await writeFundView(viewToRename.key, { ...viewToRename.preset, name: trimmed });
  }
  async function deleteFundView(viewToDelete: FundSavedView, event: MouseEvent) {
    event.stopPropagation();
    if (viewToDelete.builtin || viewSaving) return;
    if (!confirm(`Delete the saved view “${viewToDelete.name}”?`)) return;
    viewSaving = true; viewError = '';
    try {
      await deleteFundDashboardView(viewToDelete.key);
      const { [viewToDelete.key]: _removed, ...remaining } = savedViews;
      savedViews = remaining;
      if (activeViewKey === viewToDelete.key) activeViewKey = ALL_INVESTORS_KEY;
    } catch (err: any) { viewError = err?.message || 'Could not delete view.'; }
    finally { viewSaving = false; }
  }
  async function loadTodayDecisions() {
    const sequence = ++todayDecisionsSequence;
    try {
      const data = await getFundTodayDecisions();
      if (sequence === todayDecisionsSequence) {
        todayDecisions = data.decisions; todayDecisionsError = '';
      }
    } catch (err: any) {
      if (sequence === todayDecisionsSequence)
        todayDecisionsError = err?.message || 'Could not load mailbox suggestions.';
    } finally {
      if (sequence === todayDecisionsSequence) todayDecisionsLoaded = true;
    }
  }
  async function loadTodayBriefs() {
    // Consented Use as may inspect persisted AI notes, but support sessions never
    // write their read receipts or dismiss them (see toggle/dismiss below).
    if (isSupportView() && !canUseAs()) { todayBriefs = []; todayBriefsLoaded = true; return; }
    const sequence = ++todayBriefsSequence;
    try {
      const data = await getFundTodayBriefs();
      if (!data || !Array.isArray(data.briefs)) throw new Error('Today note response was incomplete.');
      if (sequence === todayBriefsSequence) {
        todayBriefs = data.briefs; todayBriefsError = '';
      }
    } catch (err: any) {
      if (sequence === todayBriefsSequence)
        todayBriefsError = err?.message || 'Could not load Today notes.';
    } finally {
      if (sequence === todayBriefsSequence) todayBriefsLoaded = true;
    }
  }
  const TODAY_BRIEF_TAG: Record<FundTodayBrief['signal_type'], string> = {
    post_call: 'Meeting outcome', momentum: 'Momentum',
    relationship_risk: 'Relationship risk', overdue_step: 'Overdue step',
    first_reply: 'First reply', top_up_window: 'Top-up window',
    coverage_gap: 'Coverage', onboarding_snapshot: 'Onboarding insight',
  };
  // Status, as Sales colours its Briefing: at risk red, needs you amber, good news green, information blue.
  const TODAY_BRIEF_TONE: Record<FundTodayBrief['signal_type'], 'red' | 'amber' | 'green' | 'blue'> = {
    relationship_risk: 'red', overdue_step: 'red', first_reply: 'amber', coverage_gap: 'amber',
    momentum: 'green', top_up_window: 'green', post_call: 'blue', onboarding_snapshot: 'blue',
  };
  const todayBriefTone = (brief: FundTodayBrief) => TODAY_BRIEF_TONE[brief.signal_type];
  function todayBriefName(brief: FundTodayBrief) {
    return todayLpGroups.find(group => group.key === brief.entity_key)?.name
      || allContacts.find(contact => contact.counterparty_id === brief.counterparty_id)?.display_name
      || 'LP relationship';
  }
  function todayBriefPreview(brief: FundTodayBrief) {
    return brief.content.split('\n').map(line => line.trim())
      .find(line => line && !line.startsWith('#') && !/^\*\*[^*]+\*\*$/.test(line))
      ?.replace(/\[\w\d+\]/g, '').replace(/\*\*/g, '').trim() || brief.headline;
  }
  async function toggleTodayBrief(brief: FundTodayBrief) {
    const key = `note:${brief.entity_key}`;
    const open = !todayExpanded[key];
    todayExpanded = { [key]: open };
    if (open && !brief.read && !isSupportView()) {
      try {
        await actOnFundTodayBrief(brief.entity_key, 'read');
        brief.read = true;
      } catch { /* The note remains readable if its read receipt fails. */ }
    }
  }
  async function dismissTodayBrief(brief: FundTodayBrief) {
    if (isSupportView()) return;
    try {
      await actOnFundTodayBrief(brief.entity_key, 'dismiss');
      todayBriefs = todayBriefs.filter(row => row.entity_key !== brief.entity_key);
    } catch (err: any) {
      todayBriefsError = err?.message || 'Could not dismiss this Today note.';
    }
  }
  // One verb per F13 type, landing where that move is made (designs/today/today-briefing-actions.md).
  type TodayBriefMove = { verb: string; mode: 'mike' | 'next-step' | 'emails' | 'contacts' | 'open'; ask?: string };
  const TODAY_BRIEF_MOVE: Record<FundTodayBrief['signal_type'], TodayBriefMove> = {
    post_call: { verb: 'Draft follow-up →', mode: 'mike', ask: 'We just met this LP. Draft the follow-up I should send: the specific takeaways, the next step we agreed and a concrete time or deliverable. Then suggest the next step I should record.' },
    momentum: { verb: 'Keep it moving →', mode: 'mike', ask: 'This LP just moved forward. Pick the one reviewable action that keeps the momentum, say why in two lines, and draft it if it is an email.' },
    relationship_risk: { verb: 'Address the risk →', mode: 'mike', ask: 'This note flags a risk. First tell me what in the mail supports it and what is still uncertain, then how I should address it; if outreach is right, draft it.' },
    overdue_step: { verb: 'Update next step →', mode: 'next-step' },
    first_reply: { verb: 'Read their reply →', mode: 'emails' },
    top_up_window: { verb: 'Prepare the top-up →', mode: 'mike', ask: 'This LP is discussing a further allocation. Help me prepare the top-up conversation without assuming a commitment: what they said, what to ask and what to have ready.' },
    coverage_gap: { verb: 'Widen coverage →', mode: 'contacts' },
    onboarding_snapshot: { verb: 'Read the briefing →', mode: 'open' },
  };
  const TODAY_BRIEF_NOTE_CHARS = 3400;  // ask + note stay under fund_ir._MAX_CHAT_CHARS (4,000)
  function actOnTodayBrief(brief: FundTodayBrief) {
    const contact = allContacts.find(item => item.counterparty_id === brief.counterparty_id);
    if (!contact) return;
    const move = TODAY_BRIEF_MOVE[brief.signal_type];
    if (move.mode === 'mike' && todayCanWrite(contact)) {
      const note = brief.content.replace(/\[\w\d+\]/g, '').trim().slice(0, TODAY_BRIEF_NOTE_CHARS);
      todayChatSeed = { counterparty_id: contact.counterparty_id,
        text: `${move.ask}\n\n--- ${agent}'s Today note: ${brief.headline} ---\n${note}` };
      openContact(contact);
    } else if (move.mode === 'next-step' && relationshipActions(contact)
        && (!isSupportView() || canUseAs())) openFollowUp(contact);
    else if (move.mode === 'emails' || move.mode === 'contacts') openContactAt(contact, move.mode);
    else openContact(contact);
  }
  async function loadTodayCalls() {
    const sequence = ++todayCallsSequence;
    const now = new Date();
    const start = now;
    const end = new Date(now.getTime() + 72 * 60 * 60 * 1000);
    todayCallsLoading = true;
    try {
      const data = await getFundTodayCalls(start.toISOString(), end.toISOString());
      if (!data || !Array.isArray(data.events)) throw new Error('Calendar response was incomplete.');
      if (sequence === todayCallsSequence && view === 'today') {
        todayCalls = data.events;
        todayCallsError = '';
      }
    } catch (err: any) {
      if (sequence === todayCallsSequence && view === 'today')
        todayCallsError = err?.message || 'Could not load upcoming LP calls.';
    } finally {
      if (sequence === todayCallsSequence) {
        todayCallsLoading = false;
        todayCallsLoaded = true;
      }
    }
  }
  async function actOnTodayDecision(decision: FundTodayDecision, action: 'dismiss' | 'snooze') {
    try {
      await actOnFundTodayDecision(decision, action);
      todayDecisions = todayDecisions.filter(item =>
        item.counterparty_id !== decision.counterparty_id || item.kind !== decision.kind
        || item.message_id !== decision.message_id);
      todayDecisionsError = '';
    } catch (err: any) {
      todayDecisionsError = err?.message || 'Could not update this suggestion.';
      await loadTodayDecisions();
    }
  }
  onMount(() => {
    load(); loadViews(); loadTodayDecisions(); loadTodayBriefs();
    const calendarRefresh = window.setInterval(() => { if (view === 'today') loadTodayCalls(); }, 5 * 60 * 1000);
    return () => window.clearInterval(calendarRefresh);
  });
  $effect(() => {
    if (view === 'today' || view === 'dashboard' || (view === 'detail' && selectedOrganization)) {
      $pollVersion; loadDashboardMailActivity();
    }
  });
  $effect(() => {
    if (view === 'today') { $pollVersion; loadTodayCalls(); }
  });
  async function loadAllScheduled() {
    scheduledLoading = true; scheduledError = '';
    try {
      const data = await getAllFundScheduled();
      allScheduled = data.messages;
    } catch (err: any) {
      scheduledError = err?.message || 'Could not load scheduled emails.';
    } finally { scheduledLoading = false; }
  }
  $effect(() => { if (view === 'scheduled' || view === 'today') loadAllScheduled(); });
  $effect(() => {
    if ($pollVersion > 0) { load(); loadTodayDecisions(); loadTodayBriefs(); }
  });
  async function reloadContext(id: string) {
    contextLoading = true; contextError = '';
    try {
      const data = await getFundContext(id);
      if (contactId === id) contactContext = data;
    } catch (err: any) {
      if (contactId === id) contextError = err?.message || 'Could not load relationship context.';
    } finally {
      if (contactId === id) contextLoading = false;
    }
  }
  $effect(() => {
    if (view !== 'detail' || !selected || !recordAllowed) {
      contactContext = null;
      return;
    }
    const id = selected.counterparty_id;
    contactContext = null;
    contextError = '';
    sentReceipts = []; emailTracking = []; scheduledSends = [];
    reloadContext(id);
    if (!relationshipActions(selected)) return;
    getFundSent(id).then(data => {
      if (contactId === id) sentReceipts = data.messages;
    }).catch(() => { /* Email context error remains the actionable alert. */ });
    getFundEmailTracking(id).then(data => {
      if (contactId === id) emailTracking = data.messages;
    }).catch(() => { /* Tracking is optional. */ });
    getFundScheduled(id).then(data => {
      if (contactId === id) scheduledSends = data.messages;
    }).catch(() => { /* The composer still works if the schedule list cannot load. */ });
  });

  function openContact(contact: FundContact) {
    if (view === 'today' || view === 'dashboard' || view === 'contacts') detailOrigin = view;
    draftInstruction = '';
    actionError = '';
    contactContext = null;
    detailTab = 'briefing';
    $currentView = contact.counterparty_id;
  }
  const detailBackLabel = $derived(detailOrigin === 'contacts' ? 'Contacts'
    : detailOrigin === 'today' ? 'Today' : 'Dashboard');
  function backFromDetail() { $currentView = detailOrigin; }
  function openPortfolioCompany(counterpartyId: string) {
    const contact = allContacts.find(item => item.counterparty_id === counterpartyId);
    if (contact && isPortfolioContact(contact)) openContact(contact);
  }
  /** Opens `contact` on `tab`; `person` expands and scrolls to that Contacts-tab entry. */
  function openContactAt(contact: FundContact, tab: typeof detailTab, person = '') {
    openContact(contact);
    landing = { contactId: contact.counterparty_id, tab, person };
  }
  // A person under an organization is an entry on that organization's page, not its top.
  function openDirectoryContact(contact: FundContact, grouped: boolean) {
    if (grouped) openContactAt(contact, 'contacts', contact.counterparty_id);
    else openContact(contact);
  }

  function openFollowUp(contact: FundContact) {
    if (!relationshipActions(contact)) return;
    followUpEditorRequested = contact.counterparty_id;
    followUpEditing = true;
    openContact(contact);
  }

  /** The one write path: the linked institution's record, else the person's. Mirrors `relationshipNextStep`. */
  async function writeNextStep(contact: FundContact, step: string, due: string | null) {
    const institution = relationshipInstitution(contact);
    if (institution) await setFundInstitutionFollowUp(institution.institution_id, step, due);
    else await setFundFollowUp(contact.counterparty_id, step, due);
  }

  let chaseError = $state('');
  let chaseSaving = $state('');
  /** Snooze moves the due date, as Sales Today does, so Dashboard and Chase agree. */
  async function settleDueNextStep(contact: FundContact, action: 'snooze' | 'done') {
    if (chaseSaving) return;
    chaseSaving = contact.counterparty_id; chaseError = '';
    try {
      const later = new Date();
      later.setDate(later.getDate() + 7);
      if (action === 'snooze') await writeNextStep(contact, relationshipNextStep(contact) || '', localDay(later));
      else await writeNextStep(contact, '', null);
      await load();
    } catch (err: any) {
      chaseError = err?.message || 'Could not update this next step.';
    } finally { chaseSaving = ''; }
  }

  function openTodayReply(decision: FundTodayDecision) {
    const contact = allContacts.find(item => item.counterparty_id === decision.counterparty_id);
    if (!contact || contact.role !== 'investor' || contact.automated_address) return;
    todayReplyRequest = { counterparty_id: contact.counterparty_id,
      message_id: decision.message_id, subject: decision.subject };
    openContact(contact);
  }

  function todayKey(decision: FundTodayDecision) {
    return `${decision.kind}:${decision.counterparty_id}:${decision.message_id}`;
  }
  function todayContact(decision: FundTodayDecision) {
    return allContacts.find(item => item.counterparty_id === decision.counterparty_id);
  }
  // Draft, rework and send address the LP's own mailbox thread; anyone else only gets Open.
  // Support view: drafting needs Use as, and sending is never offered (the server refuses it).
  function todayCanWrite(contact: FundContact | undefined): contact is FundContact {
    return !!contact?.email && contact.role === 'investor' && !contact.automated_address
      && (!isSupportView() || canUseAs());
  }
  function todayCardStage(contact: FundContact | undefined) {
    const stage = contact && relationshipStage(contact);
    return stage ? { label: fundStageLabel(stage), color: fundStageColor(stage) } : null;
  }
  function todayCardName(contact: FundContact | undefined, fallback: string) {
    return (contact && organizationHint(contact)?.name) || contact?.display_name || fallback;
  }
  // "Oct 13 (2d ago)", the third line of a Sales Today card.
  function dateWithAgo(value: string, ageDays?: number): string {
    if (!value) return '';
    const date = new Date(value.length === 10 ? value + 'T00:00:00Z' : value);
    if (Number.isNaN(date.getTime())) return value;
    const days = typeof ageDays === 'number' ? ageDays
      : Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
    return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} (${days}d ago)`;
  }
  // A reply keeps the thread subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one.
  function todayReplySubject(decision: FundTodayDecision) {
    return /^re:/i.test(decision.subject) ? decision.subject : `Re: ${decision.subject || '(No subject)'}`;
  }
  function todayCardDraft(decision: FundTodayDecision): CardDraft | null {
    const generated = todayCardDrafts[todayKey(decision)];
    if (generated) return generated;
    const ready = decision.ready_draft;
    return ready && ready.source_message_id === decision.message_id
      ? { subject: ready.subject, body: ready.body, generated_at: ready.generated_at } : null;
  }
  function todayCcOptions(contact: FundContact | undefined): string[] {
    if (!contact) return [];
    return allContacts.filter(c => c.counterparty_id !== contact.counterparty_id && !!c.email
        && c.role === 'investor' && !c.automated_address
        && (contact.institution_id ? c.institution_id === contact.institution_id
          : !!contact.suggested_organization_domain && c.suggested_organization_domain === contact.suggested_organization_domain))
      .map(c => c.email as string);
  }
  // Display only: the synced body without the quoted history under "On … wrote:".
  function todayMessageText(body: string) {
    const text = unwrapHardBreaks(repairLostApostrophes(stripCids(body || ''))).trim();
    const quote = text.search(/\n\s*(On [^\n]{0,300}wrote:|-{2,}\s*Original Message\s*-{2,}|From:\s[^\n]+\n\s*Sent:)/i);
    return (quote > 0 ? text.slice(0, quote) : text).trim();
  }
  async function loadTodayInbound(decision: FundTodayDecision) {
    const key = todayKey(decision);
    if (todayInbound[key] && !todayInbound[key].error) return;
    todayInbound = { ...todayInbound, [key]: { text: '', loading: true, error: '' } };
    try {
      const message = await getFundMessage(decision.counterparty_id, decision.message_id, $userEmailStore);
      todayInbound = { ...todayInbound, [key]: { text: todayMessageText(message.body) || '(No message text was synced.)', loading: false, error: '' } };
    } catch (err: any) {
      todayInbound = { ...todayInbound, [key]: { text: '', loading: false, error: err?.message || 'Could not load this message.' } };
    }
  }
  function setTodayBusy(key: string, value: '' | 'generate' | 'polish' | 'send') {
    const next = { ...todayBusy };
    if (value) next[key] = value; else delete next[key];
    todayBusy = next;
  }
  async function generateTodayDraft(decision: FundTodayDecision): Promise<CardDraft | null> {
    const contact = todayContact(decision);
    const key = todayKey(decision);
    if (!todayCanWrite(contact) || todayBusy[key]) return null;
    setTodayBusy(key, 'generate'); todayDraftError = '';
    try {
      const focus = decision.kind === 'respond'
        ? 'Reply to the latest inbound question using only supported context. Keep it concise.'
        : 'Write a short follow-up to the existing conversation. Do not imply a reply is owed.';
      // A Respond card names the message it answers, so the model answers that one rather than the newest synced mail.
      const result = await generateFundOutput(contact.counterparty_id, 'draft', focus, [],
        decision.kind === 'respond' ? decision.message_id : '');
      const parsed = parseEmailDraft(result.text);
      if (!parsed) {
        todayDraftError = `The draft for ${contact.display_name} needs review before it can be sent. Open their Memory to read it.`;
        return null;
      }
      const draft = { ...parsed, generated_at: new Date().toISOString() };
      todayCardDrafts = { ...todayCardDrafts, [key]: draft };
      return draft;
    } catch (err: any) {
      todayDraftError = err?.message || 'Could not prepare the LP draft.';
      return null;
    } finally { setTodayBusy(key, ''); }
  }
  async function polishTodayDraft(decision: FundTodayDecision, mode: 'polish' | 'refine', subject: string, body: string) {
    const contact = todayContact(decision);
    const key = todayKey(decision);
    if (!todayCanWrite(contact) || todayBusy[key]) return null;
    setTodayBusy(key, 'polish'); todayDraftError = '';
    try {
      return await polishFundEmail(contact.counterparty_id, subject, body, mode);
    } catch (err: any) {
      todayDraftError = err?.message || 'Could not rework this draft.';
      return null;
    } finally { setTodayBusy(key, ''); }
  }
  async function sendTodayDraft(decision: FundTodayDecision, outgoing: CardSend): Promise<boolean> {
    const contact = todayContact(decision);
    const key = todayKey(decision);
    if (!todayCanWrite(contact) || todayBusy[key] || !outgoing.subject.trim() || !outgoing.body.trim()) return false;
    const to = contact.email as string;
    if (!confirm(`Send this ${decision.kind === 'respond' ? 'reply' : 'follow-up'} from your connected mailbox to ${to}${outgoing.cc ? `, CC ${outgoing.cc}` : ''}?`)) return false;
    setTodayBusy(key, 'send'); todayDraftError = ''; todaySendNotice = '';
    // One id per card, so a retry after an unclear result cannot send twice.
    const requestId = todayRequestIds[key] ||= crypto.randomUUID();
    try {
      const sent = await submitFundEmail(contact.counterparty_id, {
        request_id: requestId, to, subject: outgoing.subject, body: outgoing.body, cc: outgoing.cc,
        reply_message_id: decision.message_id, today_decision_kind: decision.kind,
        document_ids: [], files: outgoing.files,
      });
      todaySendNotice = sent.today_dismissed === false
        ? `Sent to ${to}, but Today could not hide this suggestion. Check Sent; do not send it again.`
        : `Sent to ${to}. It will appear in this relationship after the next sync.`;
      todayDecisions = todayDecisions.filter(item => todayKey(item) !== key);
      // The server records the dismissal after provider success. Its stable
      // request ID prevents a second dispatch even if dismissal fails.
      return true;
    } catch (err: any) {
      todayDraftError = err?.message || 'Send status is unclear. Check your Sent folder before trying again.';
      return false;
    } finally { setTodayBusy(key, ''); }
  }
  function discussTodayDraft(decision: FundTodayDecision, draft: CardDraft) {
    const contact = todayContact(decision);
    if (!todayCanWrite(contact)) return;
    const respond = decision.kind === 'respond';
    const theirs = (todayInbound[todayKey(decision)]?.text || '').slice(0, 1500);
    todayChatSeed = { counterparty_id: contact.counterparty_id, text: [
      respond
        ? `${decision.display_name} emailed me. Help me decide whether this reply is the right move and how to make it stronger.`
        : `${decision.display_name} has not replied to my email. Help me decide whether this follow-up is the right move and how to make it stronger.`,
      `--- ${respond ? 'Their message' : 'My last email'} ---\nSubject: ${decision.subject || '(No subject)'}${theirs ? `\n\n${theirs}` : ''}`,
      `--- Proposed ${respond ? 'reply' : 'follow-up'} ---\nSubject: ${draft.subject}\n\n${isHtml(draft.body) ? htmlToPlain(draft.body) : draft.body}`,
    ].join('\n\n') };
    openContact(contact);
  }
  async function dismissTodayCard(decision: FundTodayDecision) {
    try {
      await actOnFundTodayDecision(decision, 'dismiss');
      todayDismissed = new Set([...todayDismissed, todayKey(decision)]);
      todayDecisionsError = '';
    } catch (err: any) {
      todayDecisionsError = err?.message || 'Could not update this suggestion.';
      await loadTodayDecisions();
    }
  }
  async function undoTodayDismiss(decision: FundTodayDecision) {
    try {
      await actOnFundTodayDecision(decision, 'restore');
      const next = new Set(todayDismissed);
      next.delete(todayKey(decision));
      todayDismissed = next;
    } catch (err: any) {
      todayDecisionsError = err?.message || 'Could not restore this suggestion.';
    }
  }

  async function prepareTodayBrief(contactId: string) {
    const contact = dashboardLps.find(item => item.counterparty_id === contactId);
    if (!contact || contact.automated_address || todayBriefContactId) return;
    todayBriefContactId = contactId;
    todayBriefError = '';
    try {
      // This click selects one confirmed LP. Optional documents stay opt-in in Memory.
      await generateFundOutput(contactId, 'brief', '', []);
      if (view === 'today') {
        todayTabRequest = { counterparty_id: contact.counterparty_id, tab: 'memory' };
        openContact(contact);
      }
    } catch (err: any) {
      if (view === 'today') todayBriefError = err?.message || 'Could not prepare this LP brief.';
    } finally {
      todayBriefContactId = '';
    }
  }

  async function downloadTodayCallBrief(call: FundTodayCall,
                                         person: FundTodayCall['investors'][number]) {
    const key = `${call.id}:${person.counterparty_id}`;
    if (!person.call_brief || todayCallBriefDownloadKey) return;
    todayCallBriefDownloadKey = key;
    todayCallBriefError = '';
    try {
      await downloadFundCallBriefPdf(call.id, person.counterparty_id);
    } catch (err: any) {
      todayCallBriefError = err?.message || 'Could not download this call brief.';
    } finally {
      todayCallBriefDownloadKey = '';
    }
  }

  function roleLabel(role: DisplayRelationship | null): string {
    return role === 'investor' ? 'LP' : role === 'portfolio' ? 'Portfolio'
      : role === 'neither' ? 'Neither' : role === 'unknown' ? 'Unclear from mail' : 'Unclassified';
  }

  function mailDate(value: string | null | undefined, fallback = '—'): string {
    if (!value) return fallback;
    // Gmail metadata uses epoch milliseconds; Graph and saved evidence use ISO.
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    return Number.isNaN(date.getTime())
      ? fallback
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function mailTime(value: string | null | undefined): number {
    if (!value) return 0;
    const date = /^\d{13}$/.test(value) ? new Date(Number(value)) : parseDay(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  function mostRecentFirst(a: FundContact, b: FundContact): number {
    return mailTime(b.last_seen) - mailTime(a.last_seen)
      || a.display_name.localeCompare(b.display_name);
  }

  async function setRole(contact: FundContact, role: FundRoleChoice, control?: HTMLSelectElement) {
    if (!contact.email || savingRole) return;
    savingRole = contact.counterparty_id;
    actionError = '';
    try {
      await confirmFundRole({ email: contact.email, name: contact.display_name }, role);
      await load();
      // The investor context effect loads after the saved role appears in the
      // workspace. A failed context read must not be reported as a failed save.
    } catch (err: any) {
      // A native select changes before the request starts. If persistence fails,
      // restore the server-backed role so the control does not claim a save.
      if (control) control.value = roleChoice(contact);
      actionError = err?.message || 'Could not save this relationship.';
    } finally {
      savingRole = '';
    }
  }

  function handleOverlayKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && mikeOverlayOpen) { mikeOverlayOpen = false; return; }
  }

  async function saveStage(contact: FundContact, value: string, control: HTMLSelectElement) {
    if (savingStage) return;
    savingStage = true; actionError = '';
    try {
      if (contact.institution_id) await setFundInstitutionStage(contact.institution_id, value || null);
      else await setFundStage(contact.counterparty_id, value || null);
      await load();
    } catch (err: any) {
      control.value = relationshipStage(contact) || '';
      actionError = err?.message || 'Could not save this relationship stage.';
    } finally { savingStage = false; }
  }

  async function saveFollowUp() {
    if (!selected || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      await writeNextStep(selected, nextStepDraft, followUpDateDraft || null);
      followUpEditing = false;
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this next step.';
    } finally { savingFollowUp = false; }
  }

  async function removeEarlierPersonalStep() {
    if (!selected || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      // Scope "contact": the route would otherwise clear the institution's live step.
      await setFundFollowUp(selected.counterparty_id, '', null, 'contact');
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not remove the earlier contact step.';
    } finally { savingFollowUp = false; }
  }

  /* Takes the id so a dashboard row can pin without first opening the contact.
     `pinError` is separate from `actionError`: the detail rail owns that one, and a
     failed pin from the table must not blank a message the rail is showing. */
  async function togglePin(counterpartyId: string | undefined) {
    if (!counterpartyId || savingPin) return;
    savingPin = true; pinError = '';
    try {
      if (isPinned(counterpartyId)) await unpinFundContact(counterpartyId);
      else await pinFundContact(counterpartyId);
      await refreshSidebarLps();
    } catch (err: any) {
      pinError = err?.message || 'Could not update this pin.';
    } finally { savingPin = false; }
  }

  async function toggleOrganizationPin(key: string) {
    if (savingPin) return;
    savingPin = true; pinError = '';
    try {
      if (isOrganizationPinned(key)) await unpinFundOrganization(key);
      else await pinFundOrganization(key);
      await refreshSidebarLps();
    } catch (err: any) {
      pinError = err?.message || 'Could not update this organization pin.';
    } finally { savingPin = false; }
  }

  function openOrganization(key: string) {
    const organization = groupFundContacts(allContacts).find(group => group.key === key);
    const contact = organization?.contacts.find(member => member.role === 'investor')
      || organization?.contacts.find(member => member.suggested_role === 'investor')
      || organization?.contacts[0];
    if (!contact) return;
    openContact(contact);
  }

  function openOrganizationFollowUp() {
    if (!selectedOrganizationRecord) return;
    nextStepDraft = selectedOrganizationRecord.next_step || '';
    followUpDateDraft = selectedOrganizationRecord.follow_up_on || '';
    followUpEditing = true;
  }

  async function saveOrganizationFollowUp() {
    if (!selectedOrganizationRecord || savingFollowUp) return;
    savingFollowUp = true; actionError = '';
    try {
      await setFundInstitutionFollowUp(selectedOrganizationRecord.institution_id,
        nextStepDraft, followUpDateDraft || null);
      followUpEditing = false;
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this institution next step.';
    } finally { savingFollowUp = false; }
  }

  async function addContact() {
    if (savingRole) return;
    const email = newEmail.trim();
    if (!email) {
      actionError = 'Enter a contact email address.';
      return;
    }
    savingRole = email;
    actionError = '';
    try {
      await confirmFundRole({ email, name: newName.trim() }, newRole);
      await load();
      newEmail = '';
      newName = '';
      addOpen = false;
    } catch (err: any) {
      actionError = err?.message || 'Could not add this contact.';
    } finally {
      savingRole = '';
    }
  }

  async function generate(kind: 'draft') {
    if (!selected || generating) return;
    const id = selected.counterparty_id;
    const composerId = composeRequestId;
    const replyMessageId = composeReplyMessageId;
    generating = kind;
    actionError = '';
    try {
      const result = await generateFundOutput(id, kind,
        draftInstruction, groundingDocumentIds, replyMessageId);
      if (contactId !== id || !composeOpen || composeRequestId !== composerId) return;
      const context = await getFundContext(id);
      if (contactId !== id || !composeOpen || composeRequestId !== composerId) return;
      contactContext = context;
      useDraftResult(result.text, replyMessageId);
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || `Could not generate the ${kind}.`;
    } finally {
      generating = '';
    }
  }

  function parseEmailDraft(text: string): { subject: string; body: string } | null {
    const cleaned = text.trim().replace(/^```(?:text|email)?\s*\n/i, '').replace(/\n```\s*$/, '');
    const match = cleaned.match(/^Subject:\s*([^\r\n]+)\r?\n(?:\r?\n)?([\s\S]+)$/i);
    if (!match || !match[1].trim() || !match[2].trim()) return null;
    return { subject: match[1].trim(), body: match[2].trim() };
  }

  function useDraftResult(text: string, replyMessageId = '') {
    const parsed = parseEmailDraft(text);
    if (!parsed) {
      actionError = 'The generated response did not contain a sendable Subject and body. Read it below; start a new email to write one yourself.';
      detailTab = 'memory';
      generatedOpen = true;
      return;
    }
    // A reply keeps the thread subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one.
    if (!replyMessageId) composeSubject = parsed.subject;
    composeBody = parsed.body;
    composeReplyMessageId = replyMessageId;
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeRequestId = crypto.randomUUID();
    composeOpen = true;
    detailTab = 'emails';
  }

  function startFundEmail() {
    if (composeOpen && (composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and start a new one?')) return;
    composeSubject = ''; composeBody = '';
    composeCc = ''; composeReplyMessageId = '';
    draftInstruction = ''; groundingDocumentIds = [];
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeTracking = false; composeMeeting = false;
    meetingSummary = ''; meetingLocation = ''; meetingStart = ''; meetingEnd = '';
    composeRequestId = crypto.randomUUID();
    composeOpen = true; sendNotice = ''; actionError = ''; polishChanges = '';
    // The composer lives in the Emails tab, so opening one goes there. It used to
    // ride on Emails being the landing tab; since Briefing became the default,
    // every caller would otherwise have to remember to switch — and the Today
    // hand-off paths did not.
    detailTab = 'emails';
  }

  function closeFundEmail() {
    if (composeSubject.trim() || composeBody.trim()) {
      if (!confirm('Discard this unfinished email?')) return;
    }
    composeOpen = false;
    draftInstruction = ''; groundingDocumentIds = [];
  }

  function generateComposeDraft() {
    // Replying pre-fills the locked `Re:` subject, so only the body counts as written.
    const written = composeReplyMessageId ? composeBody.trim() : (composeSubject.trim() || composeBody.trim());
    if (written && !confirm(composeReplyMessageId
      ? 'Replace the reply you have written with a new AI draft?'
      : 'Replace the current subject and message with a new AI draft?')) return;
    generate('draft');
  }

  function startReply(message: { message_id: string; subject: string; owner_email: string }): boolean {
    if (message.owner_email.toLowerCase() !== $userEmailStore.toLowerCase()) return false;
    if (composeOpen && (composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and reply in this thread?')) return false;
    composeReplyMessageId = message.message_id;
    composeSubject = /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject || '(No subject)'}`;
    composeBody = ''; composeCc = '';
    composeFiles = []; composeDocumentIds = []; scheduleOpen = false;
    composeTracking = false; composeMeeting = false;
    meetingSummary = ''; meetingLocation = ''; meetingStart = ''; meetingEnd = '';
    composeRequestId = crypto.randomUUID();
    composeOpen = true; actionError = ''; sendNotice = ''; polishChanges = '';
    detailTab = 'emails';   // same reason as startFundEmail()
    return true;
  }

  function draftReply(message: { message_id: string; subject: string; owner_email: string }) {
    if (!canGenerateDraft || generating || !startReply(message)) return;
    generateComposeDraft();
  }

  function chooseNewEmailRecipient(id: string): boolean {
    const person = institutionBriefMembers.find(member => member.counterparty_id === id);
    if (!person || person.counterparty_id === selected?.counterparty_id) return false;
    if ((composeSubject.trim() || composeBody.trim())
        && !confirm('Discard the unfinished email and write to another contact?')) return false;
    todayComposeRequest = { contactId: person.counterparty_id, subject: '', body: '' };
    openContact(person);
    return true;
  }

  async function editFundEmail(mode: 'polish' | 'refine') {
    if (!selected || polishing || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    polishing = true; actionError = '';
    try {
      const result = await polishFundEmail(id, composeSubject, composeBody, mode);
      if (contactId === id) {
        composeSubject = result.subject; composeBody = result.body;
        polishChanges = result.changes || 'Edited draft. Review it before sending.';
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not edit this email.';
    } finally { polishing = false; }
  }

  async function saveReviewDraftInDealton() {
    if (!canUseAs() || !selected || savingReviewDraft || !composeSubject.trim() || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    savingReviewDraft = true;
    actionError = '';
    try {
      await saveFundReviewDraft(id, composeSubject, composeBody);
      if (contactId === id) {
        sendNotice = 'Review-only draft saved in Dealton. Nothing was saved to the mailbox.';
        await reloadContext(id);
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not save the review-only draft.';
    } finally { savingReviewDraft = false; }
  }

  async function saveEmailToDrafts() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()) return;
    if (composeMeeting || composeTracking) {
      actionError = 'Mailbox Drafts currently save the message and attachments only. Turn off invite and tracking, or send/schedule this email.';
      return;
    }
    const id = selected.counterparty_id;
    sending = true; actionError = '';
    try {
      const saved = await saveFundProviderDraft(id, {
        request_id: composeRequestId || crypto.randomUUID(), to: selected.email,
        subject: composeSubject, body: composeBody, cc: composeCc,
        reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles,
      });
      if (contactId === id) {
        sendNotice = `Saved in your ${saved.provider === 'microsoft' ? 'Outlook' : 'Gmail'} Drafts. It has not been sent.`;
        composeOpen = false; composeRequestId = ''; composeFiles = []; composeDocumentIds = [];
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Check provider Drafts before trying again.';
    } finally { sending = false; }
  }

  function checkedMeeting(): { calendar_start: string; calendar_end: string; calendar_summary: string; calendar_location: string } | null {
    if (!composeMeeting) return { calendar_start: '', calendar_end: '', calendar_summary: '', calendar_location: '' };
    const start = new Date(meetingStart);
    const end = new Date(meetingEnd);
    if (!meetingSummary.trim() || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
        || start.getTime() <= Date.now() || end.getTime() <= start.getTime()) {
      actionError = 'Enter a meeting title and valid future start and end times.';
      return null;
    }
    return {
      calendar_start: start.toISOString(), calendar_end: end.toISOString(),
      calendar_summary: meetingSummary.trim(), calendar_location: meetingLocation.trim(),
    };
  }

  async function saveInstitution(institutionId: string | null) {
    if (!selected || savingInstitution) return;
    savingInstitution = true; actionError = '';
    try {
      await assignFundInstitution(selected.counterparty_id, institutionId);
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not save this institution.';
    } finally { savingInstitution = false; }
  }

  async function createAndAssignInstitution() {
    if (!selected || savingInstitution || !newInstitutionName.trim()) return;
    savingInstitution = true; actionError = '';
    try {
      const result = await createFundInstitution(newInstitutionName.trim());
      await assignFundInstitution(selected.counterparty_id, result.institution_id);
      newInstitutionName = '';
      await load();
    } catch (err: any) {
      actionError = err?.message || 'Could not create this institution.';
    } finally { savingInstitution = false; }
  }

  function startNote(note?: { note_id: string; title: string; body: string }) {
    noteEditId = note?.note_id || '';
    noteTitle = note?.title || '';
    noteBody = note?.body || '';
    noteEditorOpen = true;
    actionError = '';
  }

  async function saveNote() {
    if (!selected || selected.role === 'neither' || noteSaving || !noteTitle.trim() || !noteBody.trim()) return;
    const id = selected.counterparty_id;
    noteSaving = true; actionError = '';
    try {
      if (noteEditId) await editFundNote(id, noteEditId, noteTitle, noteBody);
      else await addFundNote(id, noteTitle, noteBody);
      if (contactId === id) {
        noteEditorOpen = false; noteEditId = ''; noteTitle = ''; noteBody = '';
        await reloadContext(id);
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not save this note.';
    } finally { noteSaving = false; }
  }

  async function toggleMessage(personId: string, owner: string, messageId: string) {
    if (!selected) return;
    const id = selected.counterparty_id;
    if (personId !== id && !institutionBriefMembers.some(member => member.counterparty_id === personId)) return;
    const key = `${personId}:${owner}:${messageId}`;
    if (openedMessages.has(key) && !messageErrors[key]) {
      const next = new Set(openedMessages);
      next.delete(key);
      openedMessages = next;
      return;
    }
    openedMessages = new Set(openedMessages).add(key);
    if (messageBodies[key]) return;
    try {
      const result = await getFundMessage(personId, messageId, owner);
      if (contactId !== id) return;
      messageBodies = { ...messageBodies, [key]: result.body || '(No message body)' };
      messageRecipients = { ...messageRecipients, [key]: ([['To', result.to], ['Cc', result.cc], ['Bcc', result.bcc]] as const)
        .filter(([, people]) => people?.length).map(([label, people]) => ({ label, people: people! })) };
      messageErrors = { ...messageErrors, [key]: '' };
    } catch (err: any) {
      if (contactId !== id) return;
      messageErrors = { ...messageErrors, [key]: err?.message || 'Could not load this email.' };
    }
  }

  async function refreshOlderThreads() {
    if (!selected || refreshingThreads) return;
    const id = selected.counterparty_id;
    refreshingThreads = true; actionError = '';
    try {
      const result = await refreshFundThreads(id);
      if (contactId !== id) return;
      await reloadContext(id);
      if (selected.institution_id) {
        const activity = await getFundInstitutionActivity(selected.institution_id);
        if (contactId === id) institutionActivity = activity.messages;
      }
      if (contactId === id && result.updated) {
        expandedEmailThreads = new Set(); openedMessages = new Set();
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not refresh mailbox thread metadata.';
    } finally { refreshingThreads = false; }
  }

  async function sendReviewedEmail() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()) return;
    const id = selected.counterparty_id;
    const to = selected.email;
    const calendar = checkedMeeting();
    if (!calendar) return;
    if (!confirm(`Send this ${composeReplyMessageId ? 'reply' : 'email'} from your connected mailbox to ${to}${composeCc.trim() ? `, CC ${composeCc.trim()}` : ''}${composeMeeting ? ', with a calendar invite' : ''}${composeTracking ? ', with open tracking' : ''} now?`)) return;
    sending = true; actionError = ''; sendNotice = '';
    const requestId = composeRequestId || crypto.randomUUID();
    composeRequestId = requestId;
    try {
      await submitFundEmail(id, {
        request_id: requestId, to, subject: composeSubject, body: composeBody,
        cc: composeCc, reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles,
        tracking: composeTracking, ...calendar,
      });
      if (contactId === id) {
        sendNotice = `Sent to ${to}. It will appear in your provider Sent folder immediately and in this relationship after the next sync.`;
        composeOpen = false; composeSubject = ''; composeBody = ''; composeCc = ''; composeReplyMessageId = ''; composeRequestId = '';
        composeFiles = []; composeDocumentIds = [];
        composeTracking = false; composeMeeting = false;
      }
      getFundSent(id).then(data => {
        if (contactId === id) sentReceipts = data.messages;
      }).catch(() => { /* The successful provider response is the source of truth. */ });
      getFundEmailTracking(id).then(data => {
        if (contactId === id) emailTracking = data.messages;
      }).catch(() => {});
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Send status is unclear. Check your Sent folder before trying again.';
    } finally { sending = false; }
  }

  async function scheduleReviewedEmail() {
    if (!selected?.email || sending || !composeSubject.trim() || !composeBody.trim()
        || !scheduleDate || !scheduleTime) return;
    const when = new Date(`${scheduleDate}T${scheduleTime}`);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now() + 60000) {
      actionError = 'Choose a time at least one minute in the future.';
      return;
    }
    const calendar = checkedMeeting();
    if (!calendar) return;
    const id = selected.counterparty_id;
    if (!confirm(`Schedule this email to ${selected.email}${composeCc.trim() ? `, CC ${composeCc.trim()}` : ''} for ${when.toLocaleString()}? It will send automatically at that time unless cancelled.`)) return;
    sending = true; actionError = '';
    const requestId = composeRequestId || crypto.randomUUID();
    composeRequestId = requestId;
    try {
      await submitFundEmail(id, {
        request_id: requestId, to: selected.email, subject: composeSubject,
        body: composeBody, cc: composeCc, reply_message_id: composeReplyMessageId,
        document_ids: composeDocumentIds, files: composeFiles, send_at: when.toISOString(),
        tracking: composeTracking, ...calendar,
      });
      if (contactId === id) {
        sendNotice = `Scheduled for ${when.toLocaleString()}. You can cancel it below before dispatch.`;
        composeOpen = false; scheduleOpen = false;
        composeSubject = ''; composeBody = ''; composeCc = ''; composeReplyMessageId = '';
        composeRequestId = ''; composeFiles = []; composeDocumentIds = [];
        composeTracking = false; composeMeeting = false;
        const data = await getFundScheduled(id);
        if (contactId === id) scheduledSends = data.messages;
      }
    } catch (err: any) {
      if (contactId === id) actionError = err?.message || 'Could not schedule this email.';
    } finally { sending = false; }
  }

  async function cancelScheduled(requestId: string) {
    if (!selected || !confirm('Cancel this scheduled email before it sends?')) return;
    const id = selected.counterparty_id;
    try {
      await cancelFundScheduled(requestId);
      const data = await getFundScheduled(id);
      if (contactId === id) scheduledSends = data.messages;
    } catch (err: any) {
      actionError = err?.message || 'Could not cancel this scheduled email.';
    }
  }

  async function cancelFromSchedule(requestId: string) {
    if (!confirm('Cancel this scheduled email before it sends?')) return;
    try {
      await cancelFundScheduled(requestId);
      if (selectedSchedule?.request_id === requestId) selectedSchedule = null;
      await loadAllScheduled();
    } catch (err: any) {
      scheduledError = err?.message || 'Could not cancel this scheduled email.';
    }
  }

  async function openSchedule(requestId: string) {
    scheduledError = '';
    try {
      const detail = await getFundScheduledDetail(requestId);
      selectedSchedule = detail;
      scheduleSubject = detail.subject;
      scheduleBody = detail.body;
      scheduleCc = detail.cc || '';
      const local = new Date(detail.send_at);
      scheduleEditDate = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
      scheduleEditTime = `${String(local.getHours()).padStart(2, '0')}:${String(local.getMinutes()).padStart(2, '0')}`;
    } catch (err: any) {
      scheduledError = err?.message || 'Could not open this scheduled email.';
    }
  }

  async function saveScheduleEdit() {
    if (!selectedSchedule || scheduleSaving) return;
    const when = new Date(`${scheduleEditDate}T${scheduleEditTime}`);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now() + 60000) {
      scheduledError = 'Choose a time at least one minute in the future.';
      return;
    }
    scheduleSaving = true; scheduledError = '';
    try {
      await editFundScheduled(selectedSchedule.request_id, {
        subject: scheduleSubject, body: scheduleBody, cc: scheduleCc,
        send_at: when.toISOString(),
      });
      const id = selectedSchedule.request_id;
      await loadAllScheduled();
      await openSchedule(id);
    } catch (err: any) {
      scheduledError = err?.message || 'Could not save this scheduled email.';
    } finally { scheduleSaving = false; }
  }

</script>

<svelte:window onclick={onWindowClick} onkeydown={handleOverlayKey} />

{#if loading && !workspace}
  <div class="empty-state" role="status">Loading Investor Relations…</div>
{:else if workspace}
  <!-- A failed REFRESH keeps the page. `load()` runs again on every poll version
       bump, and ordering the error branch ahead of this one replaced a working
       workspace with an error card — the Sales Today shows a retry strip over
       the data instead. Only a first load with nothing to show falls through. -->
  {#if error}
    <div class="empty-state fund-reload-alert" role="status">
      Could not refresh this workspace, so you are seeing the last loaded data. {error}
      <button class="today-mini-btn" type="button" onclick={load}>Retry</button>
    </div>
  {/if}
  {#if view === 'today'}
    <div class="today-topbar"><SyncButton /></div>
    <TodayHero firstName={userName.split(' ')[0] || 'there'} agentName={agent}
      status={todayStatus} />
    {#if todayDecisionsError}<div class="fund-reload-alert" role="status">Mailbox suggestions are unavailable: {todayDecisionsError} <button class="today-mini-btn" onclick={loadTodayDecisions}>Retry</button></div>{/if}
    {#if scheduledError && !pendingSends.length}<div class="fund-reload-alert" role="status">Pending sends are unavailable: {scheduledError} <button class="today-mini-btn" onclick={loadAllScheduled}>Retry</button></div>{/if}
    {#if todayDraftError}<div class="fund-reload-alert" role="alert">{todayDraftError}</div>{/if}
    {#if todaySendNotice}<div class="fund-send-notice" role="status">{todaySendNotice}</div>{/if}
    {#if todayBriefError}<div class="fund-reload-alert" role="alert">{todayBriefError}</div>{/if}
    <TodayNav active={spy.active} onJump={jumpToSection} items={todayNavItems} />
    <section class="today-section" id="fund-briefing" use:spyOn>
      <header class="today-section-header"><h2>Today briefing</h2><span class="today-section-count">
        {#if !todayBriefsLoaded}
          Checking…
        {:else if todayBriefs.length}
          {todayBriefs.length} event briefing{todayBriefs.length === 1 ? '' : 's'}
        {:else}
          No briefing available
        {/if}
      </span></header>
      {#if todayBriefsError}<p class="fund-alert" role="status">Today notes are unavailable: {todayBriefsError} <button class="today-mini-btn" onclick={loadTodayBriefs}>Retry</button></p>{/if}
      {#if !todayBriefsLoaded}
        <div class="today-empty" role="status">Checking AI Today event briefings…</div>
      {:else if todayBriefs.length}
        {#each (todayBriefsExpanded ? todayBriefs : todayBriefs.slice(0, 5)) as brief, i (brief.entity_key)}
          <div class="fund-briefing-tile tone-{todayBriefTone(brief)}" class:open={!!todayExpanded[`note:${brief.entity_key}`]}
            class:fund-today-note-unread={!brief.read}
            role="button" tabindex="0" aria-expanded={!!todayExpanded[`note:${brief.entity_key}`]}
            onclick={(event) => { if (!(event.target as HTMLElement).closest('.fund-briefing-detail, button')) void toggleTodayBrief(brief); }}
            onkeydown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); void toggleTodayBrief(brief); } }}>
            <span class="fund-briefing-num">{i + 1}</span>
            <div class="fund-briefing-body">
              <div class="fund-briefing-top">{#if !brief.read}<span class="fund-briefing-dot" aria-label="unread"></span>{/if}<strong>{todayBriefName(brief)}</strong><span class="fund-briefing-pill">{TODAY_BRIEF_TAG[brief.signal_type]}</span><span class="fund-briefing-chevron" aria-hidden="true">›</span></div>
              <p class="fund-briefing-preview">{brief.headline} · {todayBriefPreview(brief)}</p>
              {#if todayExpanded[`note:${brief.entity_key}`]}
                <p class="fund-briefing-source">Today event briefing · updated {emailListDate(brief.updated_at)}</p>
                <div class="fund-briefing-detail" role="group" aria-label="Today event briefing for {todayBriefName(brief)}">
                  <div class="md-content">{@html renderSafeMarkdown(brief.content)}</div>
                  {#if brief.source_manifest.sources.length}
                    <details class="fund-today-note-sources"><summary>Sources used</summary>
                      <ul>{#each brief.source_manifest.sources as source (source.id)}<li>[{source.id}] {source.label}</li>{/each}</ul>
                    </details>
                  {/if}
                  <div class="fund-today-workbench-actions fund-briefing-actions">
                    <button type="button" class="fund-briefing-act" onclick={() => actOnTodayBrief(brief)}>{TODAY_BRIEF_MOVE[brief.signal_type].verb}</button>
                    {#if !isSupportView()}<button type="button" class="today-mini-btn ghost" onclick={() => dismissTodayBrief(brief)}>Dismiss</button>{/if}
                  </div>
                </div>
              {/if}
            </div>
            <button type="button" class="fund-briefing-verb" onclick={(event) => { event.stopPropagation(); actOnTodayBrief(brief); }}>{TODAY_BRIEF_MOVE[brief.signal_type].verb}</button>
          </div>
        {/each}
        {#if todayBriefs.length > 5}
          <button type="button" class="today-mini-btn ghost today-expand-btn" onclick={() => todayBriefsExpanded = !todayBriefsExpanded}>
            {todayBriefsExpanded ? 'Show fewer notes ↑' : `Show ${todayBriefs.length - 5} more Today notes ↓`}
          </button>
        {/if}
      {:else}
        <!-- The three onboarding states used to live in the "Next steps" section, which
             the spec folds into Chase. They land here instead: Briefing is the first
             section, and an account with no inbox has nothing to chase. -->
        <div class="today-empty">
          {#if !workspace.inbox_connected}
            Connect a Google or Microsoft mailbox to bring real relationships into Today.
            {#if !isSupportView()}<a class="today-mini-btn" href={workspace.provider === 'microsoft' ? '/api/auth/microsoft/login' : '/api/auth/login?reauth=true'}>Connect inbox →</a>{/if}
          {:else if !allContacts.length}
            {$pollingActive ? 'Your relationships will appear here as the mailbox sync progresses.' : 'Your inbox is connected. Sync to discover external contacts and their email history.'}
            <SyncButton />
          {:else if !dashboardLps.length}
            {allContacts.length} external contact{allContacts.length === 1 ? '' : 's'} discovered, no LP relationship filed yet.
          <button class="today-mini-btn fund-navlink" onclick={() => $currentView = 'contacts'}>Browse contacts →</button>
          {:else}
            No AI event briefing is available today. Calls, Respond, Chase, and Possible LPs remain below.
          {/if}
        </div>
      {/if}
    </section>
    {#if pendingSends.length}
      <section class="today-section" id="fund-sends" use:spyOn>
        <header class="today-section-header"><h2>Going out soon</h2><span class="today-section-count">{pendingSends.length} pending</span></header>
        {#if scheduledError}<p class="fund-alert" role="alert">{scheduledError}</p>{/if}
        {#each pendingSends.slice(0, 5) as send (send.request_id)}
          <div class="fund-send-row"><time datetime={send.send_at}>{new Date(send.send_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time><div><strong>{send.subject}</strong><small>To {send.recipient}</small></div><button class="today-mini-btn fund-navlink" onclick={() => { $currentView = 'scheduled'; openSchedule(send.request_id); }}>Review →</button><button class="today-mini-btn ghost" onclick={() => cancelFromSchedule(send.request_id)}>Cancel</button></div>
        {/each}
        {#if pendingSends.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => $currentView = 'scheduled'}>See all {pendingSends.length} scheduled emails →</button>{/if}
      </section>
    {/if}
    <section class="today-section" id="fund-calls" use:spyOn>
      <header class="today-section-header"><h2>Calls in the next 3 days</h2><span class="today-section-count">{todayCalls.length} scheduled</span></header>
      {#if todayCallBriefError}<p class="fund-alert" role="alert">{todayCallBriefError}</p>{/if}
      {#if todayCallsError}
        <div class="today-empty" role="status">LP calendar calls are unavailable. {todayCallsError} <button class="today-mini-btn" onclick={loadTodayCalls}>Retry</button></div>
      {:else if todayCallsLoading && !todayCalls.length}
        <div class="today-empty">Checking your calendar…</div>
      {:else if todayCalls.length}
        {#each (todayExpanded.calls ? todayCalls : todayCalls.slice(0, 3)) as call (call.id)}
          {@const many = call.investors.length > 1}
          {@const who = [...new Set(call.investors.map(person => { const contact = allContacts.find(item => item.counterparty_id === person.counterparty_id); return contact ? (relationshipInstitution(contact)?.name || organizationHint(contact)?.name || person.display_name) : person.display_name; }))]
            .filter(label => !call.summary.toLowerCase().includes(label.toLowerCase())).join(', ')}
          <article class="fund-call-card">
            <time datetime={call.start}>{new Date(call.start).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time>
            <div class="fund-call-main">
              <h3 title={call.summary}>{call.summary}</h3>
              {#if who}<p>{who}</p>{/if}
            </div>
            <div class="fund-today-actions">
              {#each call.investors as person (person.counterparty_id)}
                {@const contact = allContacts.find(item => item.counterparty_id === person.counterparty_id)}
                {@const suffix = many ? ` · ${person.display_name}` : ''}
                {#if person.call_brief}
                  <button class="today-mini-btn fund-call-dl" disabled={!!todayCallBriefDownloadKey} aria-label="Download brief for {person.display_name}"
                    onclick={() => downloadTodayCallBrief(call, person)}>
                    {#if todayCallBriefDownloadKey === `${call.id}:${person.counterparty_id}`}Downloading…{:else}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>Download brief{suffix}{/if}
                  </button>
                {:else if person.call_brief_status}
                  <span class="fund-call-status">{person.call_brief_status === 'generating' ? 'Preparing brief…'
                    : person.call_brief_status === 'failed' ? 'Brief will retry' : 'Brief hidden in support view'}{suffix}</span>
                {:else}
                  <button class="today-mini-btn ghost" disabled={!!todayBriefContactId} aria-label="Prepare LP brief for {person.display_name}"
                    onclick={() => prepareTodayBrief(person.counterparty_id)}>
                    {todayBriefContactId === person.counterparty_id ? 'Preparing…' : `Prepare LP brief${suffix} →`}
                  </button>
                {/if}
                {#if contact}<button class="today-mini-btn fund-navlink" onclick={() => openContact(contact)}>Open {many ? person.display_name : 'LP'} →</button>{/if}
              {/each}
            </div>
          </article>
        {/each}
        {#if todayCalls.length > 3}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.calls = !todayExpanded.calls}>{todayExpanded.calls ? 'Show fewer ↑' : `Show ${todayCalls.length - 3} more ↓`}</button>{/if}
      {:else}<div class="today-empty">No calls with LPs on your calendar in the next 3 days.</div>{/if}
    </section>
    <!-- No "Next steps" section: a recorded follow-up that is DUE is a Chase row, which
         is how sales Today does it. See the Chase section below. -->
    <section class="today-section" id="fund-respond" use:spyOn>
      <header class="today-section-header"><h2>Respond</h2>{#if respondCountLabel}<span class="today-section-count">{respondCountLabel}</span>{/if}</header>
      <!-- Sales Respond card (lib/FundDraftCard.svelte); only the draft prompt is Fund's. -->
      {#if respondDecisions.length}
        {#each respondDecisions as decision, i (todayKey(decision))}
          {@const key = todayKey(decision)}
          {@const contact = todayContact(decision)}
          {@const writable = todayCanWrite(contact)}
          <FundDraftCard n={i + 1} name={todayCardName(contact, decision.display_name)} stage={todayCardStage(contact)}
            severity={todayCardDraft(decision) ? '' : decision.age_days > 7 ? 'red' : 'amber'}
            subtitle={`${decision.display_name} emailed · respond now`}
            detail={`Received ${dateWithAgo(decision.sent_at, decision.age_days)} · “${decision.subject || '(No subject)'}”`}
            inbound={{ label: 'They wrote', ...(todayInbound[key] || { text: '', loading: true, error: '' }) }}
            onExpand={() => loadTodayInbound(decision)}
            draft={todayCardDraft(decision)} to={contact?.email || ''} ccOptions={todayCcOptions(contact)}
            replySubject={todayReplySubject(decision)} busy={todayBusy[key] || ''}
            emptyNote="No draft yet. Draft a reply from this conversation, or open the LP to write one."
            draftLabel="Draft reply" dismissed={todayDismissed.has(key)}
            extraActions={writable ? [{ label: 'Reply in thread →', onclick: () => openTodayReply(decision) }]
              : contact ? [{ label: 'Open LP →', onclick: () => openContact(contact) }] : []}
            onDismiss={() => dismissTodayCard(decision)} onUndo={() => undoTodayDismiss(decision)}
            onGenerate={writable ? () => generateTodayDraft(decision) : undefined}
            onPolish={writable ? (mode, subject, body) => polishTodayDraft(decision, mode, subject, body) : undefined}
            onSend={writable && !isSupportView() ? (outgoing) => sendTodayDraft(decision, outgoing) : undefined}
            onDiscuss={writable ? (draft) => discussTodayDraft(decision, draft) : undefined} />
        {/each}
      {:else if !todayDecisionsLoaded}<div class="today-empty" role="status">Checking synced LP mail…</div>
      {:else if !todayDecisionsError}<div class="today-empty">No LP email is waiting on a reply.</div>{/if}
    </section>
    <section class="today-section" id="fund-chase" use:spyOn>
      <header class="today-section-header"><h2>Chase</h2>{#if chaseCountLabel}<span class="today-section-count">{chaseCountLabel}</span>{/if}</header>
      {#if chaseError}<p class="fund-alert" role="alert">{chaseError}</p>{/if}
      {#if chaseItems.length}
        {#each (todayExpanded.chase ? chaseItems : chaseItems.slice(0, 5)) as item, i (item.key)}
          {@const contact = item.contact}
          {@const decision = item.decision}
          {#if decision}
            {@const key = todayKey(decision)}
            {@const writable = todayCanWrite(contact)}
            <FundDraftCard n={i + 1} name={todayCardName(contact, decision.display_name)} stage={todayCardStage(contact)}
              severity={decision.age_days >= 14 ? 'red' : 'amber'}
              subtitle={`${decision.display_name} has not replied`}
              detail={`You wrote “${decision.subject || '(No subject)'}” ${dateWithAgo(decision.sent_at, decision.age_days)}`}
              inbound={{ label: 'You last wrote', ...(todayInbound[key] || { text: '', loading: true, error: '' }) }}
              onExpand={() => loadTodayInbound(decision)}
              draft={todayCardDraft(decision)} to={contact?.email || ''} ccOptions={todayCcOptions(contact)}
              replySubject={todayReplySubject(decision)} busy={todayBusy[key] || ''}
              emptyNote="No follow-up drafted yet. Draft one from this conversation, add a next step, or open the LP."
              draftLabel="Draft follow-up" dismissed={todayDismissed.has(key)}
              extraActions={[
                ...(contact && canAddNextStep(contact) ? [{ label: 'Add next step', onclick: () => openFollowUp(contact) }] : []),
                ...(contact ? [{ label: 'Open LP →', onclick: () => openContact(contact) }] : []),
              ]}
              onSnooze={() => actOnTodayDecision(decision, 'snooze')}
              onDismiss={() => dismissTodayCard(decision)} onUndo={() => undoTodayDismiss(decision)}
              onGenerate={writable ? () => generateTodayDraft(decision) : undefined}
              onPolish={writable ? (mode, subject, body) => polishTodayDraft(decision, mode, subject, body) : undefined}
              onSend={writable && !isSupportView() ? (outgoing) => sendTodayDraft(decision, outgoing) : undefined}
              onDiscuss={writable ? (draft) => discussTodayDraft(decision, draft) : undefined} />
          {:else if contact}
            {@const due = relationshipDue(contact)}
            <FundDraftCard n={i + 1} name={todayCardName(contact, contact.display_name)} stage={todayCardStage(contact)}
              severity={statusPill(contact)?.label === 'Overdue' ? 'red' : 'amber'}
              subtitle={`Next step · ${relationshipNextStep(contact)}`}
              detail={due ? `Due ${dateWithAgo(due)}` : 'No due date'}
              emptyNote={`${nextStepAuthor(relationshipInstitution(contact) || contact)}. Edit the step, prepare a brief, or open the LP.`}
              dismissLabel="Done" locked={!!chaseSaving}
              extraActions={[
                { label: 'Edit next step', onclick: () => openFollowUp(contact) },
                { label: todayBriefContactId === contact.counterparty_id ? 'Preparing…' : 'Prepare brief',
                  onclick: () => prepareTodayBrief(contact.counterparty_id), disabled: !!todayBriefContactId },
                { label: 'Open LP →', onclick: () => openContact(contact) },
              ]}
              onSnooze={() => settleDueNextStep(contact, 'snooze')}
              onDismiss={() => settleDueNextStep(contact, 'done')} />
          {/if}
        {/each}
        {#if chaseItems.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.chase = !todayExpanded.chase}>{todayExpanded.chase ? 'Show fewer ↑' : `Show ${chaseItems.length - 5} more ↓`}</button>{/if}
      {:else if !todayDecisionsLoaded}<div class="today-empty" role="status">Checking synced LP mail…</div>
      {:else if !todayDecisionsError}<div class="today-empty">Nothing to chase.</div>{/if}
    </section>
    <section class="today-section" id="fund-new-lps" use:spyOn>
      <header class="today-section-header"><h2>Possible LPs</h2><span class="today-section-count">{newLpGroups.length} with an investment signal</span></header>
      {#if newLpGroups.length}
        {#each (todayExpanded.newLps ? newLpGroups : newLpGroups.slice(0, 5)) as group, i (group.key)}
          <details class="fund-today-tile"><summary><span class="fund-today-num">{i + 1}</span><span class="fund-today-tile-main"><strong>{group.name}</strong>{#if group.source}<small>{group.source}</small>{/if}<span>{group.contacts.length} LP contact{group.contacts.length === 1 ? '' : 's'} in an early stage</span></span><span class="fund-today-tile-count">Potential</span><span class="fund-today-chevron" aria-hidden="true">›</span></summary>
            <div class="fund-today-detail">{#each group.contacts as contact (contact.counterparty_id)}<div class="fund-today-person"><div><strong>{contact.display_name}</strong><p>{contact.suggested_reason || 'Classified from direct mail'}</p></div><div class="fund-today-actions"><button class="today-mini-btn fund-navlink" onclick={() => openContact(contact)}>Open LP →</button></div></div>{/each}</div>
          </details>
        {/each}
        {#if newLpGroups.length > 5}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.newLps = !todayExpanded.newLps}>{todayExpanded.newLps ? 'Show fewer ↑' : `Show ${newLpGroups.length - 5} more ↓`}</button>{/if}
        <div class="fund-plist-foot"><button class="today-mini-btn fund-navlink" onclick={() => { dashTab = 'ir'; $currentView = 'dashboard'; }}>View LPs →</button></div>
      {:else}<div class="today-empty">No new early-stage LPs from synced mail.</div>{/if}
    </section>
    <section class="today-section" id="fund-quiet" use:spyOn>
      <header class="today-section-header"><h2>Gone cold</h2>{#if quietGroups.length}<span class="today-section-count">{quietGroups.length} quiet for 30+ days</span>{/if}</header>
      {#if quietGroups.length}
        {#each (todayExpanded.quiet ? quietGroups : quietGroups.slice(0, 3)) as group, i (group.key)}
          {@const latest = groupLatestMail(group.contacts)}
          <details class="fund-today-tile"><summary><span class="fund-today-num">{i + 1}</span><span class="fund-today-tile-main"><strong>{group.name}</strong>{#if group.source}<small>{group.source}</small>{/if}<span>Last email {emailListDate(latest?.last_seen)}</span></span><span class="fund-today-tile-count">Review context</span><span class="fund-today-chevron" aria-hidden="true">›</span></summary>
            <div class="fund-today-detail"><div class="fund-today-actions"><button class="today-mini-btn fund-navlink" onclick={() => group.organization ? openOrganization(group.key) : openContact(group.contacts[0])}>Review relationship →</button>{#if !groupNextAction(group.contacts) && canAddNextStep(group.contacts[0])}<button class="today-mini-btn" onclick={() => openFollowUp(group.contacts[0])}>Add next step</button>{/if}</div></div>
          </details>
        {/each}
        {#if quietGroups.length > 3}<button class="today-mini-btn ghost today-expand-btn" onclick={() => todayExpanded.quiet = !todayExpanded.quiet}>{todayExpanded.quiet ? 'Show fewer ↑' : `Show ${quietGroups.length - 3} more ↓`}</button>{/if}
      {:else}<div class="today-empty">No LP has gone 30+ days without an email.</div>{/if}
    </section>
  {:else if view === 'dashboard'}
    <div class="fund-page-heading"><div class="page-title">{dashTab === 'portfolio' ? 'Portfolio & pipeline' : 'LP Dashboard'}</div><SyncButton /></div>
    {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
    <!-- Named for the management company, not for one vehicle: a single org-level IR
         workspace covers every fund the team raises for. -->
    <div class="page-subtitle">{workspace.profile.manager_name} · {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
    <div class="tabs fund-dashboard-tabs" role="tablist" aria-label="Dashboard relationships">
      <button type="button" class="tab" role="tab" aria-selected={dashTab === 'ir'} class:active={dashTab === 'ir'} onclick={() => applySavedView(ALL_INVESTORS_KEY)}>LPs <span class="tab-count">{allIrEntities.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={dashTab === 'portfolio'} class:active={dashTab === 'portfolio'} onclick={() => applySavedView(PORTFOLIO_VIEW_KEY)}>Portfolio <span class="tab-count">{allPortfolioCompanies.length}</span></button>
    </div>
    <!-- The Sales dashboard's saved-view bar, same classes, same control: the trigger
         names the active view, `· modified` appears when the chips have drifted off the
         preset, and the menu renames / deletes / bookmarks in place. `.dash-view-bar`
         and friends live in app.css. -->
    <div class="dash-view-bar">
      <div class="vdt" role="button" tabindex="0" aria-haspopup="menu" aria-expanded={viewDropdownOpen}
        onclick={() => viewDropdownOpen = !viewDropdownOpen}
        onkeydown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); viewDropdownOpen = !viewDropdownOpen; } }}>
        {#if activeFundView.bookmarked}<span class="vdt-bm" aria-label="Bookmarked"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg></span>{/if}
        <span class="vdt-name">{activeFundView.name}</span>
        <span class="vdt-arrow">▼</span>
      </div>
      {#if dashTab === 'ir' && fundViewDirty}<span class="vdt-dirty">· modified</span>{/if}
      <span class="count-label">{#if dashTab === 'portfolio'}<b>{portfolioCompanies.length}</b> compan{portfolioCompanies.length === 1 ? 'y' : 'ies'}{:else}<b>{irEntities.length}</b> LP{irEntities.length === 1 ? '' : 's'}{/if}</span>
      <div class="dash-view-spacer"></div>
      <!-- The name box appears only while naming, so the resting state of this row is
           the trigger alone — not four controls. -->
      {#if dashTab === 'ir' && viewNaming}
        <div class="dash-view-name-form">
          <input aria-label="Name for new relationship view" bind:value={newViewName} maxlength="60" placeholder="Name this view" />
          <button class="dash-save-btn" disabled={viewSaving || !newViewName.trim()} onclick={saveCurrentView}>Save</button>
          <button class="dash-save-btn ghost" onclick={() => { viewNaming = false; newViewName = ''; }}>Cancel</button>
        </div>
      {:else if dashTab === 'ir' && fundViewDirty}
        {#if !activeFundView.builtin}
          <button class="dash-save-btn" disabled={viewSaving} onclick={updateActiveFundView}
            title={`Overwrite “${activeFundView.name}” with the current filters + sort`}>Update view</button>
        {/if}
        {#if irFiltersSet}<button class="dash-save-btn ghost" onclick={openSaveAsNew}>Save as new</button>{/if}
      {:else if dashTab === 'ir'}
        <button class="dash-save-btn ghost" disabled>+ Save current view</button>
      {/if}
      {#if viewDropdownOpen}
        <div class="vdm" role="menu">
          <div class="vdm-section-label">Bookmarked</div>
          {#each allFundViews.filter(v => v.bookmarked) as v (v.key)}
            {@render fundViewRow(v)}
          {/each}
          {#if allFundViews.some(v => !v.bookmarked)}
            <div class="vdm-divider"></div>
            <div class="vdm-section-label">All views</div>
            {#each allFundViews.filter(v => !v.bookmarked) as v (v.key)}
              {@render fundViewRow(v)}
            {/each}
          {/if}
        </div>
      {/if}
      {#if viewError}<span class="fund-alert" role="alert">{viewError}</span>{/if}
    </div>
    <!-- One dropdown row: bookmark toggle · name (inline-editable) · count · hover
         rename/delete. "All LPs" is the builtin and owns none of those edits. -->
    {#snippet fundViewRow(v: FundSavedView)}
      <div class="vdm-item" class:active={activeViewKey === v.key} class:editing={editingViewKey === v.key}
        role="menuitem" tabindex="0"
        onclick={() => { if (editingViewKey !== v.key) applyFundView(v); }}
        onkeydown={(event) => {
          if (event.target === event.currentTarget && editingViewKey !== v.key
              && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault(); applyFundView(v);
          }
        }}>
        <button class="vdm-bm" class:on={v.bookmarked} type="button" aria-label="Toggle bookmark"
          disabled={v.builtin}
          title={v.builtin ? 'The default view is always bookmarked'
            : v.bookmarked ? 'Remove bookmark' : 'Bookmark (show in shortcuts)'}
          onclick={(event) => toggleFundViewBookmark(v, event)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
        </button>
        {#if editingViewKey === v.key}
          <input class="vdm-edit" value={v.name} aria-label="Rename view" use:focusSelect
            onclick={(event) => event.stopPropagation()}
            onkeydown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') { renameFundView(v, event.currentTarget.value); editingViewKey = null; }
              else if (event.key === 'Escape') editingViewKey = null;
            }}
            onblur={(event) => { renameFundView(v, event.currentTarget.value); editingViewKey = null; }} />
        {:else}
          <span class="vdm-name">{v.name}</span>
          {#if !v.builtin}
            <button class="vdm-act vdm-rename" title="Rename view" aria-label="Rename view"
              onclick={(event) => { event.stopPropagation(); editingViewKey = v.key; }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            </button>
            <button class="vdm-act vdm-del" title="Delete view" aria-label="Delete view"
              onclick={(event) => deleteFundView(v, event)}>✕</button>
          {/if}
          <span class="vdm-count">{fundViewCount(v.preset)}</span>
        {/if}
      </div>
    {/snippet}
    <div class="page-toolbar">
      <input class="page-toolbar-search" bind:value={dashboardSearch} placeholder="Search name or email…" aria-label="Search relationships" />
      {#if dashTab === 'ir'}
        <!-- Same chip grammar as the Sales dashboard — `.fchip*` lives in app.css and
             is shared, so this adds no styling of its own. Counts are over the search
             result, so ticking one chip never renumbers its siblings. -->
        <div class="fchip-wrap fchip-menu-left">
          <button class="fchip" class:set={irStageFilter.length > 0} aria-haspopup="menu"
                  aria-expanded={openFilterDD === 'stage'}
                  onclick={() => openFilterDD = openFilterDD === 'stage' ? null : 'stage'}>
            Stage <span class="fchip-v">{irStageLbl}</span> <span class="fchip-ar">▾</span>
          </button>
          {#if openFilterDD === 'stage'}
            <div class="fchip-dd" role="menu">
              {#each IR_STAGE_OPTIONS as option}
                {@const n = entityCount(irSearched.filter(c => (relationshipStage(c) || '') === option.key))}
                <button class="fchip-opt" class:on={irStageFilter.includes(option.key)} class:empty={n === 0}
                        onclick={() => irStageFilter = toggleIrFilter(irStageFilter, option.key)}>
                  <span class="fchip-check">✓</span>{option.label}<span class="fchip-count">{n}</span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <div class="fchip-wrap fchip-menu-left">
          <button class="fchip" class:set={irStatusFilter.length > 0} aria-haspopup="menu"
                  aria-expanded={openFilterDD === 'status'}
                  onclick={() => openFilterDD = openFilterDD === 'status' ? null : 'status'}>
            Status <span class="fchip-v">{irStatusLbl}</span> <span class="fchip-ar">▾</span>
          </button>
          {#if openFilterDD === 'status'}
            <div class="fchip-dd" role="menu">
              {#each IR_STATUS_OPTIONS as option}
                {@const n = entityCount(irSearched.filter(c => followUpStatus(c) === option.key))}
                <button class="fchip-opt" class:on={irStatusFilter.includes(option.key)} class:empty={n === 0}
                        onclick={() => irStatusFilter = toggleIrFilter(irStatusFilter, option.key)}>
                  <span class="fchip-check">✓</span>{option.label}<span class="fchip-count">{n}</span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <!-- Review belongs to its own page, never in the filed LP table. -->
        {#if (workspace.institutions || []).length}
          <div class="fchip-wrap">
            <button class="fchip" class:set={!!irInstitutionFilter} aria-haspopup="menu"
                    aria-expanded={openFilterDD === 'institution'}
                    onclick={() => openFilterDD = openFilterDD === 'institution' ? null : 'institution'}>
              Organization <span class="fchip-v">{irInstitutionLbl}</span> <span class="fchip-ar">▾</span>
            </button>
            {#if openFilterDD === 'institution'}
              <div class="fchip-dd" role="menu">
                {#each workspace.institutions || [] as institution}
                  {@const n = entityCount(irSearched.filter(c => c.institution_id === institution.institution_id))}
                  <button class="fchip-opt" class:on={irInstitutionFilter === institution.institution_id} class:empty={n === 0}
                          role="menuitemradio" aria-checked={irInstitutionFilter === institution.institution_id}
                          onclick={() => { irInstitutionFilter = irInstitutionFilter === institution.institution_id ? '' : institution.institution_id; openFilterDD = null; }}>
                    <span class="fchip-tick">✓</span>{institution.name}<span class="fchip-count">{n}</span>
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
        <button class="fchip" class:set={irPinnedOnly} aria-pressed={irPinnedOnly}
                onclick={() => irPinnedOnly = !irPinnedOnly}>
          Pinned <span class="fchip-v">{irPinnedOnly ? 'Only' : 'All'}</span>
        </button>
        <!-- No Sort select: the column headers sort, the way they do on the Sales
             dashboard and in the spec. -->
        {#if irFiltersSet}<button class="fchip-clear" onclick={clearIrFilters}>Clear ✕</button>{/if}
      {/if}
    </div>
    {#if !workspace.inbox_connected && !allContacts.length && dashTab !== 'portfolio'}
      <div class="empty-state">Connect your inbox in Settings to discover contacts.</div>
    {:else if dashTab === 'ir'}
        {#if pinError}<p class="fund-alert" role="alert">{pinError}</p>{/if}
        {#if dashboardMailError}<p class="fund-alert" role="alert">Mail activity is unavailable: {dashboardMailError}</p>{/if}
        <!-- The Sales skeleton plus Latest email; the last three columns match the Portfolio table. The first
             column is the pin/caret edge; names and all expanded content begin at the second column's edge. -->
        <div class="data-table-wrap" onscroll={() => openStageDD = null}><table class="data-table fund-ir-table fund-lp-table"><thead><tr>
          <th class="fund-col-pin"></th>
          <th class="th-sort" aria-sort={irAriaSort('name')}><button type="button" class="fund-sort-button" onclick={() => sortIr('name')}>LP<span class="sort-ind" class:active={irSort === 'name'}>{irSortIndicator('name')}</span></button></th>
          <th class="fund-col-status th-sort" aria-sort={irAriaSort('status')}><button type="button" class="fund-sort-button" onclick={() => sortIr('status')}>Status<span class="sort-ind" class:active={irSort === 'status'}>{irSortIndicator('status')}</span></button></th>
          <th class="fund-col-due th-sort" aria-sort={irAriaSort('due')}><button type="button" class="fund-sort-button" onclick={() => sortIr('due')}>Follow-up<span class="sort-ind" class:active={irSort === 'due'}>{irSortIndicator('due')}</span></button></th>
          <th class="fund-col-subject th-sort" aria-sort={irAriaSort('last_email')}><button type="button" class="fund-sort-button" onclick={() => sortIr('last_email')}>Latest email<span class="sort-ind" class:active={irSort === 'last_email'}>{irSortIndicator('last_email')}</span></button></th>
          <th class="fund-col-io fund-col-inbound num th-sort" aria-sort={irAriaSort('inbound')}><button type="button" class="fund-sort-button" onclick={() => sortIr('inbound')}>Inbound<span class="sort-ind" class:active={irSort === 'inbound'}>{irSortIndicator('inbound')}</span></button></th>
          <th class="fund-col-io fund-col-outbound num th-sort" aria-sort={irAriaSort('outbound')}><button type="button" class="fund-sort-button" onclick={() => sortIr('outbound')}>Outbound<span class="sort-ind" class:active={irSort === 'outbound'}>{irSortIndicator('outbound')}</span></button></th>
        </tr></thead><tbody>
          {#if !irRows.length}
            <tr><td colspan="7" class="empty-state">{dashboardLps.length ? 'No LPs match this view.' : 'No LPs filed yet. Sync your inbox or add a relationship in Contacts.'}</td></tr>
          {:else}
          {#each irGroups as group (group.key)}
            {@const open = expandedIrStages.has(group.key)}
            <tr class="group-row" role="button" tabindex="0" aria-expanded={open}
                onclick={() => toggleIrStageGroup(group.key)}
                onkeydown={(event) => activateRowOnKey(event, () => toggleIrStageGroup(group.key))}>
              <td class="fund-ir-group-gutter"><span class="group-arrow">{open ? '▼' : '▶'}</span><span class="stage-dot" style="background:{group.color};"></span></td>
              <td colspan="6" class="fund-ir-group-name"><span class="group-name">{group.label}</span><span class="group-meta">{group.rows.length} LP{group.rows.length === 1 ? '' : 's'}</span></td>
            </tr>
            {#if open}
              {#each group.rows as organization (organization.key)}
                {@const orgOpen = expandedFundOrganizations.has(organization.key)}
                {@const action = entityAction(organization)}
                {@const pill = action ? statusPill(action) : null}
                {@const inbound = groupMailActivity(organization.contacts, 'inbound')}
                {@const outbound = groupMailActivity(organization.contacts, 'outbound')}
                {@const latest = groupLatestSubject(organization.contacts)}
                <!-- The whole row expands. Navigation is the single action in its rail. -->
                <tr class="data-row fund-ir-entity" class:open={orgOpen}
                    role="button" tabindex="0" aria-expanded={orgOpen}
                    onclick={() => toggleFundOrganization(organization.key)}
                    onkeydown={(event) => activateRowOnKey(event, () => toggleFundOrganization(organization.key))}>
                  <td class="fund-ir-pin">{#if entityPinnable(organization)}<button class="star-btn" class:on={entityPinned(organization)} disabled={savingPin} title={entityPinned(organization) ? 'Unpin from sidebar' : 'Pin to sidebar'} aria-label={`${entityPinned(organization) ? 'Unpin' : 'Pin'} ${organization.name}`} onclick={(event) => { event.stopPropagation(); toggleEntityPin(organization); }}>{entityPinned(organization) ? '★' : '☆'}</button>{/if}</td>
              <td class="fund-ir-main"><span class="fund-ir-entity-name">{organization.name}</span></td>
                  <td>{#if pill}<span class="status-pill {pill.cls}" title={`Next step for ${action?.display_name}: ${relationshipNextStep(action!)}`}>{pill.label}</span>{:else}<span class="fund-ir-secondary">—</span>{/if}</td>
                  <td class="fund-col-due">{action && relationshipDue(action) ? mailDate(relationshipDue(action)) : '—'}</td>
                  <td class="fund-ir-subject" title={latest?.subject || undefined}><span class="fund-ir-subject-text">{latest ? latest.subject || '(no subject)' : '—'}</span></td>
                  <td class="num fund-col-inbound">{activityAgo(inbound)}</td>
                  <td class="num fund-col-outbound">{activityAgo(outbound)}</td>
                </tr>
                {#if orgOpen}
                  {@const stageContact = entityStageContact(organization)}
                  <!-- The spec's two-column panel: what the records say on the left, the
                       controls that change them on the right. Clicks inside must not
                       reach the row, or reading the panel would collapse it. -->
                  <tr class="fund-ir-detail-row"><td class="fund-ir-detail-gutter"></td><td colspan="6">
                   <!-- svelte-ignore a11y_click_events_have_key_events -->
                   <!-- svelte-ignore a11y_no_static_element_interactions -->
                   <div class="fund-ir-panel" onclick={(event) => event.stopPropagation()}>
                    <div class="fund-ir-panel-main">
                      <div class="fund-record-subhead">{agent}'s summary</div>
                      <p class="fund-ir-summary">{organization.contacts.find(c => c.suggested_reason)?.suggested_reason || 'No relationship summary is available yet. Open this LP to review the evidence or prepare a brief.'}</p>
                      <div class="fund-ir-section">
                        <div class="fund-record-subhead">Source</div>
                        <p class="fund-ir-summary">{organization.contacts.some(c => c.set_by === 'model')
                          ? 'Relationship inferred from synced mail.' : 'Relationship recorded by your team.'}
                          {organization.key.startsWith('domain:') ? ' Contacts are grouped by shared email domain.' : ''}</p>
                        {#if inbound || outbound}<p class="fund-ir-source">Last mail: {inbound ? `inbound ${mailDate(inbound)}` : 'no inbound recorded'} · {outbound ? `outbound ${mailDate(outbound)}` : 'no outbound recorded'}</p>{/if}
                      </div>
                      <div class="fund-ir-section">
                        <div class="fund-record-subhead">Update opens ({(dashboardTracking[organization.key] || []).reduce((total, item) => total + item.opens, 0)})</div>
                        {#if dashboardTrackingLoading.has(organization.key)}
                          <p class="fund-ir-muted-note">Loading your tracked updates…</p>
                        {:else if dashboardTrackingErrors[organization.key]}
                          <p class="fund-alert" role="alert">{dashboardTrackingErrors[organization.key]} <button class="today-mini-btn" onclick={() => { const { [organization.key]: _old, ...remaining } = dashboardTracking; dashboardTracking = remaining; void loadEntityTracking(organization); }}>Retry</button></p>
                        {:else if dashboardTracking[organization.key]?.length}
                          {#each dashboardTracking[organization.key].slice(0, 5) as tracked (tracked.tracking_id)}
                            <div class="fund-ir-open"><strong>{tracked.opens}×</strong><span>{tracked.subject}</span><time>{tracked.last_opened_at ? `Last opened ${mailDate(tracked.last_opened_at)}` : 'Not opened'}</time></div>
                          {/each}
                          <p class="fund-ir-source">Your tracked sends only. Mail privacy settings may block opens.</p>
                        {:else}
                          <p class="fund-ir-muted-note">No tracked LP updates sent from your mailbox yet.</p>
                        {/if}
                      </div>
                      <div class="fund-ir-section">
                        <div class="fund-record-subhead">Stage reasoning</div>
                        <p class="fund-ir-summary">{stageContact && storedStage(stageContact)
                          ? relationshipStageSource(stageContact) === 'candidate'
                            ? relationshipStageReason(stageContact) || 'Early relationship from synced mail.'
                            : relationshipStageSource(stageContact) === 'ai'
                            ? relationshipStageReason(stageContact) || `${agent} inferred this stage from synced mail.`
                            : relationshipStageSource(stageContact) === 'admin_proof'
                            ? relationshipStageReason(stageContact) || 'Existing LP: fund administration mail shows a signed or funded commitment.'
                            : 'Set by your team. Manual stage updates are preserved.'
                          : stageContact && relationshipStageSource(stageContact) === 'ai'
                            ? relationshipStageReason(stageContact) || `${agent} found no synced-mail evidence beyond a possible LP, so the stage is Prospect.`
                            : stageContact && relationshipStage(stageContact)
                            ? 'Prospect until synced mail or your team records more.'
                            : 'No stage is recorded for this LP yet.'}</p>
                      </div>
                    </div>
                    <!-- Stage and manual override use the same pattern as the Sales expand. -->
                    <div class="fund-ir-rail">
                      <span class="fund-ir-rail-label">Stage</span>
                      {#if actionableLp(stageContact)}
                        <div class="fchip-wrap fund-ir-stage-dd">
                          <button class="fund-ir-stage-btn" aria-haspopup="menu"
                                  aria-expanded={openStageDD === organization.key} disabled={savingStage}
                                  onclick={(event) => toggleStageMenu(organization.key, event.currentTarget)}>
                            <span class="fund-ir-stage-val"><span class="stage-dot" style="background:{fundStageColor(relationshipStage(stageContact))};"></span>{fundStageLabel(relationshipStage(stageContact))}</span>
                            <span class="fchip-ar">▾</span>
                          </button>
                        </div>
                      {:else if stageContact}
                        <span class="fund-ir-stage-btn"><span class="fund-ir-stage-val"><span class="stage-dot" style="background:{fundStageColor(relationshipStage(stageContact))};"></span>{fundStageLabel(relationshipStage(stageContact))}</span></span>
                      {:else}
                        <p class="fund-ir-secondary">Choose an LP at this organization to set a stage.</p>
                      {/if}
                      {#if stageContact}
                        <p class="fund-ir-rail-help">{relationshipStageSource(stageContact) === 'candidate'
                          ? 'Potential relationship · stage inferred from synced mail'
                          : relationshipStageSource(stageContact) === 'ai'
                          ? `Set by ${agent} · updates after sync`
                          : relationshipStageSource(stageContact) === 'admin_proof'
                          ? `Set by ${agent} from fund administration mail`
                          : storedStage(stageContact) ? 'Manual stage' : 'Prospect by default · updates after sync'}</p>
                        {#if relationshipStageSource(stageContact) === 'manual_override'
                            || (storedStage(stageContact) && !relationshipStageSource(stageContact))}
                          <button class="fund-row-link" disabled={savingStage}
                                  onclick={() => resumeEntityAutoStage(organization)}>Resume automatic stage updates</button>
                        {/if}
                      {/if}
                      <!-- The API refuses a date without a next step, so the date field exists only once there is one. -->
                      {#if stageContact && actionableLp(stageContact) && relationshipNextStep(stageContact)}
                        <label class="fund-ir-rail-label" for={`due-${organization.key}`}>Follow-up</label>
                        <input class="fund-ir-rail-input" id={`due-${organization.key}`} type="date"
                               value={relationshipDue(stageContact) || ''} disabled={savingFollowUp}
                               onchange={(event) => setEntityFollowUpDate(organization, event.currentTarget.value)} />
                      {:else if stageContact}
                        <span class="fund-ir-rail-label">Follow-up</span>
                        <button class="fund-row-link fund-ir-secondary" onclick={() => openFollowUp(stageContact)}>
                          Add a next step to set a follow-up date →
                        </button>
                      {:else}
                        <span class="fund-ir-rail-label">Follow-up</span>
                        <p class="fund-ir-secondary">Available once an LP here is filed.</p>
                      {/if}
                      <span class="fund-ir-rail-label">LP name</span>
                      <div class="fund-ir-rail-name">{organization.name}</div>
                      <button class="fund-row-link fund-ir-rail-open" onclick={() => organization.organization
                        ? openOrganization(organization.key) : openContact(organization.contacts[0])}>
                        Open LP →
                      </button>
                    </div>
                  </div></td></tr>
                {/if}
              {/each}
            {/if}
          {/each}
          {/if}
        </tbody></table></div>
        {#if openStageDD}
          {@const menuEntity = irSortedEntities.find(entity => entity.key === openStageDD)}
          {#if menuEntity}
            {@const menuContact = entityStageContact(menuEntity)}
            {#if menuContact}
              <div class="fchip-dd fund-ir-stage-dd fund-ir-stage-menu" role="menu" use:fitStageMenu
                   style={`top:${stageMenuPosition.top}px;left:${stageMenuPosition.left}px`}>
                {#each FUND_STAGES as option}
                  <button class="fchip-opt" role="menuitemradio" aria-checked={relationshipStage(menuContact) === option.key}
                          class:on={relationshipStage(menuContact) === option.key}
                          onclick={() => setEntityStage(menuEntity, option.key)}>
                    <span class="fchip-tick">✓</span><span class="stage-dot" style="background:{fundStageColor(option.key)};"></span>{option.label}
                  </button>
                {/each}
                {#if menuEntity.contacts.length === 1}
                  <div class="fund-ir-dd-sep"></div>
                  <div class="fund-ir-dd-label">Role</div>
                  {#each [['investor', 'LP'], ['potential_portfolio', 'Potential Portfolio'], ['portfolio', 'Portfolio'], ['neither', 'Neither']] as [value, label]}
                    <button class="fchip-opt" role="menuitemradio" aria-checked={roleChoice(menuEntity.contacts[0]) === value}
                            class:on={roleChoice(menuEntity.contacts[0]) === value}
                            disabled={!!savingRole}
                            onclick={() => { openStageDD = null; setRole(menuEntity.contacts[0], value as FundRoleChoice); }}>
                      <span class="fchip-tick">✓</span>{label}
                    </button>
                  {/each}
                {/if}
              </div>
            {/if}
          {/if}
        {/if}
        <div class="fund-ir-table-footer">
          <p class="fund-ir-scan-note">{irScanNote}</p>
        </div>
    {:else}
        {#if dashboardMailError}<p class="fund-alert" role="alert">Mail activity is unavailable: {dashboardMailError}</p>{/if}
        <!-- The LP table's skeleton and expand panel, without Status and Follow-up. People are on the company page's Contacts tab, not in this table. -->
        <div class="data-table-wrap"><table class="data-table fund-ir-table fund-portfolio-table">
          <thead><tr><th class="fund-col-pin"></th>
            <th class="th-sort" aria-sort={portfolioAriaSort('name')}><button type="button" class="fund-sort-button" onclick={() => sortPortfolio('name')}>Company<span class="sort-ind" class:active={portfolioSort === 'name'}>{portfolioSortIndicator('name')}</span></button></th>
            <th class="fund-col-subject th-sort" aria-sort={portfolioAriaSort('last_email')}><button type="button" class="fund-sort-button" onclick={() => sortPortfolio('last_email')}>Latest email<span class="sort-ind" class:active={portfolioSort === 'last_email'}>{portfolioSortIndicator('last_email')}</span></button></th>
            <th class="fund-col-io fund-col-inbound num th-sort" aria-sort={portfolioAriaSort('inbound')}><button type="button" class="fund-sort-button" onclick={() => sortPortfolio('inbound')}>Inbound<span class="sort-ind" class:active={portfolioSort === 'inbound'}>{portfolioSortIndicator('inbound')}</span></button></th>
            <th class="fund-col-io fund-col-outbound num th-sort" aria-sort={portfolioAriaSort('outbound')}><button type="button" class="fund-sort-button" onclick={() => sortPortfolio('outbound')}>Outbound<span class="sort-ind" class:active={portfolioSort === 'outbound'}>{portfolioSortIndicator('outbound')}</span></button></th>
          </tr></thead>
          <tbody>
          {#if !portfolioCompanies.length}
            <tr><td colspan="5" class="empty-state">{searchTerm ? 'No portfolio or pipeline companies match this search.' : 'No invested or potential portfolio companies yet.'}</td></tr>
          {/if}
          {#each portfolioGroups as group (group.key)}
            {@const groupOpen = !collapsedPortfolioStages.has(group.key)}
            <tr class="group-row" role="button" tabindex="0" aria-expanded={groupOpen}
                onclick={() => togglePortfolioStage(group.key)}
                onkeydown={(event) => activateRowOnKey(event, () => togglePortfolioStage(group.key))}>
              <td class="fund-ir-group-gutter"><span class="group-arrow">{groupOpen ? '▼' : '▶'}</span><span class="stage-dot" style="background:{group.color};"></span></td>
              <td colspan="4" class="fund-ir-group-name"><span class="group-name">{group.label}</span><span class="group-meta">{group.rows.length} compan{group.rows.length === 1 ? 'y' : 'ies'}</span></td>
            </tr>
            {#if groupOpen}
            {#each group.rows as company (company.key)}
            {@const open = expandedFundOrganizations.has(company.key)}
            {@const latest = groupLatestSubject(company.contacts)}
            {@const inbound = groupMailActivity(company.contacts, 'inbound')}
            {@const outbound = groupMailActivity(company.contacts, 'outbound')}
            {@const invested = company.contacts.some(contact => contact.role === 'portfolio' && !potentialPortfolio(contact))}
            <tr class="data-row fund-ir-entity" class:open role="button" tabindex="0" aria-expanded={open}
                onclick={() => toggleFundOrganization(company.key)}
                onkeydown={(event) => activateRowOnKey(event, () => toggleFundOrganization(company.key))}>
              <td class="fund-ir-pin"></td>
              <td class="fund-ir-main"><span class="fund-ir-entity-name">{company.name}</span></td>
              <td class="fund-ir-subject" title={latest?.subject || undefined}><span class="fund-ir-subject-text">{latest ? latest.subject || '(no subject)' : '—'}</span></td>
              <td class="num fund-col-inbound">{activityAgo(inbound)}</td>
              <td class="num fund-col-outbound">{activityAgo(outbound)}</td>
            </tr>
            {#if open}
              <tr class="fund-ir-detail-row"><td class="fund-ir-detail-gutter"></td><td colspan="4">
               <!-- svelte-ignore a11y_click_events_have_key_events -->
               <!-- svelte-ignore a11y_no_static_element_interactions -->
               <div class="fund-ir-panel" onclick={(event) => event.stopPropagation()}>
                <div class="fund-ir-panel-main">
                  <div class="fund-record-subhead">{agent}'s summary</div>
                  <p class="fund-ir-summary">{company.contacts.find(c => c.suggested_reason)?.suggested_reason || 'No summary is available yet. Open this company to read the synced mail.'}</p>
                  <div class="fund-ir-section">
                    <div class="fund-record-subhead">Source</div>
                    <p class="fund-ir-summary">{invested ? 'Recorded investment.' : company.contacts.some(contact => contact.suggested_source === 'user_candidate') ? 'Potential opportunity recorded by your team; no investment recorded.' : 'Potential opportunity from synced mail; no investment recorded.'}
                      {company.key.startsWith('domain:') ? ' Contacts share a business email domain.' : ''}</p>
                    {#if !company.organization && company.contacts[0]?.suggested_organization_name}
                      <p class="fund-ir-source">Organization mentioned in mail: {company.contacts[0].suggested_organization_name} · not linked</p>
                    {/if}
                    {#if inbound || outbound}<p class="fund-ir-source">Last mail: {inbound ? `inbound ${mailDate(inbound)}` : 'no inbound recorded'} · {outbound ? `outbound ${mailDate(outbound)}` : 'no outbound recorded'}</p>{/if}
                  </div>
                </div>
                <div class="fund-ir-rail">
                  <span class="fund-ir-rail-label">Relationship</span>
                  <span class="fund-ir-stage-btn">{group.label}</span>
                  <span class="fund-ir-rail-label">Company name</span>
                  <div class="fund-ir-rail-name">{company.name}</div>
                  <button class="fund-row-link fund-ir-rail-open" onclick={() => openContact(portfolioPageContact(company))}>
                    Open company →
                  </button>
                </div>
              </div></td></tr>
            {/if}
            {/each}
            {/if}
          {/each}</tbody>
        </table></div>
        <div class="fund-ir-table-footer">
          <p class="fund-ir-scan-note">{portfolioScanNote}</p>
        </div>
    {/if}
  {:else if view === 'scheduled'}
    <div class="fund-page-heading"><div class="page-title">Scheduled Emails</div><button class="today-mini-btn" onclick={loadAllScheduled} disabled={scheduledLoading}>Refresh</button></div>
    <div class="page-toolbar">
      {#each [['all', 'All'], ['pending', 'Pending'], ['sent', 'Sent'], ['attention', 'Needs review']] as [key, label]}
        <button class="fchip" class:set={scheduledFilter === key} onclick={() => scheduledFilter = key as typeof scheduledFilter}>{label}</button>
      {/each}
      <span class="count-label"><b>{shownScheduled.length}</b> emails</span>
    </div>
    {#if scheduledError}<p class="fund-alert" role="alert">{scheduledError}</p>{/if}
    {#if scheduledLoading && !allScheduled.length}<div class="empty-state" role="status">Loading scheduled emails…</div>
    {:else if !shownScheduled.length}<div class="empty-state">No {scheduledFilter === 'all' ? '' : scheduledFilter + ' '}scheduled emails from your mailbox.</div>
    {:else}
      <div class="data-table-wrap"><table class="data-table"><thead><tr><th>LP</th><th>Subject</th><th>To / CC</th><th>Send at</th><th>Status</th><th></th></tr></thead><tbody>
        {#each shownScheduled as item (item.request_id)}
          {@const contact = allContacts.find(c => c.counterparty_id === item.counterparty_id)}
          <tr class="data-row">
            <td>{#if contact}<button class="fund-row-link" onclick={() => openContact(contact)}>{contact.display_name}</button>{:else}{item.recipient}{/if}</td>
            <td><button class="fund-row-link" onclick={() => openSchedule(item.request_id)}>{item.subject}</button></td><td>{item.recipient}{#if item.cc}<div class="fund-hint">CC {item.cc}</div>{/if}</td>
            <td>{new Date(item.send_at).toLocaleString()}</td>
            <td>{item.status === 'uncertain' || item.status === 'claimed' ? 'Check Sent folder' : item.status}</td>
            <td>{#if item.status === 'pending'}<button class="today-mini-btn ghost" onclick={() => cancelFromSchedule(item.request_id)}>Cancel</button>{/if}</td>
          </tr>
        {/each}
      </tbody></table></div>
      {#if selectedSchedule}
        <section class="card fund-schedule-editor" aria-label="Scheduled email details">
          <div class="fund-compose-heading"><h2>Review scheduled email</h2><button class="today-mini-btn" onclick={() => selectedSchedule = null}>Close</button></div>
          <p class="fund-muted">From your mailbox · To {selectedSchedule.recipient} · {selectedSchedule.status}</p>
          {#if selectedSchedule.calendar_invite}<p class="fund-muted">Calendar invite: {selectedSchedule.calendar_invite.summary} · {new Date(selectedSchedule.calendar_invite.start).toLocaleString()}</p>{/if}
          {#if selectedSchedule.tracking_enabled}<p class="fund-muted">Open tracking is enabled for this email.</p>{/if}
          {#if selectedSchedule.status === 'pending'}
            <label class="fund-field">Subject<input bind:value={scheduleSubject} maxlength="250" /></label>
            <label class="fund-field">CC (optional)<input bind:value={scheduleCc} maxlength="1000" /></label>
            <label class="fund-field">Message<EmailBodyEditor bind:value={scheduleBody} rows={10} /></label>
            <div class="fund-compose-actions"><label class="fund-field">Date<input type="date" bind:value={scheduleEditDate} /></label><label class="fund-field">Time<input type="time" bind:value={scheduleEditTime} /></label></div>
            {#if selectedSchedule.attachment_names.length}<p class="fund-muted">Attachments: {selectedSchedule.attachment_names.join(', ')}</p>{/if}
            <div class="fund-compose-actions"><button class="btn-primary" onclick={saveScheduleEdit} disabled={scheduleSaving || !scheduleSubject.trim() || !scheduleBody.trim()}>{scheduleSaving ? 'Saving…' : 'Save changes'}</button><button class="today-mini-btn" onclick={() => cancelFromSchedule(selectedSchedule!.request_id)} disabled={scheduleSaving}>Cancel send</button></div>
          {:else}<p class="fund-muted">No longer pending. If unsure, check your Sent folder.</p>{/if}
        </section>
      {/if}
    {/if}
  {:else if view === 'contacts'}
    {@const tabContacts = contactsTab === 'lp' ? lpDirectoryContacts : portfolioDirectoryContacts}
    <div class="page-title">Contacts</div>
    <div class="tabs fund-dashboard-tabs" role="tablist" aria-label="Contact relationships">
      <button type="button" class="tab" role="tab" aria-selected={contactsTab === 'lp'} class:active={contactsTab === 'lp'} onclick={() => { contactsTab = 'lp'; newRole = 'investor'; editingContactId = ''; }}>LPs <span class="tab-count">{lpDirectoryContacts.length}</span></button>
      <button type="button" class="tab" role="tab" aria-selected={contactsTab === 'portfolio'} class:active={contactsTab === 'portfolio'} onclick={() => { contactsTab = 'portfolio'; newRole = 'portfolio'; editingContactId = ''; }}>Portfolio <span class="tab-count">{portfolioDirectoryContacts.length}</span></button>
    </div>
    <div class="page-toolbar">
      <input class="page-toolbar-search" bind:value={contactsSearch} oninput={() => { collapsedContactSearchGroups = new Set(); }} placeholder="Search name, email or institution…" aria-label="Search contacts" />
      <span class="count-label"><b>{tabContacts.length}</b> contact{tabContacts.length === 1 ? '' : 's'}</span>
      <div class="page-toolbar-spacer"></div>
      <div class="page-toolbar-actions"><button class="action-btn primary-action" onclick={() => { actionError = ''; addOpen = !addOpen; }}>+ Add contact</button></div>
    </div>
    {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
    {#if addOpen}
      <form class="card mb-4" onsubmit={(event) => { event.preventDefault(); addContact(); }}>
        <div class="font-bold mb-3">Add contact</div>
        <div class="grid-3 mb-3">
          <div><label class="text-xs text-dim" for="fund-new-email">Email *</label><input id="fund-new-email" type="email" bind:value={newEmail} required placeholder="person@example.com" /></div>
          <div><label class="text-xs text-dim" for="fund-new-name">Name</label><input id="fund-new-name" bind:value={newName} placeholder="Full name" /></div>
          <div><label class="text-xs text-dim" for="fund-new-role">Relationship *</label><select id="fund-new-role" bind:value={newRole}><option value="investor">LP</option><option value="portfolio">Portfolio</option></select></div>
        </div>
        <div class="row"><button class="btn-primary" type="submit" disabled={!!savingRole}>{savingRole ? 'Saving…' : 'Save'}</button><button type="button" class="today-mini-btn" onclick={() => addOpen = false} disabled={!!savingRole}>Cancel</button></div>
      </form>
    {/if}
    {#if !workspace.inbox_connected && !allContacts.length}
      <div class="empty-state">Connect your inbox in Settings to discover contacts.</div>
    {:else if !tabContacts.length}
      <div class="empty-state">{contactsSearch.trim() ? `No ${contactsTab === 'lp' ? 'LP' : 'Portfolio'} contacts match this search.`
        : allContacts.length ? `No ${contactsTab === 'lp' ? 'LP' : 'Portfolio'} contacts found yet.` : 'No contacts synced yet. Start a mailbox sync.'}</div>
    {:else}
      <div class="data-table-wrap"><table class="data-table fund-contacts-table"><thead><tr><th class="fund-contacts-gutter"></th><th>Contact</th><th class="fund-contacts-email">Email</th><th class="fund-contacts-date">Last seen</th><th class="num fund-contacts-activity">Activity</th><th class="fund-contacts-actions"></th></tr></thead><tbody>
        {#each contactSections as section (section.key)}
          {@const sectionOpen = !collapsedContactSections.has(section.key)}
          <tr class="group-row" role="button" tabindex="0" aria-expanded={sectionOpen}
              onclick={() => toggleContactSection(section.key)}
              onkeydown={(event) => activateRowOnKey(event, () => toggleContactSection(section.key))}>
            <td colspan="6"><div class="group-header"><span class="group-arrow">{sectionOpen ? '▼' : '▶'}</span><span class="stage-dot" style="background:{section.color};"></span><span class="group-name">{section.label}</span><span class="group-meta">{section.groups.length} {contactsTab === 'lp' ? `LP${section.groups.length === 1 ? '' : 's'}` : `compan${section.groups.length === 1 ? 'y' : 'ies'}`}</span></div></td>
          </tr>
          {#if sectionOpen}
            {#each section.groups as organization (organization.key)}
              <!-- An organization starts closed, as in Sales; a lone person starts open so their row shows. -->
              {@const open = contactsSearch.trim() ? !collapsedContactSearchGroups.has(organization.key)
                : expandedContactOrganizations.has(organization.key) !== !organization.organization}
              {@const latest = groupLatestMail(organization.contacts)}
              {@const stage = contactGroupStage(organization)}
              {@const invested = companyInvested(organization)}
              <tr class="subgroup-row" role="button" tabindex="0"
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${organization.name}`} aria-expanded={open}
                  onclick={() => toggleContactOrganization(organization.key)}
                  onkeydown={(event) => activateRowOnKey(event, () => toggleContactOrganization(organization.key))}>
                <td colspan="6"><div class="subgroup-header">
                  <span class="subgroup-arrow">{open ? '▾' : '▸'}</span><span class="subgroup-name">{organization.name}</span>
                  <span class="subgroup-meta">{[`${organization.contacts.length} contact${organization.contacts.length === 1 ? '' : 's'}`, organization.source,
                    latest ? `last seen ${mailDate(latest.last_seen)}` : 'no activity'].filter(Boolean).join(' · ')}</span>
                  {#if contactsTab === 'lp'}
                    <button type="button" class="stage-pill" aria-haspopup="menu" aria-expanded={openStageDD === `contacts:${organization.key}`}
                            aria-label={`Stage for ${organization.name}: ${stage ? fundStageLabel(stage) : 'No stage'}`}
                            title={stageEditable(organization) ? undefined : 'File a contact here as an LP to set a stage'}
                            disabled={!stageEditable(organization)}
                            onclick={(event) => { event.stopPropagation(); toggleStageMenu(`contacts:${organization.key}`, event.currentTarget); }}>
                      <span class="stage-dot" style="background:{stage ? fundStageColor(stage) : 'var(--text-dim)'};"></span>{stage ? fundStageLabel(stage) : 'No stage'}<span class="fchip-ar">▾</span>
                    </button>
                  {:else}
                    <button type="button" class="stage-pill" aria-haspopup="menu" aria-expanded={openStageDD === `contacts:${organization.key}`}
                            aria-label={`${organization.name}: ${invested ? 'Existing' : 'Potential'} Portfolio`}
                            disabled={!typeEditable(organization)}
                            onclick={(event) => { event.stopPropagation(); toggleStageMenu(`contacts:${organization.key}`, event.currentTarget); }}>
                      <span class="stage-dot" style="background:{invested ? fundStageColor('Client') : 'var(--text-dim)'};"></span>{invested ? 'Existing' : 'Potential'}<span class="fchip-ar">▾</span>
                    </button>
                  {/if}
                </div></td>
              </tr>
              {#if open}
                {#each organization.contacts as contact (contact.counterparty_id)}
                  {@render fundContactRow(contact, organization.organization)}
                {/each}
              {/if}
            {/each}
          {/if}
        {/each}
      </tbody></table></div>
      {#if openStageDD?.startsWith('contacts:')}
        {@const menuGroup = contactSections.flatMap(section => section.groups).find(group => `contacts:${group.key}` === openStageDD)}
        {#if menuGroup}
          <div class="fchip-dd fund-ir-stage-dd fund-ir-stage-menu" role="menu" use:fitStageMenu
               style={`top:${stageMenuPosition.top}px;left:${stageMenuPosition.left}px`}>
            {#if contactsTab === 'lp'}
              {#each FUND_STAGES as option}
                <button class="fchip-opt" role="menuitemradio" aria-checked={contactGroupStage(menuGroup) === option.key}
                        class:on={contactGroupStage(menuGroup) === option.key}
                        onclick={() => setEntityStage(menuGroup, option.key)}>
                  <span class="fchip-tick">✓</span><span class="stage-dot" style="background:{fundStageColor(option.key)};"></span>{option.label}
                </button>
              {/each}
            {:else}
              {#each PORTFOLIO_TYPES as option}
                <button class="fchip-opt" role="menuitemradio" aria-checked={companyInvested(menuGroup) === option.invested}
                        class:on={companyInvested(menuGroup) === option.invested}
                        onclick={() => setCompanyInvested(menuGroup, option.invested)}>
                  <span class="fchip-tick">✓</span><span class="stage-dot" style="background:{option.invested ? fundStageColor('Client') : 'var(--text-dim)'};"></span>{option.label}
                </button>
              {/each}
            {/if}
          </div>
        {/if}
      {/if}
    {/if}
  {:else if view === 'detail' && selectedOrganization}
    <div class="client-header-wrap">
      <button class="back-crumb" onclick={backFromDetail}>← Back to {detailBackLabel}</button>
      <div class="lp-hdr">
        <div class="lp-hdr-text">
          <div class="page-title">{selectedOrganization.name}</div>
          <div class="lp-meta"><span>{selectedOrganization.contacts.length} contact{selectedOrganization.contacts.length === 1 ? '' : 's'}</span></div>
        </div>
        <div class="lp-hdr-actions">
          <button class="today-mini-btn" disabled={savingPin} aria-label={isOrganizationPinned(selectedOrganization.key) ? `Unpin ${selectedOrganization.name}` : `Pin ${selectedOrganization.name}`}
            onclick={() => toggleOrganizationPin(selectedOrganization.key)}>{isOrganizationPinned(selectedOrganization.key) ? '★ Pinned' : '☆ Pin'}</button>
          {#if selectedOrganizationRecord}
            <button class="today-mini-btn" onclick={openOrganizationFollowUp}>{selectedOrganizationRecord.next_step ? 'Edit next step' : 'Add next step'}</button>
          {/if}
        </div>
      </div>
      {#if pinError}<p class="fund-alert" role="alert">{pinError}</p>{/if}
      {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
      <div class="fund-source-block">
        <div class="fund-record-subhead">Source</div>
        <p>{selectedOrganizationRecord
          ? selectedOrganization.contacts.some(contact => contact.set_by === 'model')
            ? 'LP relationship inferred from synced mail.'
            : 'LP relationship recorded by your team.'
          : 'Contacts grouped by shared business email domain.'}</p>
      </div>
      {#if selectedOrganizationRecord}
        <div class="fund-compose-actions">
          <label class="fund-field">Relationship stage
            <select class="fund-inline-role" value={selectedOrganizationRecord.stage || 'Prospect'} disabled={savingStage}
              onchange={async (event) => {
                const control = event.currentTarget;
                savingStage = true; actionError = '';
                try { await setFundInstitutionStage(selectedOrganizationRecord.institution_id, control.value || null); await load(); }
                catch (err: any) { control.value = selectedOrganizationRecord.stage || 'Prospect'; actionError = err?.message || 'Could not save institution stage.'; }
                finally { savingStage = false; }
              }}>
              {#each FUND_STAGES as stage}<option value={stage.key}>{stage.label}</option>{/each}
            </select>
          </label>
          {#if selectedOrganizationRecord.next_step}<div class="lp-nextstep" title={nextStepAuthor(selectedOrganizationRecord)}><span class="lbl">Next step</span><span class="val">{selectedOrganizationRecord.next_step}</span>{#if selectedOrganizationRecord.follow_up_on}<span class="due">by {mailDate(selectedOrganizationRecord.follow_up_on)}</span>{/if}</div>{/if}
        </div>
        {#if followUpEditing}
          <div class="fund-action-card">
            <div class="fund-compose-heading"><h3>{selectedOrganizationRecord.next_step ? 'Edit next step' : 'Add next step'} · {selectedOrganization.name}</h3><button class="today-mini-btn" onclick={() => followUpEditing = false}>Close</button></div>
            <label class="fund-field">Action<textarea bind:value={nextStepDraft} maxlength="500" rows="2" placeholder="An action for this institution"></textarea></label>
            <label class="fund-field">Follow up by (optional)<input type="date" bind:value={followUpDateDraft} /></label>
            <button class="btn-primary" onclick={saveOrganizationFollowUp} disabled={savingFollowUp || (!!followUpDateDraft && !nextStepDraft.trim())}>{savingFollowUp ? 'Saving…' : 'Save next step'}</button>
            {#if selectedOrganizationRecord.next_step}<button class="today-mini-btn ghost" disabled={savingFollowUp} onclick={() => { nextStepDraft = ''; followUpDateDraft = ''; saveOrganizationFollowUp(); }}>Remove next step</button>{/if}
          </div>
        {/if}
      {:else}
        {#if selectedOrganization.contacts[0]}<button class="today-mini-btn" onclick={() => openContactAt(selectedOrganization.contacts[0], 'contacts')}>Open a contact →</button>{/if}
      {/if}
    </div>
    <div class="fund-record-subhead">People at {selectedOrganization.name}</div>
    <div class="data-table-wrap"><table class="data-table"><thead><tr><th>Contact</th><th>Relationship</th><th>Inbound</th><th>Outbound</th><th></th></tr></thead><tbody>
      {#each selectedOrganization.contacts as member (member.counterparty_id)}
        <tr class="data-row"><td><button class="fund-row-link" onclick={() => openContact(member)}>{member.display_name}</button><div class="fund-ir-secondary">{member.email || 'No email address'}</div></td>
          <td>{member.role ? roleLabel(member.role) : unfiledLabel(member)}</td>
          <td>{activityAgo(dashboardMailActivity[member.counterparty_id]?.inbound || null)}</td>
          <td>{activityAgo(dashboardMailActivity[member.counterparty_id]?.outbound || null)}</td>
          <td><button class="fund-row-link" onclick={() => openContact(member)}>Open person →</button></td></tr>
      {/each}
    </tbody></table></div>
    <div class="fund-record-subhead">Recent email context</div>
    {#if selectedOrganizationRecord}
      {#if institutionActivityLoading}<p class="fund-muted">Loading institution mail…</p>
      {:else if institutionActivityError}<p class="fund-alert" role="alert">{institutionActivityError}</p>
      {:else if institutionActivity.length}
        {#each institutionActivity as item (`${item.counterparty_id}:${item.owner_email}:${item.message_id}`)}
          <div class="fund-person-row"><div><button class="fund-row-link" onclick={() => { const person = allContacts.find(c => c.counterparty_id === item.counterparty_id); if (person) openContact(person); }}>{item.subject || '(No subject)'}</button><p>{item.display_name} · {mailDate(item.sent_at)} · synced by {item.owner_email}</p></div></div>
        {/each}
      {:else}<p class="fund-muted">No synced mail for LP members of this institution.</p>{/if}
    {:else}
      {#each selectedOrganization.contacts as member (member.counterparty_id)}
        {#each member.sample_subjects || [] as subject, index (`${member.counterparty_id}:${index}`)}
          <div class="fund-person-row"><div><button class="fund-row-link" onclick={() => openContact(member)}>{subject}</button><p>{member.display_name}</p></div></div>
        {/each}
      {/each}
    {/if}
  {:else if view === 'detail'}
    <div class="client-header-wrap" class:rail-open={showMikeRail} style="--fund-mike-w: {mikeW}px">
      <button class="back-crumb" onclick={backFromDetail}>← Back to {detailBackLabel}</button>
      {#if selected}
        {@const selectedOrgHint = organizationHint(selected)}
        {@const headerTitle = selectedInstitution?.name || selectedOrgHint?.name || selected.display_name}
        {@const headerStage = relationshipStage(selected)}
        <!-- Header per designs/client/header.html: title + read-only type and stage, one status
             line of facts, the next step when there is one, and Pin + ⋯. No agent voice here. -->
        <div class="lp-hdr">
          <div class="lp-hdr-text">
            <div class="lp-titlerow">
              <div class="page-title">{headerTitle}</div>
              <span class="status-pill status-pill-grey lp-type">{typeLabel(selected)}</span>
              {#if headerStage}<span class="lp-stage" style:--stage={fundStageColor(headerStage)}>{fundStageLabel(headerStage)}</span>{/if}
            </div>
            {#if headerTitle !== selected.display_name && headerTitle !== personLabel(selected)}
              <div class="lp-meta"><span>Contact: {selected.display_name}</span></div>
            {/if}
            {#if headerStatus}
              <div class="lp-status">
                {#if headerStatus.state}<span class="lp-state" style:color={headerStatus.state.color}>{headerStatus.state.icon} {headerStatus.state.label}</span>{/if}
                {#if headerStatus.state?.age}<span>{headerStatus.state.age}</span>{/if}
                {#if headerStatus.last}<span>{headerStatus.last}</span>{/if}
              </div>
            {/if}
            {#if relationshipNextStep(selected)}
              {@const pill = statusPill(selected)}
              <div class="lp-nextstep" title={nextStepAuthor(relationshipInstitution(selected) || selected)}>
                <span class="lbl">Next step</span>
                <span class="val">{relationshipNextStep(selected)}</span>
                {#if relationshipDue(selected)}<span class="due">by {mailDate(relationshipDue(selected))}</span>{/if}
                {#if pill && headerStatus?.state?.label !== 'Overdue'}<span class="status-pill {pill.cls}">{pill.label}</span>{/if}
              </div>
            {/if}
            {#if selectedInstitution && selected.next_step}
              <!-- The institution's record is the live one; this person's own step drives nothing until it is removed or they are ungrouped. -->
              <div class="lp-nextstep" title={nextStepAuthor(selected)}>
                <span class="lbl">Earlier contact step</span>
                <span class="val">{selected.next_step}</span>
                <span class="due">not used while grouped under {selectedInstitution.name}</span>
                <button class="today-mini-btn ghost" disabled={savingFollowUp} onclick={removeEarlierPersonalStep}>Remove</button>
              </div>
            {/if}
          </div>
          <div class="lp-hdr-actions">
            {#if selected.role === 'investor' && !selected.automated_address}
              {#if entityKeyForContact(selected)}
                <button class="today-mini-btn" onclick={() => toggleOrganizationPin(entityKeyForContact(selected)!)} disabled={savingPin} aria-label={isOrganizationPinned(entityKeyForContact(selected)!) ? 'Unpin' : 'Pin'}>{isOrganizationPinned(entityKeyForContact(selected)!) ? '★ Pinned' : '☆ Pin'}</button>
              {:else}
                <button class="today-mini-btn" onclick={() => togglePin(selected.counterparty_id)} disabled={savingPin} aria-label={isPinned(selected.counterparty_id) ? 'Unpin' : 'Pin'}>{isPinned(selected.counterparty_id) ? '★ Pinned' : '☆ Pin'}</button>
              {/if}
            {/if}
            <div class="lp-more">
              <button class="today-mini-btn" aria-haspopup="menu" aria-expanded={moreOpen}
                      aria-label="More relationship settings"
                      onclick={() => moreOpen = !moreOpen}>⋯</button>
              {#if moreOpen}
                <div class="lp-menu" role="menu">
                  <label class="lp-mi">Role
                    <select class="fund-inline-role" aria-label="Relationship type" value={roleChoice(selected)}
                            disabled={!!savingRole || !selected.email}
                            onchange={(event) => setRole(selected, event.currentTarget.value as FundRoleChoice, event.currentTarget)}>
                      {@render unfiledOption(selected)}
                      <option value="investor" disabled={selected.automated_address}>LP</option>
                      <option value="potential_portfolio" disabled={selected.automated_address}>Potential Portfolio</option>
                      <option value="portfolio" disabled={selected.automated_address}>Portfolio</option>
                      <option value="neither">Neither</option>
                    </select>
                  </label>
                  {#if selected.role === 'investor' && selected.email}
                    <button class="lp-mi" disabled={!!savingRole}
                            onclick={() => { moreOpen = false; setRole(selected!, 'neither'); }}>Not an LP</button>
                  {/if}
                  {#if relationshipActions(selected) && !portfolioPage}
                    <label class="lp-mi">Stage
                      <select class="fund-inline-role" aria-label="LP relationship stage"
                              value={relationshipStage(selected) || ''} disabled={savingStage}
                              onchange={(event) => saveStage(selected, event.currentTarget.value, event.currentTarget)}>
                        {#each FUND_STAGES as stage}<option value={stage.key}>{stage.label}</option>{/each}
                      </select>
                    </label>
                  {/if}
                  {#if canAddNextStep(selected) || (selected.role === 'investor' && relationshipNextStep(selected))}
                    <button class="lp-mi" onclick={() => { moreOpen = false; openFollowUpEditor(); }}>{relationshipNextStep(selected) ? 'Edit next step' : 'Add next step'}</button>
                  {/if}
                  {#if selected.role === 'investor' && !selected.automated_address}
                    <button class="lp-mi" onclick={() => { moreOpen = false; organizationEditing = true; }}>Organization</button>
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        </div>
        {#if !selected.role && selected.suggested_source === 'company_investor'}
          <div class="fund-review-strip">
            <span>Company investor · invests in {workspace.profile.manager_name} itself, not in a fund{selected.suggested_reason ? ` · ${selected.suggested_reason}` : ''}</span>
          </div>
        {/if}
        {#if followUpEditing}
          <div class="fund-action-card">
            <div class="fund-compose-heading"><h3>{relationshipNextStep(selected) ? 'Edit next step' : 'Add next step'}{selectedInstitution ? ` · ${selectedInstitution.name}` : ''}</h3><button class="today-mini-btn" onclick={() => followUpEditing = false}>Close</button></div>
            <label class="fund-field">Action<textarea bind:value={nextStepDraft} maxlength="500" rows="2" placeholder="An action you intend to take"></textarea></label>
            <label class="fund-field">Follow up by (optional)<input type="date" bind:value={followUpDateDraft} /></label>
            <button class="btn-primary" onclick={saveFollowUp} disabled={savingFollowUp || (!!followUpDateDraft && !nextStepDraft.trim())}>{savingFollowUp ? 'Saving…' : 'Save next step'}</button>
            {#if relationshipNextStep(selected)}<button class="today-mini-btn ghost" disabled={savingFollowUp} onclick={() => { nextStepDraft = ''; followUpDateDraft = ''; saveFollowUp(); }}>Remove next step</button>{/if}
          </div>
        {/if}
        {#if organizationEditing && selected.role === 'investor' && !selected.automated_address}
          <div class="fund-action-card fund-organization-editor">
            <div class="fund-compose-heading"><h3>Organization · {selected.display_name}</h3><button class="today-mini-btn" onclick={() => organizationEditing = false}>Close</button></div>
            <label class="fund-field">Organization
              <select class="fund-inline-role" value={selected.institution_id || ''} disabled={savingInstitution}
                      onchange={(event) => saveInstitution(event.currentTarget.value || null)}>
                <option value="">Not linked</option>
                {#each workspace.institutions || [] as institution}
                  <option value={institution.institution_id}>{institution.name}</option>
                {/each}
              </select>
            </label>
            <div class="fund-compose-actions">
              <input aria-label="New organization name" bind:value={newInstitutionName} maxlength="160" placeholder="New organization" />
              <button class="today-mini-btn" disabled={savingInstitution || !newInstitutionName.trim()} onclick={createAndAssignInstitution}>{selectedInstitution ? 'Create and move' : 'Create and link'}</button>
            </div>
          </div>
        {/if}
      {/if}
    </div>
    {#if selected}
      {#if selected.automated_address}
        <p class="fund-alert" role="alert">Automated notification address: brief, draft and assistant are off. Add the person's real email in Contacts, or mark it Neither.</p>
      {/if}
      <div class="split-shell fund-detail-shell" bind:this={shellEl}
           class:dragging={mikeDragging} class:collapsed={!showMikeRail}
           style="--fund-mike-w: {mikeW}px">
        <aside class="split-rail fund-mike-rail" aria-label="{agent} chat">
          {#if showMikeRail}
            <div class="fund-mike-head">
              <div class="fund-mike-title"><span aria-hidden="true">✦</span> {agent}</div>
              <div class="fund-mike-actions">
                <button type="button" title="Open {agent} as a tab" aria-label="Open {agent} as a tab" onclick={() => setMikeMode('tab')}>⛶</button>
                <button type="button" title="Minimise {agent} to bubble" aria-label="Minimise {agent} to bubble" onclick={() => setMikeMode('bubble')}>−</button>
              </div>
              <div class="fund-mike-reading">{mikeContextLabel}</div>
            </div>
            {@render assistantRail()}
          {/if}
        </aside>
        <div class="split-divider fund-mike-divider">
          {#if showMikeRail}
            <button type="button" class="split-drag"
                    aria-label="Resize {agent} panel — arrow keys adjust"
                    onpointerdown={resizeMike} onkeydown={resizeMikeKey}></button>
          {/if}
        </div>
        <!-- The briefing and relationship records are the centre of the LP page. -->
        <section class="split-main fund-detail-main">
          <div class="tabs" role="tablist" aria-label="Relationship details">
            <!-- First and default, but only for an actionable LP or a Portfolio contact:
                 there is nothing to brief on otherwise, and the review strip above says why. -->
            {#if briefingAllowed}
              <button class="tab" class:active={detailTab === 'briefing'} role="tab"
                      aria-selected={detailTab === 'briefing'}
                      onclick={() => detailTab = 'briefing'}>{portfolioPage ? 'Portfolio briefing' : 'LP relationship briefing'}</button>
            {/if}
            {#if mikeMode === 'tab'}
              <button class="tab tab-assist" class:active={detailTab === 'assistant'} role="tab"
                      aria-selected={detailTab === 'assistant'} onclick={() => detailTab = 'assistant'}>
                <span class="spark">✦</span> {agent}
              </button>
            {/if}
            <!-- Counts only where the number is a real record count; Memory and Files
                 carry no badge because a zero there is an empty state, not a total. -->
            <button class="tab" class:active={detailTab === 'emails'} role="tab" aria-selected={detailTab === 'emails'} onclick={() => detailTab = 'emails'}>Emails{#if emailThreads.length}{' '}<span class="tab-count">{emailThreads.length}</span>{/if}</button>
            <button class="tab" class:active={detailTab === 'contacts'} role="tab" aria-selected={detailTab === 'contacts'} onclick={() => detailTab = 'contacts'}>Contacts <span class="tab-count">{1 + lpColleagues.length}</span></button>
            <button class="tab" class:active={detailTab === 'memory'} role="tab" aria-selected={detailTab === 'memory'} onclick={() => detailTab = 'memory'}>Memory</button>
            <button class="tab" class:active={detailTab === 'files'} role="tab" aria-selected={detailTab === 'files'} onclick={() => detailTab = 'files'}>Files</button>
          </div>
          {#if actionError}<p class="fund-alert" role="alert">{actionError}</p>{/if}
          {#if detailTab === 'briefing' && portfolioPage && briefingAllowed}
            <PortfolioBriefing
              counterpartyId={selected.counterparty_id}
              companyName={selectedInstitution?.name || organizationHint(selected)?.name || selected.display_name}
              {agent}
              readOnly={isSupportView() && !canUseAs()}
              onOpenSource={openBriefingSource} />
          {:else if detailTab === 'briefing' && !portfolioPage}
            {#if briefingNotice}<p class="fund-hint">{briefingNotice}</p>{/if}
            {#if briefing.research?.enabled && !isSupportView()}
              {@const research = briefing.research}
              <p class="fund-hint" aria-live="polite">
                {researchLine(research)}
                {#if research.status !== 'queued' && research.status !== 'running'}
                  <button type="button" class="today-mini-btn" disabled={briefing.researchBusy}
                          onclick={() => briefing.refreshResearch(research.status === 'needs_identity')}>
                    {research.status === 'needs_identity' ? `Research ${selectedInstitution?.name || selected.display_name} at its own website`
                      : research.status === 'none' ? 'Research public sources' : 'Refresh research'}
                  </button>
                {/if}
              </p>
              {#if coverageLine(research)}<p class="fund-hint">{coverageLine(research)}</p>{/if}
              {#if briefing.researchError}<p class="fund-alert" role="alert">{briefing.researchError}</p>{/if}
            {/if}
            {#if sandboxDemoAvailable && visibleBriefingFixture && !briefing.briefing}
              <p class="fund-hint">{briefingFixtureNotice}</p>
            {/if}
            <BriefingView
              briefing={briefing.briefing ?? visibleBriefingFixture}
              lpName={selectedInstitution?.name || selected.display_name}
              gpName={workspace?.profile.manager_name || ''}
              {agent}
              bind:view={briefingView}
              loading={briefing.loading}
              generating={briefing.generating}
              error={briefing.error}
              lockedBy={briefing.lockedBy}
              readOnly={(isSupportView() && !canUseAs()) || (!!visibleBriefingFixture && !briefing.briefing)}
              saveState={briefing.saveState}
              onReadingChange={(label) => readingLabel = label}
              portfolioCompanies={briefingPortfolioCompanies}
              onOpenPortfolioCompany={openPortfolioCompany}
              onGenerate={isSupportView() && !canUseAs() ? undefined : () => briefing.generate()}
              onSaveSection={(id, content, briefingId) => briefing.save(id, content, briefingId)}
              onRevertSection={(id) => briefing.revert(id)}
              onOpenSource={openBriefingSource}
              onCompare={(id) => { briefingNotice =
                `${agent} has a newer draft of section ${id.toUpperCase()}. Revert the section to read it.`; }}
              onEnterEdit={() => briefing.enterEdit()}
              onLeaveEdit={() => briefing.leaveEdit()} />
          {:else if detailTab === 'assistant'}
            <div class="seg-assist fund-mike-tab-chat">
              <div class="fund-mike-tab-head">
                <span><span aria-hidden="true">✦</span> {agent}</span>
                <button type="button" class="today-mini-btn" onclick={() => setMikeMode('rail')}>Restore to rail</button>
              </div>
              {@render assistantRail()}
            </div>
          {:else if detailTab === 'emails'}
            {#if selected.role === 'neither'}
              <div class="empty-state">Filed as Neither. Earlier notes are in Memory.</div>
            {:else if recordAllowed}
              <div class="fund-email-toolbar">
                {#if relationshipEmails.length}
                  <div class="esearch">
                    <svg class="esearch-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
                    <input aria-label="Search email threads" placeholder="Search email threads…" bind:value={emailQuery} />
                    {#if emailQuery}<button type="button" class="eq-x" aria-label="Clear email search" onclick={() => emailQuery = ''}>×</button>{/if}
                  </div>
                {/if}
                {#if refreshingThreads || contactContext?.messages.some(m => !m.thread_id && m.owner_email.toLowerCase() === $userEmailStore.toLowerCase())}
                  <button type="button" class="today-mini-btn" disabled={refreshingThreads}
                    onclick={refreshOlderThreads}
                    title="Group older messages from your mailbox into their original conversations">
                    {refreshingThreads ? 'Grouping emails…' : 'Group emails'}
                  </button>
                {/if}
                {#if !composeOpen && relationshipActions(selected)}<button class="btn-newemail" onclick={startFundEmail}>+ New email</button>{/if}
              </div>
              {#if selectedInstitution && relationshipEmails.length}
                <p class="fund-email-scope">Showing {institutionBriefMembers.length} contact{institutionBriefMembers.length === 1 ? '' : 's'} at {selectedInstitution.name}</p>
              {/if}
              {#if sendNotice}<div class="fund-send-notice" role="status">{sendNotice}</div>{/if}
              {#if composeOpen && !replyThreadAvailable}{@render fundComposeArea()}{/if}
              {#if selectedInstitution && institutionActivityLoading}<div class="segment-loading">Loading institution emails…</div>
              {:else if selectedInstitution && institutionActivityError}<div class="empty-state">Could not load institution emails: {institutionActivityError}</div>
              {:else if contextLoading && !selectedInstitution}<div class="segment-loading">Loading synced emails…</div>
              {:else if relationshipEmails.length}
                <div class="fund-message-list">
                  {#each orderedEmailThreads as thread, index (thread.key)}
                    {#if index === 0 || (!!replyDecisionForThread(thread) !== !!replyDecisionForThread(orderedEmailThreads[index - 1]))}
                      <h3 class="fund-email-group-heading">{replyDecisionForThread(thread) ? 'Needs reply' : 'All threads'}</h3>
                    {/if}
                    <article class="ethread" class:open={expandedEmailThreads.has(thread.key)} id="fund-thread-{thread.key}">
                      <div class="et-head">
                        <button class="et-toggle" onclick={() => toggleEmailThread(thread)}>
                        <span class="et-caret" class:open={expandedEmailThreads.has(thread.key)}>▸</span>
                        <span class="et-main"><span class="et-subj" title={thread.subject}>{#if replyDecisionForThread(thread)}<span class="fund-reply-dot" aria-hidden="true"></span>{/if}<span class="fund-thread-subject-text">{thread.subject}</span></span><span class="et-meta">{#if replyDecisionForThread(thread)}<span class="fund-reply-owed">Reply owed · {replyDecisionForThread(thread)?.age_days} day{replyDecisionForThread(thread)?.age_days === 1 ? '' : 's'}</span> ·{' '}{/if}{thread.messages.map(m => m.display_name).filter((name, index, names) => names.indexOf(name) === index).join(', ')} · {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</span></span>
                        <span class="et-date">{emailListDate(thread.sent_at)}</span>
                        </button>
                      </div>
                      {#if expandedEmailThreads.has(thread.key)}
                        {@const replyTarget = threadReplyTarget(thread)}
                        <div class="et-body">
                          {#each thread.messages as message (`${message.counterparty_id}:${message.owner_email}:${message.message_id}`)}
                            {@const key = `${message.counterparty_id}:${message.owner_email}:${message.message_id}`}
                            {@const direction = messageDirection(message)}
                            <div class="emsg" id="fund-msg-{key}" class:out={direction === 'outbound'} class:mopen={openedMessages.has(key)}>
                              <button class="em-h" onclick={() => toggleMessage(message.counterparty_id, message.owner_email, message.message_id)}>
                                <span class="em-caret" class:open={openedMessages.has(key)}>▸</span>
<b title={message.sender}>{senderName(message.sender)}</b>
                                <!-- In the institution view a thread can belong to another member.
                                     Name them only then; on their own page it is the page's title. -->
                                {#if message.counterparty_id !== selected.counterparty_id}<span class="em-who">on {message.display_name}&rsquo;s thread</span>{/if}
                                <span>{emailListDate(message.sent_at)}{direction ? ` · ${direction}` : ''}</span>
                              </button>
                              {#if openedMessages.has(key)}
                                {#if messageRecipients[key]?.length}
                                  <div class="em-rcpt">
                                    {#each messageRecipients[key] as row (row.label)}
                                      {@const all = expandedRecipients.has(`${key}:${row.label}`)}
                                      <div><span class="em-rcpt-k">{row.label}</span>{#each all ? row.people : row.people.slice(0, 6) as person, i}{i ? ', ' : ''}<span title={person.email}>{person.name || person.email}</span>{/each}{#if !all && row.people.length > 6}
                                        <button class="em-rcpt-more" onclick={() => expandedRecipients = new Set(expandedRecipients).add(`${key}:${row.label}`)}>+{row.people.length - 6} more</button>{/if}</div>
                                    {/each}
                                  </div>
                                {/if}
                                {#if messageErrors[key]}<p class="fund-alert">{messageErrors[key]} <button class="today-mini-btn" onclick={() => toggleMessage(message.counterparty_id, message.owner_email, message.message_id)}>Retry</button></p>
                                {:else if messageBodies[key]}<div class="em-b md-content">{@html renderSafeMarkdown(unwrapHardBreaks(repairLostApostrophes(stripCids(messageBodies[key]))))}</div>
                                {:else}<span class="spinner"></span> Loading full message…{/if}
                                {#if message.counterparty_id !== selected.counterparty_id}
                                  <button class="today-mini-btn" onclick={() => { const person = allContacts.find(c => c.counterparty_id === message.counterparty_id); if (person) openContact(person); }}>Open {message.display_name} to reply</button>
                                {/if}
                              {:else if messageSnippet(message.excerpt)}
                                <div class="em-snip">{messageSnippet(message.excerpt)}</div>
                              {/if}
                            </div>
                          {/each}
                          {#if relationshipActions(selected) && replyTarget && !(composeOpen && composeReplyMessageId === replyTarget.message_id)}
                            <div class="fund-thread-actions">
                              <button type="button" class="today-mini-btn ai-tool" disabled={!canGenerateDraft || !!generating} onclick={() => draftReply(replyTarget)}><span class="ai-spark" aria-hidden="true">✦</span> Draft reply</button>
                              <button type="button" class="today-mini-btn" onclick={() => startReply(replyTarget)}>Reply</button>
                            </div>
                          {/if}
                        </div>
                      {/if}
                      {#if composeOpen && composeReplyMessageId && thread.messages.some(message => message.message_id === composeReplyMessageId && message.owner_email.toLowerCase() === $userEmailStore.toLowerCase())}
                        {@render fundComposeArea()}
                      {/if}
                    </article>
                  {/each}
                </div>
                {#if !visibleEmailThreads.length}<div class="eno">No threads match “{emailQuery}”.</div>{/if}
              {:else if contextError && !contactContext}<div class="empty-state">Could not load synced email: {contextError} <button class="today-mini-btn" onclick={() => reloadContext(selected.counterparty_id)}>Retry</button></div>
              {:else}
                <div class="empty-state fund-email-empty">
                  {#if portfolioPage && !selected.email}
                    <p class="fund-email-empty-title">No email address is linked to this Portfolio company.</p>
                    <p>Add or link a person in Contacts to show the company&rsquo;s email history and enable replies.</p>
                    <button type="button" class="today-mini-btn fund-navlink" onclick={() => $currentView = 'contacts'}>Open Contacts →</button>
                  {:else}
                    <p>No synced email for this {selected.role === 'investor' ? 'LP' : 'contact'} yet.</p>
                    <SyncButton />
                  {/if}
                </div>
              {/if}
              {#if sentReceipts.length}
                <div class="fund-record-subhead">Sent from your mailbox</div>
                {#each sentReceipts as receipt}<div class="fund-sent-row"><b>{receipt.subject}</b><span>{receipt.status === 'sent' ? 'Sent' : 'Check provider Sent folder'} · {mailDate(receipt.created_at)}</span></div>{/each}
              {/if}
              {#if emailTracking.length}
                <div class="fund-record-subhead">Open tracking</div>
                {#each emailTracking as item}
                  <div class="fund-sent-row"><b>{item.subject}</b><span>{item.opens} image loads · {item.last_opened_at ? mailDate(item.last_opened_at) : 'No load recorded'}</span></div>
                {/each}
              {/if}
              {#if scheduledSends.length}
                <div class="fund-record-subhead">Scheduled from your mailbox</div>
                {#each scheduledSends as scheduled}
                  <div class="fund-sent-row"><b>{scheduled.subject}</b><span>{scheduled.status} · {new Date(scheduled.send_at).toLocaleString()}</span>{#if scheduled.status === 'pending'}<button class="today-mini-btn" onclick={() => cancelScheduled(scheduled.request_id)}>Cancel</button>{/if}</div>
                {/each}
              {/if}
            {:else if selected.automated_address}<div class="empty-state">Automated address. Use the person's direct email.</div>
            {:else}<div class="fund-unclassified"><h3>Not an LP or Portfolio contact</h3><p>Mail is hidden. To change the role, use ⋯.</p>{@render recentSubjects()}</div>{/if}
            {#snippet recentSubjects()}{#if selected?.sample_subjects?.length}<div class="fund-record-subhead">Recent email subjects</div><ul>{#each selected.sample_subjects as subject}<li>{subject}</li>{/each}</ul>{/if}{/snippet}
          {:else if detailTab === 'contacts'}
            <!-- As on the Sales client page a row expands to that person's threads; the organization is the title, provenance is in Memory. -->
            {#each [selected, ...lpColleagues.map(entry => entry.contact)] as person (person.counterparty_id)}
              {@const own = person.counterparty_id === selected.counterparty_id}
              {@const mail = peopleMailShown ? personMail(person) : null}
              {@const open = !!mail && expandedPerson === person.counterparty_id}
              <div class="fund-crow" id="fund-person-{person.counterparty_id}">
                <div class="fund-chead">
                  {#if mail}
                    <button type="button" class="et-toggle" aria-expanded={open} onclick={() => expandedPerson = open ? '' : person.counterparty_id}>
                      <span class="et-caret" class:open>▸</span>{@render personHead(person, own, mail)}
                    </button>
                  {:else}<div class="fund-chead-static">{@render personHead(person, own, null)}</div>{/if}
                  {#if !own}<button type="button" class="today-mini-btn" aria-label={`Open ${person.display_name}`} onclick={() => openContact(person)}>Open →</button>{/if}
                </div>
                {#if open && mail}
                  <div class="fund-cbody">
                    {#if mail.state === 'loading'}<div class="segment-loading">Loading emails…</div>
                    {:else if mail.state === 'error'}<p class="fund-muted">Could not load emails: {mail.error}</p>
                    {:else if mail.state === 'elsewhere'}<p class="fund-muted">{person.display_name}&rsquo;s threads are on their own page.</p>
                    {:else}
                      {#each mail.threads as thread (thread.key)}
                        {@const latest = thread.messages.find(message => message.counterparty_id === person.counterparty_id)}
                        <div class="fund-ci">
                          <span class="fund-tlc" aria-hidden="true">✉</span>
                          <div class="fund-tlbody">
                            <div class="fund-tlhead"><span class="fund-tlt" title={thread.subject}>{thread.subject}</span><span class="fund-tld">{emailListDate(thread.sent_at)} · {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</span></div>
                            {#if messageSnippet(latest?.excerpt)}<div class="fund-tlx">{messageSnippet(latest?.excerpt)}</div>{/if}
                            <div class="fund-tlacts"><button type="button" class="today-mini-btn" onclick={() => openThreadInEmails(thread)}>View in Emails →</button></div>
                          </div>
                        </div>
                      {:else}<p class="fund-muted">No recent synced email with {person.display_name}.</p>{/each}
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          {:else if detailTab === 'memory'}
            <!-- One record block, then one memory list (Sales' Memory: who wrote an entry is only its tag). -->
            {#if lpRecord.length}
              <div class="fund-record-subhead">Record</div>
              <dl class="lp-record">
                {#each lpRecord as row (row.label)}
                  <dt>{row.label}</dt>
                  <dd>{row.value}{#each row.lines || [] as line}<span class="lp-record-line">{line}</span>{/each}{#if row.detail}<span class="lp-record-detail">{row.detail}</span>{/if}</dd>
                {/each}
              </dl>
            {/if}
            {#if recordAllowed}
              <div class="lp-sec lp-sec-row">
                <span class="fund-record-subhead">Memory <span class="tab-count">{memoryNotes.length}</span></span>
                {#if selected.role !== 'neither'}<button class="today-mini-btn" onclick={() => startNote()}>+ Add note</button>{/if}
              </div>
              {#if noteEditorOpen}
                <div class="card fund-note-editor">
                  <label class="fund-field">Title<input bind:value={noteTitle} maxlength="160" placeholder="Question, commitment, or meeting context" /></label>
                  <label class="fund-field">Note<textarea bind:value={noteBody} rows="5" maxlength="10000" placeholder="Record what you know, and where it came from."></textarea></label>
                  <div class="fund-compose-actions"><button class="btn-primary" onclick={saveNote} disabled={noteSaving || !noteTitle.trim() || !noteBody.trim()}>{noteSaving ? 'Saving…' : 'Save note'}</button><button class="today-mini-btn" onclick={() => noteEditorOpen = false} disabled={noteSaving}>Cancel</button></div>
                </div>
              {/if}
              {#each memoryNotes as note (note.note_id)}
                {@const mine = note.author_email.toLowerCase() === $userEmailStore.toLowerCase()}
                <div class="ethread" class:open={openNoteId === note.note_id}>
                  <div class="et-head">
                    <button type="button" class="et-toggle" aria-expanded={openNoteId === note.note_id}
                            onclick={() => openNoteId = openNoteId === note.note_id ? '' : note.note_id}>
                      <span class="et-caret" class:open={openNoteId === note.note_id}>▸</span>
                      <span class="et-main">
                        <span class="et-subj">{note.title}</span>
                        <span class="et-meta">{#if note.fromChat}<span class="fund-ir-sparkle" aria-hidden="true">✦</span> Saved from chat{:else}Note{/if} · {mine ? 'You' : note.author_email} · {mailDate(note.updated_at)}</span>
                      </span>
                    </button>
                    {#if mine && selected.role !== 'neither'}<button class="today-mini-btn" onclick={() => startNote(note)}>Edit</button>{/if}
                  </div>
                  {#if openNoteId === note.note_id}<div class="et-body md-content">{@html renderSafeMarkdown(note.text)}</div>{/if}
                </div>
              {:else}
                <p class="lp-foot">No notes yet. Add one, or use Save to memory in the chat.</p>
              {/each}
              <!-- "Prepare brief" on Today (Calls, Chase) lands here with this open; it is the brief's only renderer. -->
              {#if briefingAllowed && (contactContext?.outputs.brief || contactContext?.outputs.draft)}
                <details class="lp-generated lp-sec" bind:open={generatedOpen}>
                  <summary class="fund-record-subhead"><span class="et-caret" class:open={generatedOpen}>▸</span><span class="fund-ir-sparkle" aria-hidden="true">✦</span> Generated by {agent}</summary>
                  {#if contactContext?.outputs.brief}<section class="fund-output-card"><header class="fund-output-header"><span class="fund-output-mark">✦</span><div><h3>Relationship brief</h3><p>For your review</p></div></header><div class="fund-output-body md-content">{@html renderSafeMarkdown(contactContext.outputs.brief)}</div>{@render outputSources('brief')}</section>{/if}
                  {#if contactContext?.outputs.draft}<section class="fund-output-card"><header class="fund-output-header"><span class="fund-output-mark">✦</span><div><h3>Last generated email</h3><p>For your review</p></div></header><div class="fund-output-body md-content">{@html renderSafeMarkdown(contactContext.outputs.draft)}</div>{@render outputSources('draft')}<div class="fund-output-footer"><button class="today-mini-btn" onclick={() => useDraftResult(contactContext?.outputs.draft || '')}>Use as editable email</button></div></section>{/if}
                </details>
              {/if}
            {:else}<div class="empty-state">Team notes are kept for LP and Portfolio contacts.</div>{/if}
          {:else if detailTab === 'files'}
            <!-- Sales' Files tab: what was attached to mail with this contact, both directions. -->
            <div class="fund-record-subhead">Shared with this {portfolioPage ? 'contact' : 'LP'} <span class="tab-count">{contactFiles.length}</span></div>
            {#if contactFilesLoading}
              <div class="segment-loading"><span class="spinner"></span> Loading files…</div>
            {:else if contactFilesError}
              <p class="fund-alert" role="alert">{contactFilesError}</p>
            {:else if contactFiles.length}
              {#each contactFiles as file (file.attachment_id)}
                {@render fileRow(`a:${file.attachment_id}`, file.filename, file.filename,
                  `${file.direction === 'them' ? `From ${senderName(file.sender) || 'them'}` : 'From your team'} · ${mailDate(file.sent_at)} · ${formatFileSize(file.size_bytes)} · ${file.subject || '(no subject)'}`,
                  fundContactFileUrl(selected.counterparty_id, file.attachment_id),
                  fundContactFilePreviewUrl(selected.counterparty_id, file.attachment_id),
                  file.readable ? file.excerpt : '')}
              {/each}
            {:else}
              <div class="empty-state">No files on synced mail with this {portfolioPage ? 'contact' : 'LP'} yet.</div>
            {/if}
            <div class="fund-record-subhead" style="margin-top:26px">Firm library <span class="tab-count">{workspace.documents.length}</span></div>
            {#each workspace.documents as doc (doc.document_id)}
              {@render fileRow(`d:${doc.document_id}`, doc.title, doc.filename,
                `${doc.filename} · ${doc.category === 'fund' ? 'Fund' : 'Collateral'}`,
                fundDocumentUrl(doc.document_id), fundDocumentPreviewUrl(doc.document_id), '')}
            {:else}<div class="empty-state">No Fund files uploaded yet. Add them in Teach {agent.split(' ')[0]}.</div>{/each}
          {/if}
        </section>
      </div>
      {#if showMikeBubble}
        <button type="button" class="fund-mike-bubble" aria-label="Open {agent} chat"
                title="Open {agent} chat" onclick={openMikeBubble}>✦</button>
      {/if}
      {#if mikeOverlayOpen && mikeNarrow}
        <div class="fund-mike-overlay-backdrop" onclick={() => mikeOverlayOpen = false} role="presentation"></div>
        <aside class="fund-mike-overlay" aria-label="{agent} chat">
          <div class="fund-mike-head">
            <div class="fund-mike-title"><span aria-hidden="true">✦</span> {agent}</div>
            <div class="fund-mike-actions">
              <button type="button" title="Open {agent} as a tab" aria-label="Open {agent} as a tab" onclick={() => setMikeMode('tab')}>⛶</button>
              <button type="button" title="Close {agent} overlay" aria-label="Close {agent} overlay" onclick={() => mikeOverlayOpen = false}>×</button>
            </div>
            <div class="fund-mike-reading">{mikeContextLabel}</div>
          </div>
          {@render assistantRail()}
        </aside>
      {/if}
    {:else}<div class="empty-state">This contact is unavailable.</div>{/if}
  {/if}
{:else if error}
  <div class="empty-state" role="alert">Could not load your workspace. {error} <button type="button" class="today-mini-btn" onclick={load}>Retry</button></div>
{/if}

<!-- Sales' Files row (ClientView segDocs): expand to preview a PDF; other files show the text the assistant reads. -->
{#snippet fileRow(key: string, title: string, filename: string, meta: string, downloadUrl: string, previewUrl: string, text: string)}
  <div class="ethread" class:open={openFileKey === key}>
    <div class="et-head">
      <button type="button" class="et-toggle" aria-expanded={openFileKey === key} onclick={() => toggleFilePreview(key, previewUrl, filename)}>
        <span class="et-caret" class:open={openFileKey === key}>▸</span>
        <span class="doc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d={fileIconPath(filename)}/></svg></span>
        <span class="et-main"><span class="et-subj">{title}</span><span class="et-meta">{meta}</span></span>
      </button>
      <a class="today-mini-btn" href={downloadUrl} download={filename} onclick={(e) => void downloadSupportFile(e, downloadUrl, filename).catch((err) => alert(err.message))}>Download</a>
    </div>
    {#if openFileKey === key}
      <div class="et-body">
        {#if isPdf(filename)}
          {#if filePreviewUrl}
            <iframe class="fund-file-preview" src={filePreviewUrl + '#toolbar=1&navpanes=0&view=FitH'} title="Preview of {filename}"></iframe>
          {:else}<p class="fund-muted">{filePreviewError ? 'Preview unavailable — use Download.' : 'Loading preview…'}</p>{/if}
          {#if text}<details class="fund-file-text"><summary>Text {agent} reads</summary><pre class="fund-file-excerpt">{text}</pre></details>{/if}
        {:else if text}
          <p class="fund-muted">Text {agent} reads from this file:</p>
          <pre class="fund-file-excerpt">{text}</pre>
        {:else}
          <p class="fund-muted">Preview not available — download to view.</p>
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

<!-- One LP conversation, rendered in the right rail, full tab or narrow overlay. -->
{#snippet outputSources(kind: 'brief' | 'draft')}
  {#if contactContext?.output_provenance?.[kind]}
    {@const record = contactContext.output_provenance[kind]}
    <details class="fund-output-footer">
      <summary>Sources used for this generation</summary>
      <p class="fund-muted">{record.generated_at ? mailDate(record.generated_at) : 'Date unavailable'} · Requested model {record.model_id} · {record.prompt_version}</p>
      {#each record.evidence || [] as source}
        <div><button class="fund-row-link" onclick={() => { detailTab = 'emails'; if (selected) toggleMessage(selected.counterparty_id, source.owner_email, source.message_id); }}>Email {source.number}: {source.subject || '(No subject)'}</button><span class="fund-hint"> · {mailDate(source.sent_at)} · {source.owner_email}</span></div>
      {/each}
      {#each record.notes || [] as source}<div class="fund-muted">Team note {source.number}: {source.title} · Updated {mailDate(source.updated_at)}</div>{/each}
      {#each record.files || [] as source}<div class="fund-muted">File {source.number}: {source.filename}</div>{/each}
      {#each contactContext?.output_sources?.[kind] || [] as source}<div><a href={fundDocumentUrl(source.document_id)} download={source.filename} onclick={(e) => void downloadSupportFile(e, fundDocumentUrl(source.document_id), source.filename).catch((err) => alert(err.message))}>Document {source.number}: {source.title} ({source.filename})</a></div>{/each}
    </details>
  {:else if contactContext?.output_sources?.[kind]?.length}
    <div class="fund-output-footer"><span class="fund-muted">Files provided for generation:</span>{#each contactContext.output_sources[kind] || [] as source}<a href={fundDocumentUrl(source.document_id)} download={source.filename} onclick={(e) => void downloadSupportFile(e, fundDocumentUrl(source.document_id), source.filename).catch((err) => alert(err.message))}>Document {source.number}: {source.title} ({source.filename})</a>{/each}</div>
  {/if}
{/snippet}

{#snippet generationSources()}
  {#if workspace?.documents.length}
    <details class="fund-generation-sources">
      <summary>Fund files for AI generation {groundingDocumentIds.length ? `(${groundingDocumentIds.length} selected)` : '(optional)'}</summary>
      <p class="fund-muted">Only ticked files are sent to Anthropic, for this generation.</p>
      {#if groundableDocs.length}
        {#each groundableDocs as doc (doc.document_id)}
          <label class="fund-field"><input type="checkbox" value={doc.document_id} bind:group={groundingDocumentIds}
            disabled={groundingDocumentIds.length >= 3 && !groundingDocumentIds.includes(doc.document_id)} />
            {doc.title} · {doc.filename}</label>
        {/each}
      {:else}
        <p class="fund-muted">No PDF, DOCX, TXT, MD or CSV files under 5 MB.</p>
      {/if}
    </details>
  {/if}
{/snippet}

{#snippet assistantRail()}
  {#if messages.length > 0}
    <div class="rail-util">
      <button type="button" class="rail-restart" onclick={restartConversation} title="Clear this conversation and start over">↺ Restart</button>
    </div>
  {/if}

  <div class="chat-messages">
    {#if selected?.automated_address}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">A direct email address is needed</div>
        <div class="rail-starter-s">Automated address. Add the person's real email in Contacts first.</div>
      </div>
    {:else if selected && portfolioPage && !selected.email}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">A contact email address is needed</div>
        <div class="rail-starter-s">No person's email on this company, so there is no mail to answer from.</div>
      </div>
    {:else if !canAskAssistant}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">{agent} works on LP and Portfolio relationships</div>
        <div class="rail-starter-s">File this contact as LP or Portfolio first.</div>
      </div>
    {:else if messages.length === 0 && !chatLoading}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">Ask {agent} about {selectedName}</div>
        <div class="rail-starter-s">Nothing is sent.</div>
      </div>
    {/if}
    {#each messages as msg}
      {#if msg.role === 'assistant'}
        <div class="gmsg">
          <div class="gmsg-body">
            <div class="md-content">{@html renderSafeMarkdown(msg.content)}</div>
          </div>
        </div>
      {:else}
        <div class="chat-msg user">{msg.content}</div>
      {/if}
    {/each}
    {#if chatLoading}
      <div class="chat-msg assistant" style="display:flex; align-items:center; gap:8px;">
        <span class="spinner"></span> <span class="text-muted">Thinking…</span>
      </div>
    {/if}
    {#if chatError}<p class="fund-alert" role="alert">{chatError}</p>{/if}
    {#if memoryNotice}<p class="fund-muted" role="status">{memoryNotice}</p>{/if}
  </div>

  {#if canAskAssistant}
    <div class="ask-suggest">
      {#if messages.some(m => m.role === 'assistant')}
        <button class="sug-chip" disabled={memorySaving || chatLoading} onclick={saveChatToMemory}>{memorySaving ? 'Saving…' : 'Save to memory'}</button>
      {/if}
      <button class="sug-chip" onclick={() => quickAsk(`What should my next touchpoint with this ${portfolioPage ? 'company' : 'LP'} be?`)}>Plan the next move</button>
      <button class="sug-chip" onclick={() => quickAsk(`What has this ${portfolioPage ? 'company' : 'LP'} asked for that is still open?`)}>What is still open?</button>
      <button class="sug-chip" onclick={() => quickAsk('What changed in this relationship recently?')}>What changed recently?</button>
    </div>
    <div class="strat-ask">
      <textarea
        bind:value={chatInput}
        placeholder="Ask about {selectedName}, or plan the next move…"
        class="flex-1"
        rows="2"
        style="resize: vertical; min-height: 2.4em; max-height: 12em; font-family: inherit;"
        onkeydown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleChat();
          }
        }}
      ></textarea>
      <button class="primary" onclick={handleChat} disabled={!chatInput.trim() || chatLoading}>Send</button>
    </div>
  {/if}
{/snippet}

<!-- A placeholder only when no real choice matches; otherwise it repeated "Potential Portfolio". -->
{#snippet unfiledOption(contact: FundContact)}
  {@const choice = roleChoice(contact)}
  {#if choice === '' || choice === 'unknown'}<option value={choice} disabled>{unfiledLabel(contact)}</option>{/if}
{/snippet}

<!-- Sales Contacts' row: Open · Edit · ×, and Edit flips the row into an inline editor. -->
{#snippet fundContactRow(contact: FundContact, grouped: boolean)}
  {#if editingContactId === contact.counterparty_id}
    <tr class="fund-contact-edit-row"><td colspan="6">
      <form class="fund-contact-edit" onsubmit={(event) => { event.preventDefault(); saveContactEdit(contact); }}>
        <label class="fund-field">Name<input bind:value={editName} maxlength="200" required /></label>
        <label class="fund-field">Relationship
          <select bind:value={editRole} disabled={!contact.email}>
            {@render unfiledOption(contact)}<option value="investor" disabled={contact.automated_address}>{typeLabel(contact) === 'Potential LP' ? 'Potential LP' : 'LP'}</option><option value="potential_portfolio" disabled={contact.automated_address}>Potential Portfolio</option><option value="portfolio" disabled={contact.automated_address}>Portfolio</option><option value="neither">Neither</option>
          </select>
        </label>
        <div class="fund-compose-actions"><button class="btn-primary" type="submit" disabled={savingContactEdit || !editName.trim()}>{savingContactEdit ? 'Saving…' : 'Save'}</button><button type="button" class="today-mini-btn" onclick={() => editingContactId = ''} disabled={savingContactEdit}>Cancel</button></div>
      </form>
    </td></tr>
  {:else}
    <tr class="data-row fund-contact-row fund-contact-member">
      <td></td>
      <td><div class="fund-contact-cell">
        <span class="fund-contact-avatar" style:background={avatarColor(contact.email || contact.display_name)}>{initialsFor(contact.display_name, contact.email || '')}</span>
        <div class="fund-contact-meta"><div class="fund-contact-name">{contact.display_name}</div><div class="fund-contact-sub">{typeLabel(contact)}</div></div>
      </div></td>
      <td class="fund-contacts-email">{contact.email || '—'}</td>
      <td class="fund-contacts-date">{contact.last_seen ? mailDate(contact.last_seen) : '—'}</td>
      <td class="num fund-contacts-activity">{activityAgo(contact.last_seen)}</td>
      <td class="fund-contacts-actions"><div class="fund-row-actions">
        <button type="button" class="today-mini-btn" onclick={() => openDirectoryContact(contact, grouped)}>Open</button>
        <button type="button" class="today-mini-btn" onclick={() => startContactEdit(contact)}>Edit</button>
        <button type="button" class="today-mini-btn fund-remove-btn" aria-label={`Remove ${contact.display_name} from Contacts`} title="Mark Neither"
                disabled={!contact.email || !!savingRole} onclick={() => removeFromContacts(contact)}>×</button>
      </div></td>
    </tr>
  {/if}
{/snippet}

{#snippet personHead(person: FundContact, own: boolean, mail: ReturnType<typeof personMail> | null)}
  <span class="fund-ppl-av" style="background:{avatarColor(person.email || person.counterparty_id)}">{initialsFor(person.display_name, person.email || '')}</span>
  <span class="fund-cinfo">
    <span class="fund-rname">{person.display_name}</span>
    <!-- A domain is a display hint, never a confirmed institution, so a domain colleague says so. -->
    <span class="fund-rsub">{person.email || 'No email address'}{#if !own && !person.institution_id && !selected?.institution_id}{' '}· Same email domain{/if}</span>
  </span>
  <span class="fund-cstat">{#if mail?.state === 'ready'}{mail.threads.length} email{mail.threads.length === 1 ? '' : 's'} ·{' '}{/if}Last email {mailDate(person.last_seen, 'not recorded')}</span>
{/snippet}

{#snippet fundComposeArea()}
              {#if isSupportView() && !canUseAs()}
                <p class="fund-hint">This account has not accepted staff Use as actions.</p>
              {:else}
                <div class="compose-area fund-compose">
                  <div class="fund-compose-heading"><b>{isSupportView() ? 'Review-only draft' : composeReplyMessageId ? 'Reply in thread' : 'New email'}</b><button class="today-mini-btn" onclick={closeFundEmail} disabled={sending}>Close</button></div>
                  {#if !composeReplyMessageId && selectedInstitution && institutionBriefMembers.length > 1}
                    <label class="fund-field fund-email-recipient">To
                      <select aria-label="New email recipient" value={selected?.counterparty_id || ''}
                        onchange={(event) => { if (!chooseNewEmailRecipient(event.currentTarget.value)) event.currentTarget.value = selected?.counterparty_id || ''; }}>
                        {#each institutionBriefMembers as member (member.counterparty_id)}
                          <option value={member.counterparty_id}>{member.display_name} · {member.email}</option>
                        {/each}
                      </select>
                    </label>
                  {/if}
                  <p class="fund-muted">{isSupportView() ? 'Saved only in Dealton' : 'From your connected mailbox'}{#if composeReplyMessageId || !selectedInstitution || institutionBriefMembers.length <= 1}{' '}· To <b>{selected?.email || '—'}</b>{/if}{isSupportView() ? '.' : '. Read and edit before sending.'}</p>
                  <div class="fund-compose-generate">
                    <input aria-label="Draft instructions (optional)" bind:value={draftInstruction} maxlength="1000" placeholder="Any extra instructions (optional)" />
                    <button class="primary" disabled={!!generating || !canGenerateDraft} onclick={generateComposeDraft}>{generating === 'draft' ? 'Generating…' : composeReplyMessageId ? 'Generate reply' : 'Generate draft'}</button>
                  </div>
                  {#if !canGenerateDraft && !contextLoading}<p class="fund-hint">Drafting needs a synced email or a team note.</p>{/if}
                  {@render generationSources()}
                  <!-- A reply keeps the thread subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one.
                       Same lock as the Sales composers on an existing thread. -->
                  <!-- The space lives inside the span; Svelte drops it at the start of an if-block. -->
                  <label class="fund-field">Subject{#if composeReplyMessageId}<span class="fund-muted">&nbsp;(locked to this thread)</span>{/if}
                    <input bind:value={composeSubject} maxlength="250" placeholder="Email subject"
                           readonly={!!composeReplyMessageId}
                           title={composeReplyMessageId ? 'Subject stays fixed while replying in an existing thread' : ''} /></label>
                  {#if !isSupportView()}
                    <label class="fund-field">CC (optional)<input bind:value={composeCc} maxlength="1000" placeholder="name@example.com, colleague@example.com" /></label>
                  {#if ccSuggestions.length}
                    <!-- Known addresses for this LP and the colleagues your team already has
                         mail with. Appends to the CC field; it never replaces what you typed. -->
                    {#if ccLpContacts.length}
                      <div class="fund-cc-picks">
                        <span class="fund-cc-label">CC &mdash; LP contacts</span>
                        {#each ccLpContacts as address}
                          <button type="button" class="fchip" disabled={ccAlreadyIncluded(address)}
                                  onclick={() => addCc(address)}>{address}</button>
                        {/each}
                      </div>
                    {/if}
                    {#if ccTeam.length}
                      <div class="fund-cc-picks">
                        <span class="fund-cc-label">CC &mdash; your team</span>
                        {#each ccTeam as address}
                          <button type="button" class="fchip" disabled={ccAlreadyIncluded(address)}
                                  onclick={() => addCc(address)}>{address}</button>
                        {/each}
                      </div>
                    {/if}
                  {/if}
                  {/if}
                  <label class="fund-field">Message<EmailBodyEditor bind:value={composeBody} rows={12} placeholder="Write your email…" /></label>
                  {#if polishChanges}
                    <div class="polish-changes mb-3" role="status">
                      <div class="text-xs font-bold mb-1">Changes made:</div>
                      <pre class="text-xs">{polishChanges}</pre>
                    </div>
                  {/if}
                  {#if !isSupportView()}
                  {#if workspace?.documents.length}
                    <div class="text-xs text-dim">Attachments &mdash; firm library</div>
                    <div class="chip-row mb-2">
                      {#each workspace?.documents || [] as doc (doc.document_id)}
                        <button type="button" class="chip attach" class:selected={composeDocumentIds.includes(doc.document_id)}
                                title={doc.title} onclick={() => toggleComposeDocument(doc.document_id)}>
                          <svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.filename}
                        </button>
                      {/each}
                    </div>
                  {/if}
                  <div class="text-xs text-dim">Files for this email only</div>
                  <div class="chip-row mb-2">
                    {#each composeFiles as file, index (`${file.name}:${file.size}:${index}`)}
                      <span class="chip selected fund-file-chip">{file.name}
                        <button type="button" aria-label="Remove {file.name}" onclick={() => removeComposeFile(index)}>&times;</button>
                      </span>
                    {/each}
                    <label class="today-mini-btn fund-attach-btn">+ Attach file
                      <input type="file" multiple class="sr-only" onchange={addComposeFiles} />
                    </label>
                  </div>
                  <p class="fund-hint">Up to 5 files, 10 MB total.</p>
                  <label class="fund-field"><input type="checkbox" bind:checked={composeTracking} /> Track email opens (optional)</label>
                  <p class="fund-hint">Open counts are approximate.</p>
                  <label class="fund-field"><input type="checkbox" bind:checked={composeMeeting} /> Include a calendar invite</label>
                  {#if composeMeeting}
                    <label class="fund-field">Meeting title<input bind:value={meetingSummary} maxlength="160" /></label>
                    <label class="fund-field">Start<input type="datetime-local" bind:value={meetingStart} /></label>
                    <label class="fund-field">End<input type="datetime-local" bind:value={meetingEnd} /></label>
                    <label class="fund-field">Location (optional)<input bind:value={meetingLocation} maxlength="200" /></label>
                  {/if}
                  {/if}
                  <div class="draft-actions fund-compose-commit">
                    {#if !isSupportView()}
                      <button class="btn-primary" onclick={sendReviewedEmail} disabled={sending || !composeSubject.trim() || !composeBody.trim()}>{sending ? 'Working…' : 'Send email'}</button>
                      <button class="today-mini-btn" onclick={saveEmailToDrafts} disabled={sending || !composeSubject.trim() || !composeBody.trim() || composeMeeting || composeTracking} title={composeMeeting || composeTracking ? 'Drafts support message and attachments; send or schedule to include invite or tracking' : ''}>Save to mailbox Drafts</button>
                      <button class="today-mini-btn" onclick={() => scheduleOpen = !scheduleOpen} disabled={sending}>Schedule send</button>
                    {:else if canUseAs()}
                      <button class="btn-primary" onclick={saveReviewDraftInDealton} disabled={savingReviewDraft || !composeSubject.trim() || !composeBody.trim()}>{savingReviewDraft ? 'Saving…' : 'Save review-only draft'}</button>
                    {/if}
                    <span class="act-sep" aria-hidden="true"></span>
                    <button class="today-mini-btn draft-ai-btn" disabled={polishing || !composeBody.trim()} onclick={() => editFundEmail('polish')}><span class="draft-spark">&#10022;</span> {polishing ? '…' : 'Polish wording'}</button>
                    <button class="today-mini-btn draft-ai-btn" disabled={polishing || !composeBody.trim()} onclick={() => editFundEmail('refine')}><span class="draft-spark">&#10022;</span> {polishing ? '…' : 'Refine content'}</button>
                  </div>
                  <p class="fund-hint fund-compose-note">{isSupportView() ? 'This draft stays in Dealton. Polish and Refine send the text to Anthropic; no email is sent or changed.' : 'You confirm the recipients before it sends or is scheduled. Polish and Refine send this draft text to Anthropic — review every change.'}</p>
                  {#if scheduleOpen && !isSupportView()}
                    <div class="fund-compose-actions">
                      <label class="fund-field">Date<input type="date" bind:value={scheduleDate} /></label>
                      <label class="fund-field">Time<input type="time" bind:value={scheduleTime} /></label>
                      <button class="btn-primary" onclick={scheduleReviewedEmail} disabled={sending || !scheduleDate || !scheduleTime}>Confirm schedule</button>
                    </div>
                  {/if}
                </div>
              {/if}
{/snippet}

<style>
  .today-topbar { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 14px; }
  .fund-reload-alert { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; padding: 9px 12px; border: 1px solid color-mix(in srgb, var(--red) 35%, var(--border)); border-radius: var(--radius); background: var(--bg-surface); color: var(--text-muted); font-size: 12.5px; text-align: left; }
  .fund-reload-alert .today-mini-btn { margin-left: auto; }
  .today-section { margin-bottom: 28px; scroll-margin-top: 56px; }
  .today-section-header { display: flex; align-items: baseline; gap: 12px; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid var(--border); }
  .today-section-header h2 { font-family: inherit; font-size: 15px; font-weight: 500; letter-spacing: -0.01em; color: var(--text); }
  .today-section-count { font-size: 11px; color: var(--text-dim); font-family: var(--font-mono); }
  /* Only a recorded due date can color a next-step status. Mail timing and
     suggested relationships keep neutral bars. */
  #fund-briefing { --fund-today-accent: var(--accent); }
  #fund-calls { --fund-today-accent: var(--blue); }
  #fund-new-lps { --fund-today-accent: var(--text-dim); }  /* Sales New business: forward, not urgent */
  #fund-quiet { --fund-today-accent: var(--blue); }  /* Sales Gone cold */
  .fund-action-card { border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); padding: 18px; box-shadow: var(--shadow-sm); }
  .fund-action-card h3 { margin: 0 0 7px; font-size: 15px; }
  .fund-action-card p,.fund-muted { color: var(--text-muted); font-size: 13px; line-height: 1.6; }
  .fund-action-card p { margin: 0 0 14px; }
  /* `.today-expand-btn`, `.action-btn` and `.segment-loading` are Sales classes scoped in
     Today / Contacts / ClientView, reproduced below — change one, change the other. */
  .segment-loading { display: flex; align-items: center; gap: 8px; padding: 24px 12px; color: var(--text-dim); font-size: 13px; }
  /* Toolbar pill: `.fchip` geometry, because it shares that row with the filter chips. */
  .action-btn { display: inline-flex; align-items: center; background: transparent; color: var(--text-muted); border: 1px solid var(--border); padding: 7px 13px; border-radius: 20px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: inherit; text-decoration: none; white-space: nowrap; transition: all 0.1s; }
  .action-btn:hover { color: var(--text); border-color: var(--border-strong); background: var(--bg-hover); }
  .action-btn.primary-action { background: var(--accent); color: #fff; border-color: var(--accent); }
  .action-btn.primary-action:hover { background: color-mix(in srgb, var(--accent) 88%, black); border-color: color-mix(in srgb, var(--accent) 88%, black); }
  .fund-today-workbench-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
  .fund-briefing-tile { --brief-accent: var(--text-dim); display: flex; align-items: center; gap: 16px; position: relative; overflow: hidden; min-height: 84px; margin-bottom: 10px; padding: 15px 18px; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-surface); cursor: pointer; transition: border-color 0.12s, background 0.12s; }
  /* Overlaid strip, not `border-left`: a 3px border sits outside the radius and squares the corner off. */
  .fund-briefing-tile::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 3px; background: var(--brief-accent); }
  .fund-briefing-tile:hover, .fund-briefing-tile:focus-visible { border-color: var(--border-strong); }
  /* Background lift only while collapsed — an open tile has its own `--bg-hover` boxes inside. */
  .fund-briefing-tile:not(.open):hover, .fund-briefing-tile:not(.open):focus-visible { background: var(--bg-hover); }
  .fund-briefing-tile.tone-red { --brief-accent: var(--red); }
  .fund-briefing-tile.tone-amber { --brief-accent: var(--orange); }
  .fund-briefing-tile.tone-blue { --brief-accent: var(--blue); }
  .fund-briefing-tile.tone-green { --brief-accent: var(--green); }
  .fund-today-note-unread { background: color-mix(in srgb, var(--accent) 4%, var(--bg-surface)); }  /* Today.svelte `.gnote-tile.unread` */
  .fund-briefing-dot { flex: none; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }
  .fund-today-note-sources { margin-top: 14px; }
  .fund-briefing-num { flex: none; width: 22px; text-align: center; color: var(--text-dim); font: 500 22px 'Fraunces', Georgia, serif; }
  .fund-briefing-body { flex: 1; min-width: 0; padding-right: 150px; }
  .fund-briefing-top { display: flex; align-items: center; gap: 9px; min-width: 0; }
  .fund-briefing-top strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text); font-size: 15px; }
  /* `.draft-stage-pill`, as Sales' Briefing tile and the Respond rows wear it. */
  .fund-briefing-pill { flex: none; border: 1px solid color-mix(in srgb, var(--brief-accent) 40%, transparent); border-radius: 4px; padding: 2px 6px; background: transparent; color: var(--brief-accent); font-size: 10.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; }
  .fund-briefing-chevron { color: var(--text-dim); font-size: 15px; transition: transform .12s; }
  .fund-briefing-tile.open .fund-briefing-chevron { transform: rotate(90deg); }
  .fund-briefing-preview { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; margin: 5px 0 0; color: var(--text-muted); font-size: 12.5px; line-height: 1.5; }
  .fund-briefing-tile.open .fund-briefing-preview { display: block; }
  .fund-briefing-source { margin: 8px 0 0; color: var(--text-dim); font-size: 11px; }
  .fund-briefing-detail { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border); color: var(--text-muted); font-size: 12.5px; line-height: 1.6; cursor: default; }
  .fund-briefing-detail ul { margin: 8px 0 0; padding-left: 18px; }
  .fund-briefing-detail li { margin: 5px 0; overflow-wrap: anywhere; }
  .fund-briefing-tile.open .fund-briefing-body { padding-right: 0; }
  /* Today.svelte `.ctask-btn`: right when collapsed; open, it moves to the footer's left (today-brief-v20). */
  .fund-briefing-verb, .fund-briefing-act { padding: 0; border: 0; background: none; color: var(--accent-ink); font-size: 12.5px; font-weight: 600; white-space: nowrap; cursor: pointer; }
  .fund-briefing-verb { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); }
  .fund-briefing-tile.open .fund-briefing-verb { display: none; }
  .fund-briefing-verb:hover, .fund-briefing-act:hover { background: none; text-decoration: underline; text-underline-offset: 3px; }
  .fund-briefing-actions { align-items: center; }
  .fund-briefing-actions .today-mini-btn.ghost { margin-left: auto; }
  @media (max-width: 700px) { .fund-briefing-tile { align-items: flex-start; flex-wrap: wrap; } .fund-briefing-body { padding-right: 0; } .fund-briefing-verb { position: static; transform: none; flex: 1 0 100%; box-sizing: border-box; padding-left: 38px; text-align: left; } }
  .fund-send-row, .fund-call-card { display: flex; align-items: center; gap: 14px; padding: 11px 14px; margin-bottom: 8px; border: 1px solid var(--border); border-left: 3px solid var(--blue); border-radius: var(--radius); background: var(--bg-surface); }
  .fund-send-row time, .fund-call-card time { min-width: 76px; flex: none; color: var(--blue); font: 500 12px var(--font-mono); }
  .fund-send-row > div, .fund-call-main { min-width: 0; flex: 1; }
  .fund-send-row strong { display: block; color: var(--text); font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
  .fund-send-row small { display: block; margin: 3px 0 0; color: var(--text-dim); font-size: 11.5px; overflow-wrap: anywhere; }
  .fund-call-card { flex-wrap: wrap; align-items: center; }  /* Today.svelte `.call-card` */
  .fund-call-card time { font-size: 13px; line-height: 1.6; }  /* the title's size and line box, so centred text shares a baseline */
  .fund-call-card .fund-today-actions { flex: 0 1 auto; min-width: 0; align-items: center; }
  .fund-call-main { flex: 1 1 220px; display: flex; align-items: baseline; gap: 8px; }  /* the title keeps 220px; actions wrap first */
  .fund-call-main h3, .fund-call-main p { min-width: 0; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-call-main h3 { color: var(--text); font-size: 13px; font-weight: 600; }
  .fund-call-main p { flex: 0 1000 auto; color: var(--text-dim); font-size: 12px; }  /* the org gives up width before the title */
  .fund-call-status { color: var(--text-dim); font-size: 12px; white-space: nowrap; }
  .fund-call-dl { gap: 5px; }  /* Today.svelte `.call-dl-btn` */
  .fund-call-dl svg { width: 13px; height: 13px; flex-shrink: 0; }
  .fund-today-tile { position: relative; overflow: hidden; margin-bottom: 10px; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-surface); transition: border-color 0.12s, background 0.12s; }
  .fund-today-tile::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 3px; background: var(--fund-today-accent, var(--border-strong)); }
  .fund-today-tile:hover { border-color: var(--border-strong); }
  .fund-today-tile:not([open]):hover { background: var(--bg-hover); }
  .fund-today-tile summary { display: flex; align-items: center; gap: 14px; min-height: 66px; padding: 11px 18px; cursor: pointer; list-style: none; }
  .fund-today-tile summary::-webkit-details-marker { display: none; }
  .fund-today-tile summary:focus-visible { outline: 2px solid var(--accent); outline-offset: -3px; }
  .fund-today-num { width: 22px; flex: none; color: var(--text-dim); font: 500 22px 'Fraunces', Georgia, serif; text-align: center; }
  .fund-today-tile-main { display: flex; flex: 1; flex-direction: column; min-width: 0; gap: 2px; }
  .fund-today-tile-main strong { color: var(--text); font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
  .fund-today-tile-main small, .fund-today-tile-main > span { color: var(--text-muted); font-size: 11.5px; overflow-wrap: anywhere; }
  .fund-today-tile-main small { color: var(--text-dim); }
  .fund-today-tile-count { flex: none; color: var(--text-dim); font: 11px var(--font-mono); }
  .fund-today-chevron { flex: none; color: var(--text-dim); font-size: 17px; transition: transform 0.15s; }
  .fund-today-tile[open] .fund-today-chevron { transform: rotate(90deg); }
  .fund-today-detail { padding: 2px 18px 12px 54px; border-top: 1px solid var(--border); }
  .fund-today-person { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .fund-today-person:last-child { border-bottom: 0; }
  .fund-today-person strong { color: var(--text); font-size: 12.5px; font-weight: 600; }
  /* `.fund-today-identity` and `.fund-today-clear` are gone with their markup: Chase
     recipients are `.fund-rec` rows now, and there is no "All clear" block — every
     section keeps its own header and says it is empty in its own place. */
  .fund-today-person p { margin: 3px 0 0; color: var(--text-muted); font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-today-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; flex: none; }
  /* Today's "Show N more": a plain left-aligned text affordance, never a bordered control. */
  .today-expand-btn { display: block; margin: 8px 0 0; padding: 2px 0; background: transparent; border: none; color: var(--text-dim); text-align: left; }
  .today-expand-btn:hover { background: transparent; border: none; color: var(--text-muted); }
  @media (max-width: 760px) { .fund-send-row, .fund-today-person { align-items: flex-start; flex-wrap: wrap; } .fund-today-person { flex-direction: column; } .fund-today-detail { padding-left: 18px; } .fund-today-actions { justify-content: flex-start; } .fund-today-tile-count { display: none; } .fund-call-main { flex-basis: 140px; } }
  .fund-page-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  /* The detail page's Suggested / Potential / Company-signal banner. */
  .fund-review-strip {
    display: flex; align-items: center; gap: 11px; flex-wrap: wrap;
    background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 10px 14px; margin-bottom: 14px;
    font-size: 13px; color: var(--text-muted);
  }
  .fund-row-link { border: 0; background: none; color: var(--text); font: inherit; font-weight: 600; padding: 0; cursor: pointer; text-align: left; }
  .fund-row-link:hover { color: var(--accent-ink); text-decoration: underline; }
  /* Contacts has explicit responsive column shedding below, so fixed layout is safe here
     and keeps spanning organization/edit rows from imposing their intrinsic width on the
     whole table. Without it those colspan rows held the table near 800px even after their
     secondary columns were hidden. */
  .fund-contacts-table { table-layout: fixed; }
  .fund-contacts-table .fund-contacts-gutter { width: 32px; }
  .fund-contacts-table .subgroup-row { cursor: pointer; border-top: 1px solid var(--border); }
  .fund-contacts-table .subgroup-row:hover { background: var(--bg-hover); }
  .fund-contacts-table .subgroup-row td { padding: 4px 14px 4px 32px; border-bottom: none; }
  .fund-contacts-table .subgroup-header { display: flex; align-items: center; gap: 10px; min-width: 0; font-size: 12.5px; }
  .fund-contacts-table .subgroup-arrow { color: var(--text-dim); font-size: 9px; width: 10px; flex: none; }
  .fund-contacts-table .subgroup-name { color: var(--text); font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-contacts-table .subgroup-meta { margin-right: auto; color: var(--text-dim); font: 11px var(--font-mono); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Sales Contacts' pill, pinned right; disabled where the row has nothing to set. */
  .fund-contacts-table .subgroup-header .stage-pill { flex: none; white-space: nowrap; }
  .fund-contacts-table .stage-pill:disabled { cursor: default; opacity: 0.6; border-color: var(--border); }
  .fund-contact-row.fund-contact-member td:nth-child(2) { padding-left: 32px; }
  .fund-contact-cell { display: flex; align-items: center; gap: 10px; min-width: 0; overflow: hidden; }
  /* `.contact-avatar` on Contacts: a circle, white initials. The fill comes from
     `avatarColor()`, which returns a mid-tone gradient — `--text` on it is near-black in
     the light themes and near-white in dark, so it must not inherit. */
  .fund-contact-avatar { width: 26px; height: 26px; flex: none; border-radius: 50%; display: grid; place-items: center; color: #fff; font-size: 10.5px; font-weight: 600; letter-spacing: 0.02em; }
  .fund-contact-meta { min-width: 0; overflow: hidden; }
  .fund-contact-name { color: var(--text); font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-contact-sub { color: var(--text-dim); font-size: 11.5px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-contacts-email { overflow-wrap: anywhere; }
  .fund-contacts-date, .fund-contacts-activity { white-space: nowrap; }
  .fund-contacts-actions { width: 150px; text-align: right; }
  .fund-row-actions { display: flex; justify-content: flex-end; gap: 4px; }
  .fund-row-actions .today-mini-btn { padding: 3px 8px; font-size: 11px; }
  /* Contacts follows the same always-fits table contract as Dashboard: shed secondary
     columns by the table container's width, not by viewport width. This matters at
     1024px with the sidebar open as much as it does on a phone. */
  @container data-table (max-width: 900px) {
    /* Once columns start shedding, render the remaining cells as an explicit grid.
       A fixed-layout table still reserves anonymous tracks for display:none cells,
       leaving the contact name crushed into the left half of a phone table. */
    .fund-contacts-table { display: block; table-layout: auto; }
    .fund-contacts-table thead, .fund-contacts-table tbody { display: block; }
    .fund-contacts-table thead tr, .fund-contacts-table .fund-contact-row {
      display: grid; grid-template-columns: 32px minmax(0, 1fr) minmax(150px, .8fr) 112px 132px; align-items: center;
    }
    .fund-contacts-table thead th, .fund-contacts-table .fund-contact-row td { display: block; min-width: 0; }
    .fund-contacts-table .group-row, .fund-contacts-table .subgroup-row, .fund-contacts-table .fund-contact-edit-row { display: block; width: 100%; }
    .fund-contacts-table .group-row > td, .fund-contacts-table .subgroup-row > td, .fund-contacts-table .fund-contact-edit-row > td { display: block; width: 100%; height: auto; }
    .fund-contacts-table :is(th, td).fund-contacts-activity { display: none; }
  }
  @container data-table (max-width: 760px) {
    .fund-contacts-table thead tr, .fund-contacts-table .fund-contact-row { grid-template-columns: 32px minmax(0, 1fr) minmax(160px, .8fr) 132px; }
    .fund-contacts-table :is(th, td).fund-contacts-date { display: none; }
  }
  @container data-table (max-width: 620px) {
    .fund-contacts-table thead tr, .fund-contacts-table .fund-contact-row { grid-template-columns: 32px minmax(0, 1fr) 132px; }
    .fund-contacts-table :is(th, td).fund-contacts-email { display: none; }
    .fund-contacts-table .fund-contacts-actions { width: 132px; }
    .fund-contacts-table .subgroup-row td { padding-left: 14px; padding-right: 10px; }
    .fund-contact-row.fund-contact-member td:nth-child(2) { padding-left: 14px; }
    .fund-row-actions .today-mini-btn { padding-left: 7px; padding-right: 7px; }
  }
  .fund-remove-btn { color: var(--red); }
  .fund-remove-btn:hover:not(:disabled) { color: var(--red); border-color: var(--red); background: color-mix(in srgb, var(--red) 12%, transparent); }
  .fund-remove-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .fund-contact-edit-row td { padding: 12px 14px 12px 32px; background: var(--bg); }
  /* Labels share a top line; the select is taller than the input, so bottoms cannot. */
  .fund-contact-edit { display: grid; grid-template-columns: minmax(0, 1fr) 220px auto; align-items: start; gap: 12px; }
  .fund-contact-edit .fund-field { margin: 0; }
  .fund-contact-edit .fund-compose-actions { align-self: end; }
  .fund-contact-edit select { display: block; width: 100%; margin-top: 5px; }
  @media (max-width: 560px) { .fund-contact-edit { grid-template-columns: minmax(0, 1fr); } .fund-contact-edit-row td { padding-left: 14px; } }
  .fund-source-block { margin: 16px 0; padding: 12px 0; border-top: 1px solid var(--border); }
  .fund-source-block .fund-record-subhead { margin: 0 0 6px; }
  .fund-source-block p { margin: 0 0 5px; color: var(--text-muted); font-size: 12px; line-height: 1.55; }
  .fund-hint, .fund-ir-source, .fund-ir-rail-help { color: var(--text-dim); font-size: 11px; }  /* one hint style; margins differ */
  /* The bar itself is the shared `.tabs`/`.tab`/`.tab-count` vocabulary in app.css
     (DECISIONS 2026-08-12: one tab-bar language app-wide, no underline). Only the
     top margin is local — this bar follows a page subtitle, not a toolbar. */
  .fund-dashboard-tabs { margin-top: 18px; }
  /* The mockup uses two left edges: pin/caret and LP name. Group rows and
     expanded rows are real table cells so their content follows the same grid. */
  /* Shared by the LP and Portfolio tables so their common columns line up. Keep auto
     layout: the global data-table contract explicitly forbids fixed layout because it
     squeezes declared columns and lets nowrap content overlap adjacent cells. */
  .fund-ir-table { table-layout: auto; }
  .fund-ir-table th.fund-col-pin { width: 56px; }
  .fund-ir-table th.fund-col-status { width: 110px; }
  .fund-ir-table th.fund-col-due { width: 118px; }
  .fund-ir-table th.fund-col-subject { width: 24%; }
  .fund-portfolio-table th.fund-col-subject { width: 45%; }  /* Company stays narrower than Latest email */
  .fund-ir-table th.fund-col-io { width: 100px; }
  .fund-sort-button { display: flex; align-items: center; width: 100%; padding: 0; border: 0;
    background: none; color: inherit; font: inherit; letter-spacing: inherit;
    text-transform: inherit; text-align: inherit; cursor: pointer; }
  th.num .fund-sort-button { justify-content: flex-end; }
  .fund-sort-button:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; border-radius: 2px; }
  .fund-ir-subject { color: var(--text-muted); }
  /* A nowrap <td> never ellipsises (app.css `.data-table-wrap`); clamp an inner block, like Prospecting's `.cell-clamp`. */
  .fund-ir-subject-text { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 1; line-clamp: 1; overflow: hidden; overflow-wrap: anywhere; }
  .fund-ir-table td.fund-ir-pin { text-align: left; padding-left: 16px; padding-right: 0; }
  .fund-ir-table :global(.star-btn.on) { color: var(--orange); }
  .fund-ir-table :global(.group-row td) { background: var(--bg-surface); }
  .fund-ir-table td.fund-ir-group-gutter { padding: 8px 0 8px 16px; white-space: nowrap; }
  .fund-ir-group-gutter .group-arrow { margin-right: 7px; }
  .fund-ir-group-gutter .stage-dot { display: inline-block; width: 10px; height: 10px; vertical-align: middle; }
  .fund-ir-table td.fund-ir-group-name { padding: 8px 12px; }
  .fund-ir-group-name .group-meta { margin-left: 10px; }
  .fund-ir-entity td { vertical-align: middle; }
  .fund-ir-entity-name { color: var(--text); font-size: 13.5px; font-weight: 600; line-height: 1.35; }  /* wraps at words like Sales `.cell-client`; `anywhere` let it shrink to one letter */
  /* Filled in this table, outline everywhere else — the Sales Dashboard's Status column does
     the same (`.status-badge-new` + the 15% tints of `.status-green`/`-orange`/`-red`). One
     tint strength for all three: a per-hue percentage makes them read as different weights. */
  .fund-ir-table :global(.status-pill) { border: 0; padding: 3px 9px; font-weight: 500; }
  .fund-ir-table :global(.status-pill-green) { background: color-mix(in srgb, var(--green) 15%, transparent); }
  .fund-ir-table :global(.status-pill-amber) { background: color-mix(in srgb, var(--orange) 15%, transparent); }
  .fund-ir-table :global(.status-pill-red) { background: color-mix(in srgb, var(--red) 15%, transparent); }
  .fund-ir-table td.num { color: var(--text-muted); font: 12px var(--font-mono); white-space: nowrap; }
  /* Fit the wrapper, never scroll (app.css `.data-table-wrap`): tighten spacing, then drop the least important column. */
  @container data-table (max-width: 1000px) {
    .fund-ir-table th, .fund-ir-table td { padding-left: 10px; padding-right: 10px; }
    .fund-ir-table td.fund-ir-pin, .fund-ir-table td.fund-ir-group-gutter { padding-left: 12px; padding-right: 0; }
    .fund-ir-table th.fund-col-pin { width: 44px; }
    .fund-ir-table th.fund-col-status { width: 96px; }
    .fund-ir-table th.fund-col-due { width: 108px; }
    .fund-ir-table th.fund-col-io { width: 92px; }
  }
  @container data-table (max-width: 880px) {
    .fund-lp-table .fund-col-outbound { display: none; }
  }
  @container data-table (max-width: 700px) {
    .fund-lp-table .fund-col-subject, .fund-lp-table .fund-ir-subject { display: none; }
  }
  @container data-table (max-width: 760px) {
    .fund-portfolio-table .fund-col-outbound { display: none; }
  }
  @container data-table (max-width: 540px) {
    .fund-lp-table .fund-col-inbound { display: none; }
  }
  @container data-table (max-width: 560px) {
    .fund-portfolio-table .fund-col-subject, .fund-portfolio-table .fund-ir-subject { display: none; }
  }
  @container data-table (max-width: 440px) {
    .fund-lp-table .fund-col-due { display: none; }  /* Status already says overdue / soon / on track */
    .fund-ir-table th, .fund-ir-table td { padding-left: 8px; padding-right: 8px; }
    .fund-ir-table td.fund-ir-pin, .fund-ir-table td.fund-ir-group-gutter { padding-left: 10px; }
    .fund-ir-table th.fund-col-pin { width: 38px; }
  }
  .fund-ir-panel { display: grid; grid-template-columns: minmax(0, 1fr) 230px; gap: 32px; align-items: start; }
  .fund-ir-panel-main { min-width: 0; }
  .fund-ir-panel-main .fund-record-subhead { margin: 5px 0 7px; color: var(--text-muted); font-size: 12px; font-family: inherit; text-transform: none; letter-spacing: 0; }
  .fund-ir-section { border-top: 1px solid var(--border); padding-top: 12px; margin-top: 14px; }
  .fund-ir-summary { margin: 0; color: var(--text); font-size: 13px; line-height: 1.7; }
  .fund-ir-source { margin: 5px 0 0; }
  .fund-ir-open { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; gap: 12px; padding: 4px 0; color: var(--text); font-size: 12px; line-height: 1.7; }
  .fund-ir-open time { color: var(--text-dim); font: 11px var(--font-mono); white-space: nowrap; }
  .fund-ir-table-footer { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 8px 20px; margin-top: 12px; }
  .fund-ir-scan-note { margin: 0; color: var(--text-dim); font: 11px var(--font-mono); }
  /* Stage menu. Trigger matches `.fund-inline-role`'s box so the rail's three fields line
     up; the open list is the page's own `.fchip-dd` vocabulary, not a new one. */
  .fund-ir-stage-dd { width: 100%; }
  .fund-ir-stage-menu { position: fixed; right: auto; z-index: 100; width: 240px; max-width: calc(100vw - 16px); max-height: calc(100vh - 16px); overflow-y: auto; }
  .fund-ir-stage-btn { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; min-height: 30px; padding: 2px 8px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); font: inherit; font-size: 12px; text-align: left; }
  button.fund-ir-stage-btn { cursor: pointer; }
  button.fund-ir-stage-btn:hover:not(:disabled) { border-color: var(--border-strong); }
  span.fund-ir-stage-btn { border-color: transparent; background: none; }  /* read-only: no control chrome (DESIGN-SYSTEM §4) */
  .fund-ir-stage-btn:disabled { opacity: 0.5; cursor: default; }
  .fund-ir-dd-sep { height: 1px; margin: 5px 0; background: var(--border); }
  .fund-ir-dd-label { padding: 2px 10px 4px; color: var(--text-dim); font: 10px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }
  .fund-ir-stage-menu .fchip-opt:disabled { opacity: 0.5; cursor: default; }
  .fund-ir-stage-val { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }
  .fund-ir-rail-input { width: 100%; min-height: 30px; padding: 2px 8px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); font: inherit; font-size: 12px; }
  .fund-ir-rail-input:disabled { opacity: 0.55; cursor: not-allowed; }
  .fund-plist-foot { margin-top: 10px; }
  .fund-ir-rail { display: flex; flex-direction: column; align-items: stretch; gap: 6px; }
  .fund-ir-rail-label, .fund-record-subhead { color: var(--text-dim); font: 11px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }  /* one section label */
  .fund-ir-rail-label { margin-top: 8px; }
  .fund-ir-rail-help { margin: 0; }
  .fund-ir-rail-name { overflow-wrap: anywhere; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-size: 12.5px; }
  /* Coral rail CTA — `.sug-chip.primary`'s treatment (the Sales rail's primary chip): the
     fill carries the commit, so the label is 600, not the button default. */
  /* Navigation, so a coral text link, not the coral fill reserved for Send (DESIGN-SYSTEM §2). */
  .fund-ir-rail-open { margin-top: 12px; color: var(--accent-ink); font-size: 12.5px; }
  .fund-ir-rail-open:hover { color: var(--accent-hover); text-underline-offset: 3px; }
  @container data-table (max-width: 780px) {
    .fund-ir-panel { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  }
  .fund-ir-secondary { color: var(--text-muted); font-size: 11px; line-height: 1.5; overflow-wrap: anywhere; }
  .fund-ir-detail-row td { background: var(--bg-surface); }
  .fund-ir-detail-row td.fund-ir-detail-gutter { padding: 0; }
  .fund-ir-detail-row td:not(.fund-ir-detail-gutter) { padding: 4px 12px 20px; }
  /* George's authorship mark, not a status (DESIGN-SYSTEM §1/§5) — coral is correct here. */
  .fund-ir-sparkle { color: var(--accent-ink); margin-right: 5px; }
  /* Navigation verb (`… →`): coral text link with today-mini-btn metrics, as FundDraftCard `.draft-discuss-btn` (DESIGN-SYSTEM §2–3). */
  .today-mini-btn.fund-navlink { background: transparent; border-color: transparent; padding-left: 4px; padding-right: 4px; color: var(--accent-ink); }
  .today-mini-btn.fund-navlink:hover { background: transparent; border-color: transparent; color: var(--accent-hover); text-decoration: underline; text-underline-offset: 2px; }
  .fund-email-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; }
  .fund-email-empty p { max-width: 520px; margin: 0; line-height: 1.55; }
  .fund-email-empty-title { color: var(--text-muted); font-weight: 500; }
  .fund-email-empty :global(.sync-wrap) { justify-content: center; }
  .fund-ir-muted-note { color: var(--text-dim); font-style: italic; }
  @container data-table (max-width: 480px) {
    .fund-ir-open { grid-template-columns: 34px minmax(0, 1fr); }
    .fund-ir-open time { grid-column: 2; }
  }
  /* The × lives inside the chip, so it takes the chip's colour rather than a button's. */
  .fund-file-chip { display: inline-flex; align-items: center; gap: 5px; }
  .fund-file-chip button { background: none; border: none; padding: 0; min-height: 0; color: inherit; font: inherit; line-height: 1; cursor: pointer; }
  /* A `<label>` wearing a button class still needs the button's own inline-flex box. */
  .fund-attach-btn { display: inline-flex; align-items: center; gap: 4px; cursor: pointer; }
  .fund-cc-picks { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: -4px 0 8px; font-size: 11px; }
  /* Same label vocabulary as the Sales composer's two CC groups — the two sets are
     different decisions, so a flat row of addresses hid which was which. */
  .fund-cc-label { flex: none; width: 128px; color: var(--text-dim); }
  /* LP relationship header (designs/lp-relationship-v1.html): title + SET facts only,
     actions collapsed to Pin + ⋯. */
  .lp-hdr { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .lp-hdr-text { min-width: 0; }
  .lp-titlerow { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 12px; }
  .lp-titlerow .page-title { margin-bottom: 0; }
  .lp-type, .lp-stage { pointer-events: none; user-select: none; }  /* read-only status, DESIGN-SYSTEM §4 */
  .lp-stage { display: inline-flex; align-items: center; gap: 6px; color: var(--stage); font-size: 12px; font-weight: 500; }
  .lp-stage::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--stage); }
  .lp-status { display: flex; flex-wrap: wrap; gap: 2px 8px; margin-top: 6px; color: var(--text-muted); font-size: 13px; }
  .lp-status span + span::before { content: '·'; margin-right: 8px; color: var(--text-dim); }
  .lp-state { font-weight: 600; }
  /* The fixed chat rail starts alongside the header and editor. Keep both clear
     of it, including when Mike's saved width is restored on a different screen. */
  .client-header-wrap.rail-open {
    --fund-readable-w: calc(100% - var(--fund-mike-w) - clamp(16px, 3vw, 36px) - 16px);
  }
  .client-header-wrap.rail-open .lp-hdr-text,
  .client-header-wrap.rail-open .fund-action-card { max-width: var(--fund-readable-w); }
  @media (max-width: 1100px) and (min-width: 901px) {
    .client-header-wrap.rail-open {
      --fund-readable-w: calc(100% - min(250px, var(--fund-mike-w)) - clamp(16px, 3vw, 36px) - 16px);
    }
  }
  .lp-hdr-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
  .lp-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 3px; color: var(--text-muted); font-size: 12px; }
  .lp-meta span + span::before { content: '·'; margin-right: 10px; color: var(--text-dim); }
  .lp-nextstep { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 9px; padding: 5px 9px 5px 0;
                 color: var(--text); }
  .lp-nextstep .lbl { color: var(--text-dim); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
  .lp-nextstep .due { color: var(--text-muted); font-size: 12px; }
  .lp-record { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 8px 24px; margin: 0;
               font-size: 13.5px; line-height: 1.5; }
  .lp-record dt { color: var(--text-muted); }
  .lp-record dd { margin: 0; color: var(--text); overflow-wrap: anywhere; }
  .lp-record-line { display: block; }
  .lp-record-detail { display: block; margin-top: 2px; color: var(--text-muted); font-size: 12.5px; }
  .lp-sec { margin-top: 26px; }
  .lp-sec-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
  .lp-sec-row .fund-record-subhead { margin: 0; }
  .lp-foot { margin: 6px 0 12px; color: var(--text-muted); font-size: 12.5px; line-height: 1.5; }
  .lp-generated > summary { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; list-style: none; cursor: pointer; }
  .lp-generated > summary::-webkit-details-marker { display: none; }
  .lp-generated > summary .fund-ir-sparkle { margin-right: 0; }
  .lp-more { position: relative; }
  /* Box, border and shadow are `.fchip-dd`'s in app.css — the app's other right-anchored
     menu. A lighter shadow made this one float differently from every dropdown beside it. */
  .lp-menu { position: absolute; right: 0; top: calc(100% + 4px); z-index: 40; min-width: 220px;
             display: flex; flex-direction: column; gap: 2px; padding: 5px;
             background: var(--bg-surface); border: 1px solid var(--border-strong); border-radius: var(--radius);
             box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28); }
  /* Rows are `.fchip-opt`: 7px/10px, 6px radius, weight 500 — buttons and labels mixed in
     one menu render at two weights unless the weight is stated. */
  .lp-mi { display: flex; align-items: center; justify-content: space-between; gap: 9px;
           width: 100%; padding: 7px 10px; border: 0; border-radius: var(--radius-sm); background: none;
           color: var(--text-muted); font-size: 12px; font-weight: 500; text-align: left; cursor: pointer; }
  .lp-mi:hover { background: var(--bg-hover); color: var(--text); }
  .client-header-wrap { margin-bottom: 12px; }
  /* Chrome above the shell: the back-crumb row plus the title row and its margin.
     ⚠️ Carried over from `.cv-shell` in ClientView, whose header block is the same
     construction — NOT measured on this page. Re-measure if this header changes. */
  .fund-detail-shell { --split-chrome: 118px; }
  .fund-detail-main { min-width: 0; padding-top: 0; }
  .fund-record-subhead { margin: 20px 0 10px; }
  /* Which member's thread this is — only rendered in the institution view, where a
     thread can belong to someone other than the open contact. */
  .em-who { color: var(--text-dim); font-size: 11px; }
  /* To / Cc / Bcc of an opened message, aligned with `.em-b`; the server sends Bcc only to the sender. */
  .em-rcpt { margin: 6px 0 2px; padding-left: 17px; color: var(--text-dim); font-size: 11.5px; line-height: 1.6; overflow-wrap: anywhere; }
  .em-rcpt span[title] { color: var(--text-muted); }
  .em-rcpt-k { display: inline-block; min-width: 30px; margin-right: 6px; color: var(--text-dim); font: 10.5px var(--font-mono); }
  .em-rcpt-more { margin-left: 4px; padding: 0; border: 0; background: none; color: var(--accent-ink); font: inherit; cursor: pointer; }
  /* Neutral, not green: this sits in the compose/respond area, where DESIGN-SYSTEM §1 keeps
     green out — green is on-track deal status only. The sentence says what happened. */
  .fund-send-notice { padding: 11px 14px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); margin-bottom: 12px; color: var(--text); font-size: 13px; }
  .fund-sent-row,.fund-person-row { display: flex; align-items: center; gap: 12px; padding: 15px; border-bottom: 1px solid var(--border); }
  .fund-sent-row { justify-content: space-between; font-size: 12px; }
  .fund-sent-row span,.fund-person-row p { color: var(--text-muted); font-size: 12px; }
  .fund-file-preview { display: block; width: 100%; height: 60vh; margin-top: 10px; border: 1px solid var(--border);
                       border-radius: var(--radius); background: var(--bg-surface); }
  .fund-file-text summary { margin-top: 10px; color: var(--text-muted); font-size: 12px; cursor: pointer; }
  .fund-file-excerpt { margin: 8px 0 0; padding: 12px 14px; max-height: 320px; overflow: auto; white-space: pre-wrap;
                       border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg);
                       color: var(--text-muted); font: 12px/1.5 var(--font-mono); }
  .fund-person-row p { margin: 3px 0; }
  /* Contacts rows copy the Sales client page (`.crow`, `.chead`, `.cbody`, `.ci`, `.tl*`, `.ppl-av`, `.rname`, `.rsub`, `.cstat`) — change one, change the other. */
  .fund-crow { margin-bottom: 10px; border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; background: var(--bg-surface); }
  .fund-chead { display: flex; align-items: center; gap: 10px; padding-right: 14px; }
  /* Same box as `.et-toggle`, for rows whose mail this page does not show. */
  .fund-chead-static { flex: 1; min-width: 0; display: flex; align-items: center; gap: 11px; padding: 12px 14px; }
  .fund-cbody { padding: 2px 15px 14px; border-top: 1px solid var(--border); }
  .fund-cbody > .fund-muted { margin: 12px 0 0; }
  .fund-ci { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .fund-ci:last-child { border-bottom: none; }
  .fund-tlc { width: 26px; height: 26px; border-radius: var(--radius-sm); background: var(--bg-hover); border: 1px solid var(--border); color: var(--text-muted); font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .fund-tlbody { flex: 1; min-width: 0; }
  .fund-tlhead { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 3px; }
  .fund-tlt { font-size: 13px; font-weight: 600; }
  .fund-tld { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-dim); margin-left: auto; white-space: nowrap; }
  .fund-tlx { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; }
  .fund-tlacts { margin-top: 8px; }
  .fund-ppl-av { width: 34px; height: 34px; border-radius: 50%; color: #fff; flex-shrink: 0; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  .fund-cinfo { display: flex; flex-direction: column; gap: 1px; min-width: 0; text-align: left; }
  .fund-rname { font-size: 13.5px; font-weight: 600; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-rsub { font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fund-cstat { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-dim); white-space: nowrap; margin-left: auto; }
  .fund-unclassified { padding: 24px; max-width: 65ch; }
  .fund-unclassified p,.fund-unclassified li { color: var(--text-muted); font-size: 13px; line-height: 1.6; }
  /* Same box as `.compose-area` in app.css — the Sales composer surface. */
  .fund-compose { margin: 0 0 16px; padding: 18px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); box-shadow: var(--shadow-sm); }
  .ethread .fund-compose { margin: 0 14px 14px; }
  .fund-compose-commit { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }
  .fund-compose-note { margin-top: 8px; }
  .fund-compose-generate { display: flex; gap: 8px; margin: 14px 0 8px; }
  .fund-compose-generate input { flex: 1; min-width: 0; }
  .fund-compose-generate button { flex: none; white-space: nowrap; }
  @media (max-width: 600px) { .fund-compose-generate { flex-wrap: wrap; } .fund-compose-generate input { flex-basis: 100%; } }
  .fund-compose-heading,.fund-compose-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .fund-compose-actions { justify-content: flex-start; margin-top: 14px; }
  .fund-field { display: block; margin: 12px 0; font-size: 12px; color: var(--text-muted); }
  /* A recipient is a short value; full width made it read as the page's main form. */
  .fund-email-recipient { max-width: 360px; margin-bottom: 6px; }
  .fund-field input:not([type="checkbox"]):not([type="radio"]),
  .fund-field textarea { display: block; width: 100%; margin-top: 5px; }
  .fund-field:has(> input[type="checkbox"]) { display: flex; align-items: center; gap: 7px; }
  .fund-field > input[type="checkbox"] { flex: none; margin: 0; }
  .fund-output-card { margin-top: 18px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-surface); overflow: hidden; }
  .fund-output-header { display: flex; align-items: flex-start; gap: 10px; padding: 15px 18px; border-bottom: 1px solid var(--border); }
  .fund-output-mark { display: grid; place-items: center; width: 26px; height: 26px; flex: none; border-radius: 50%; background: color-mix(in srgb, var(--accent) 14%, transparent); color: var(--accent-ink); }
  .fund-output-header h3 { margin: 1px 0 3px; color: var(--text); font-size: 14px; font-weight: 700; }
  .fund-output-header p { margin: 0; color: var(--text-muted); font-size: 12px; }
  .fund-output-body.md-content { max-width: 78ch; padding: 18px 20px 22px; font-size: 14px; line-height: 1.75; }
  .fund-output-body :global(:first-child) { margin-top: 0; }
  .fund-output-body :global(:last-child) { margin-bottom: 0; }
  .fund-output-footer { padding: 0 18px 16px; }
  .fund-inline-role { max-width: 160px; min-height: 30px; background: var(--bg-surface); color: var(--text); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 2px 6px; font: inherit; }
  .fund-alert { color: var(--red-ink); font-size: 12px; }
  /* No local narrow-screen rule: `.split-shell`'s own `@media (max-width: 1100px)` in
     app.css collapses this to one column and `rail.showTab` moves the assistant into the
     tab bar. The 900px override this page used to carry set `display: block`, which
     would now drop the grid placement the rail and divider depend on. */
</style>
